import { Alert, Snackbar } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { createLocalBlacklist, deleteBlacklist, getAllBlacklists, updateBlacklistNew } from 'API/company-blacklist';
import { isLinkedinSessionInvalidApiError, LINKEDIN_SESSION_INVALID_EVENT } from 'API/APIs/APIv1';

import { getLinkedinLocations, getPostalCodeLocation } from 'API/geo-urn';
import { getAllLinkedinAccounts, getLinkedinAccountById } from 'API/linkedin-accounts';
import { searchLinkedinTalents, streamLinkedinTalents, TalentSearchStreamEvent } from 'API/linkedin-crawler';
import { createOrCloneCampaign } from 'features/co-pilot/campaign-builder/create-campaign.api';
import {
  applyFunnelTemplate,
  changeFunnelStatus,
  getAllFunnels,
  getCampaignTemplates,
  getFunnelById,
  getPerplexitySummary,
  updateFunnel,
} from 'API/linkedin-funnel';
import {
  continueSearch,
  createSearches,
  deleteFileUpload,
  deleteSearch,
  enrichContactsLeadList,
  getAllFunnelContacts,
  judgeFunnelProfiles,
  qualifyFunnelContactsRecruiting,
  reloadSearch,
  updateContactAiJudgement,
  updateContactIsActive,
  updateContactIsBlacklisted,
  updateContactIsDeleted,
  updateContactIsSuspended,
  updateContactSex,
  updateCustomFieldEnrichment,
  updateFunnelContactsWithProspeos,
  updateJudgingSettings,
  updateSearch,
  updateSearchName,
} from 'API/linkedin-funnel-search';
import { getRecruiterConstraints, getRecruiterContacts } from 'API/recruiter-constraints';
import { changeUserInfo } from 'API/user';
import { ErrorMessages, LinkedinTypes, ProductGroups, Products, Routes } from 'enums';
import { getCoPilotRouteForPathAndTab } from 'features/co-pilot/co-pilot-routes';
import AutoSyncProcessingDialog from 'features/co-pilot/auto-sync-processing-dialog';
import AutoSyncWarningDialog from 'features/co-pilot/auto-sync-warning-dialog';
import AskForSyncDialog from 'features/co-pilot/campaign-builder/ask-for-sync-dialog';
import CampaignBuilder from 'features/co-pilot/campaign-builder/campaign-builder';
import {
  AutoReplyCase,
  BlacklistScopes,
  BlacklistUploadVariant,
  CampaignBuilderSteps,
  CampaignPipelineSteps,
  CampaignTemplate,
  CampaignTestingState,
  ContactFilter,
  ContactSearchMethods,
  EnrichmentVariants,
  FlowCreationTabs,
  LinkedinSearch,
  LinkedinSearchResult,
  Offer,
  QualificationPromptPlaceholders,
  Qualifications,
  SearchObjective,
  SearchToAdd,
  SingleSearch,
  StartNewOrClone,
  TalentSearchFeedback,
} from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { coerceOfferFieldToString } from 'features/co-pilot/campaign-builder/offer-field.utils';
import ChangeCommunicationStyleWarningDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-flow-manager/change-communication-style-warning-dialog';
import UnableToGenerateMessageDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-flow-manager/unable-to-generate-contact-request-dialog';
import AddMoreSearchesDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/add-contacts/add-more-searches-dialog';
import CreateNewSearchDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/add-contacts/pai-contact-search/create-new-search-dialog';
import RefineLinkedinSearchDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/add-contacts/pai-contact-search/refine-linkedin-search-dialog';
import TalentSearchFeedbackDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/add-contacts/pai-contact-search/talent-search-feedback-dialog';
import ChangeAiJudgementDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/change-ai-judgement-dialog';
import DeleteContactPermanentlyDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/contact-overview/delete-contact-permanently-dialog';
import DeleteMultipleContactsPermanentlyDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/contact-overview/delete-multiple-contacts-permanently-dialog';
import ContactDrawer from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/contact-drawer';
import EnrichContactsDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/final-contacts/enrich-contacts-dialog';
import EnrichmentLoadingDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/final-contacts/enrichment-loading-dialog';
import AddBlacklistDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-blacklists/add-blacklist-dialog';
import DeleteBlacklistDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-blacklists/delete-blacklist-dialog';
import EditBlacklistNameDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-blacklists/edit-blacklist-name-dialog';
import AddSingleContactDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-searches/add-single-contact-dialog';
import DeleteFileDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-searches/delete-file-dialog';
import DeleteSearchDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-searches/delete-search-dialog';
import EditSearchLinkedinAccountDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-searches/edit-search-linkedin-account-dialog';
import LoadContactsNumberDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-searches/load-contacts-number-dialog';
import RenameContactSearchDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-searches/rename-contact-search-dialog';
import SearchQuotaExceededDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-searches/search-quota-exceeded-dialog';
import useSearches from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/manage-searches/use-searches';
import QualifyContactsDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/qualify-contacts-dialog';
import RefineRecruiterSearchDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/refine-recruiter-dialog/refine-recruiter-search-dialog';
import { isEligibleForActivation } from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/contact-activation';
import UpdateSexDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/update-sex-dialog';
import useContacts from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/use-contacts';
import useFinalContacts from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/use-final-contacts';
import CoPilotLicenseExpiredDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-summary/co-pilot-license-expired-dialog';
import LinkedinAccountNotFoundDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-summary/linkedin-account-not-found-dialog';
import StartCampaignSuccessDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-summary/start-campaign-success-dialog';
import StartImmediatelyDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-summary/start-immediately-dialog';
import StartImmediatelySuccessDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-summary/start-immediately-success-dialog';
import StartMessageApprovalCampaignSuccessDialog from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-summary/start-message-approval-campaign-success-dialog';
import FlowCreationDialog from 'features/co-pilot/campaign-builder/campaign-flow/flow-creation-dialog/flow-creation-dialog';
import CreateCampaign from 'features/co-pilot/campaign-builder/create-campaign';
import CreateCampaignDialog from 'features/co-pilot/campaign-builder/create-campaign-dialog';
import CreateCampaignSuccessDialog from 'features/co-pilot/campaign-builder/create-campaign-success-dialog';
import FlowEmptyDialog from 'features/co-pilot/campaign-builder/flow-empty-dialog';
import FlowIncompleteDialog from 'features/co-pilot/campaign-builder/flow-incomplete-dialog';
import StartingCampaignNotPossibleDialog from 'features/co-pilot/campaign-builder/starting-campaign-not-possible-dialog';
import StopRunningCampaignDialog from 'features/co-pilot/campaign-builder/stop-running-campaign-dialog';
import useCampaign from 'features/co-pilot/campaign-builder/use-campaign';
import useContactDrawer from 'features/co-pilot/campaign-builder/use-contact-drawer';
import usePromptLibrary from 'features/co-pilot/campaign-builder/use-prompt-library';
import CampaignOverview from 'features/co-pilot/campaign-overview/campaign-overview';
import { editsInWizard, WizardAware } from 'features/co-pilot/campaign-overview/wizard-routing';
import CampaignStatistics from 'features/co-pilot/campaign-statistics/campaign-statistics';
import {
  AddContact,
  ContactsSearchStatuses,
  FileUpload,
  FileUploadMatchColumns,
  Funnel,
  FunnelContact,
  FunnelFunctions,
  FunnelStatuses,
  FunnelTypes,
  LinkedInCrawlerContact,
  LinkedinCrawlerSearchTypes,
  NodeVariants,
  PerplexityLeadResponse,
  PerplexityTalentResponse,
  RecruiterLeadPayload,
  RecruiterLocationSearch,
  RecruiterSearch,
  RecruiterSearchKeys,
  RecruiterSearchTypes,
  RecruiterTalentPayload,
  RefinedRecruiterSearchPayload,
  RefinedRecruiterSearchPayloadFields,
  RegularConstraint,
  Search,
} from 'features/co-pilot/co-pilot.types';
import { BlacklistItem, Blacklist_New, BlacklistTypes } from 'features/co-pilot/company-blacklists/company-blacklist.types';
import GlobalBlacklistOverview from 'features/co-pilot/company-blacklists/global-blacklist-overview';
import FileUploadDialog from 'features/co-pilot/file-upload-dialog';
import FunnelInbox from 'features/co-pilot/inbox/funnel-inbox';
import LinkedinAccountOverview from 'features/co-pilot/linkedin-accounts/linkedin-account-overview';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import TaskManager from 'features/co-pilot/task-manager/task-manager';
import useCheckLinkedinAccountStatus from 'features/co-pilot/use-check-linkedin-account-status';
import { SearchTypes } from 'features/linkedin-crawler/linkedin-profile-finder/linkedin-profile-finder.types';
import CoPilotOnboardingJourney from 'features/onboarding/co-pilot-onboarding-journey/co-pilot-onboarding-journey';
import FaqDialog from 'features/onboarding/faq/faq-dialog';
import { FaqItemKey } from 'features/onboarding/faq/faq-dialog.types';
import PaiSuccessAlert from 'features/ui/base-components/pai-success-alert';
import PaiInfoDialog from 'features/ui/base-components/pai-info-dialog';
import { useHasLicense } from 'hooks/use-has-license';
import useQueryState from 'hooks/use-query-state';
import { useUserData } from 'hooks/use-user-data';
import i18n from 'i18n';
import _ from 'lodash';
import { parse } from 'papaparse';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Node, useReactFlow } from 'reactflow';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import useCampaignNodeStore from 'stores/campaign-node-store';
import useErrorStore from 'stores/error-store';
import useUserStore from 'stores/user-store';
import { getNodeIdByStepIndex } from 'utils/flowNode.utils';
import { v4 as uuidv4 } from 'uuid';
import validator from 'validator';
import * as XLSX from 'xlsx';
import useBlacklistCompanyLookup from './hooks/use-blacklist-company-lookup';
import useCampaignStartFlow from './hooks/use-campaign-start-flow';
import useCampaignStartValidation from './hooks/use-campaign-start-validation';
import useCampaignStopFlow from './hooks/use-campaign-stop-flow';
import useCampaignTestFlow from './hooks/use-campaign-test-flow';
import { useCampaignCoPilotDialogs } from './hooks/use-campaign-co-pilot-dialogs';
import useSearchQuotaExceededDialog from './hooks/use-search-quota-exceeded-dialog';
import useStartCampaignSuccessFlow from './hooks/use-start-campaign-success-flow';

interface Props {
  productGroup: ProductGroups;
}

/** Was eine Navigation zum Builder im Router-State mitgeben darf. */
interface CampaignCoPilotLocationState {
  createCampaignSuccess?: boolean;
  /** Vom Wizard „Erweiterte Einrichtung“: alten Dialog „Kampagne erstellen“ öffnen (statt Wizard). */
  openLegacyCreateDialog?: boolean;
}

const CampaignCoPilot: React.FC<Props> = ({ productGroup }) => {
  const { firstName, onboardingDone } = useUserData();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = useAppBarNavigationStore((state) => state.activeTab);
  const setActiveTab = useAppBarNavigationStore((state) => state.setActiveTab);
  const setDetails = useUserStore((state) => state.setDetails);

  const queryClient = useQueryClient();

  const [activeCampaignStepIndex, setActiveCampaignStepIndex] = useQueryState<number>('stepIndex', 0);
  const campaignStepsArray = _.map(CampaignBuilderSteps, (tab) => tab);

  // Use useRef to track the current overview step state
  const isOverviewStepRef = useRef(false);

  // Update the ref whenever activeCampaignStepIndex changes
  useEffect(() => {
    const allSteps = _.map(CampaignBuilderSteps, (tab) => tab);
    const isOverview = activeCampaignStepIndex === allSteps.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SUMMARY);
    isOverviewStepRef.current = isOverview;
  }, [activeCampaignStepIndex]);

  const {
    campaign,
    campaignLoading,
    updateCampaignLoading,
    setCampaign,
    setCampaignWithId,
    handleUpdateCampaign,
    handleUpdateCampaignReactFlow,
    fullLinkedinAccount,
    linkedinAccountLoading,
    linkedinAccountUpdating,
    handleResetCampaign,
    askForSyncDialog,
    handleCloseAskForSyncDialog,
    handleSyncLinkedinAccount,
  } = useCampaign(handleOpenCopilotLicenseExpiredDialog);
  const { initCampaignFunctions, resetPromptLibraries } = usePromptLibrary();
  const {
    autoSyncLoading,
    autoSyncProcessingDialog,
    autoSyncWarningDialog,
    autoSyncSuccessful,
    initSyncForLinkedinAccountById,
    stopCheckAccountStatusInterval,
    handleCloseAutoSyncSuccessfulSnackbar,
    handleCloseAutoSyncProcessingDialog,
    handleCloseAutoSyncWarningDialog,
    startCheckAccountStatusInterval,
    handleOpenAutoSyncProcessingDialog,
  } = useCheckLinkedinAccountStatus();

  const [campaigns, setCampaigns] = useState<Funnel[]>([]);

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const isLinkedinResyncRequiredError = (error: any) =>
    isLinkedinSessionInvalidApiError(error) ||
    error?.message === ErrorMessages.ACCOUNT_OUT_OF_SYNC ||
    error?.code === 'SESSION_INVALID';
  const sessionRecoveryInProgressRef = useRef<Set<string>>(new Set());
  const pendingLinkedinRecoveryActionRef = useRef<
    | { type: 'regular-location' }
    | { type: 'recruiter-location'; searchType: 'geo' | 'zip' }
    | { type: 'talent-search' }
    | null
  >(null);

  useEffect(() => {
    const handleLinkedinSessionInvalid = async (event: Event) => {
      const customEvent = event as CustomEvent<{ accountId?: string }>;
      const accountId = customEvent.detail?.accountId;
      if (!accountId || sessionRecoveryInProgressRef.current.has(accountId)) {
        return;
      }

      sessionRecoveryInProgressRef.current.add(accountId);

      try {
        await initSyncForLinkedinAccountById(accountId);
        startCheckAccountStatusInterval(accountId);
      } finally {
        window.setTimeout(() => {
          sessionRecoveryInProgressRef.current.delete(accountId);
        }, 5000);
      }
    };

    window.addEventListener(LINKEDIN_SESSION_INVALID_EVENT, handleLinkedinSessionInvalid as EventListener);
    return () => window.removeEventListener(LINKEDIN_SESSION_INVALID_EVENT, handleLinkedinSessionInvalid as EventListener);
  }, [initSyncForLinkedinAccountById, startCheckAccountStatusInterval]);

  // Consolidated dialog states from useCampaignCoPilotDialogs hook
  const {
    createCampaignDialog,
    setCreateCampaignDialog,
    createCampaignSuccessDialog,
    setCreateCampaignSuccessDialog,
    openTemplateSuccessDialog,
    setOpenTemplateSuccessDialog,
    refineLinkedinSearchDialog,
    setRefineLinkedinSearchDialog,
    refineRecruiterSearchDialog,
    setRefineRecruiterSearchDialog,
    createNewSearchDialog,
    setCreateNewSearchDialog,
    addMoreSearchesDialog,
    setAddMoreSearchesDialog,
    qualifyContactsDialog,
    setQualifyContactsDialog,
    loadContactsNumberDialog,
    setLoadContactsNumberDialog,
    fileUploadDialog,
    setFileUploadDialog,
    renameContactSearchDialog,
    setRenameContactSearchDialog,
    editSearchLinkedinAccountDialog,
    setEditSearchLinkedinAccountDialog,
    deleteContactsSearchDialog,
    setDeleteContactsSearchDialog,
    deleteContactsFileDialog,
    setDeleteContactsFileDialog,
    addSingleContactDialog,
    setAddSingleContactDialog,
    deleteContactPermanentlyDialog,
    setDeleteContactPermanentlyDialog,
    changeAiJudgementDialog,
    setChangeAiJudgementDialog,
    deleteMultipleContactsPermanentlyDialog,
    setDeleteMultipleContactsPermanentlyDialog,
    addLocalBlacklistDialog,
    setAddLocalBlacklistDialog,
    deleteBlacklistDialog,
    setDeleteBlacklistDialog,
    editBlacklistNameDialog,
    setEditBlacklistNameDialog,
    enrichContactsDialog,
    setEnrichContactsDialog,
    enrichmentLoadingDialog,
    setEnrichmentLoadingDialog,
    flowCreationDialog,
    setFlowCreationDialog,
    changeCommunicationStyleWarningDialog,
    setChangeCommunicationStyleWarningDialog,
    startCampaignSuccessDialog,
    setStartCampaignSuccessDialog,
    startMessageApprovalCampaignSuccessDialog,
    setStartMessageApprovalCampaignSuccessDialog,
    startImmediatelyDialog,
    setStartImmediatelyDialog,
    startImmediatelySuccessDialog,
    setStartImmediatelySuccessDialog,
    flowIncompleteDialog,
    setFlowIncompleteDialog,
    flowEmptyDialog,
    setFlowEmptyDialog,
    creditsExhaustedDialog,
    setCreditsExhaustedDialog,
    copilotLicenseExpiredDialog,
    setCopilotLicenseExpiredDialog,
    linkedinAccountNotFoundDialog,
    setLinkedinAccountNotFoundDialog,
    stopRunningCampaignDialog,
    setStopRunningCampaignDialog,
    startingCampaignNotPossibleDialog,
    setStartingCampaignNotPossibleDialog,
    unableToGenerateMessageDialog,
    setUnableToGenerateMessageDialog,
    faqDialog,
    setFaqDialog,
  } = useCampaignCoPilotDialogs();

  const [expanded, setExpanded] = useState(true);

  const [perplexityUrl, setPerplexityUrl] = useState<string>('');
  const [perplexityLoading, setPerplexityLoading] = useState<boolean>(false);
  const [perplexityError, setPerplexityError] = useState<{
    noJobAd: boolean;
    noJobTitle: boolean;
    noProblems: boolean;
    noFeatures: boolean;
    noBenefits: boolean;
    noImpact: boolean;
  }>({ noJobAd: false, noJobTitle: false, noProblems: false, noFeatures: false, noBenefits: false, noImpact: false });
  const [perplexityStatusMessage, setPerplexityStatusMessage] = useState<string>('');
  const [offer, setOffer] = useState<Offer>({
    jobTitle: '',
    jobOffer: '',
    problems: '',
    features: '',
    benefits: '',
    impact: '',
  });

  const [activeCampaignPipelineStep, setActiveCampaignPipelineStep] = useState<CampaignPipelineSteps>(CampaignPipelineSteps.ADD_CONTACTS);
  const [contactSearchMethod, setContactSearchMethod] = useState<ContactSearchMethods | ''>('');

  const [linkedinProduct, setLinkedinProduct] = useState<LinkedinTypes | ''>('');
  const [paiSearchLanguage, setPaiSearchLanguage] = useState<string[]>(['default', 'en']);
  const [includeOwnContacts, setIncludeOwnContacts] = useState(false);
  const [locationSettings, setLocationSettings] = useState<{
    regularLocationConstraints: RegularConstraint;
    recruiterLocationConstraints: RecruiterLocationSearch;
  }>({
    regularLocationConstraints: { query: '', options: [], selected: [], isEmpty: false, loading: false },
    recruiterLocationConstraints: {
      geo: { query: '', options: [], selected: [], isEmpty: false, loading: false },
      zip: { query: '', options: [], selected: [], isEmpty: false, loading: false },
      area: '25',
    },
  });
  const [linkedinSearch, setLinkedinSearch] = useState<LinkedinSearch>({
    loading: false,
    statusMessage: '',
    result: null,
    searchResults: null,
    openIndex: null,
    addedIndices: [],
    isEmpty: false,
  });
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const pendingTalentSearchStartRef = useRef(false);
  const {
    searches,
    isLoading: searchesLoading,
    isProcessing: searchesProcessing,
  } = useSearches(
    campaign?._id,
    activeCampaignStepIndex === campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST) &&
      (activeCampaignPipelineStep === CampaignPipelineSteps.MANAGE_SEARCHES ||
        activeCampaignPipelineStep === CampaignPipelineSteps.CONTACT_OVERVIEW)
  );

  const [contactQualification, setContactQualification] = useState<{
    loading: boolean;
    promptPlaceholders: QualificationPromptPlaceholders;
    selectedOptions: string[];
    rejudgeExistingContacts: boolean;
    prompt: string;
    hasOldKeys: boolean;
    isJudging: boolean;
  }>({
    loading: false,
    promptPlaceholders: {
      required: '',
      disqualifying: '',
      preferred: '',
      additionalContactInfo: '',
      companyCharacteristics: '',
      companyChallenges: '',
      organizationalWorkingCultureValues: '',
    },
    selectedOptions: ['A', 'B'],
    rejudgeExistingContacts: false,
    prompt: '',
    hasOldKeys: false,
    isJudging: false,
  });

  const [selectedContactSearchObjective, setSelectedContactSearchObjective] = useState<SearchObjective | null>(null);
  const [searchToAdd, setSearchToAdd] = useState<SearchToAdd | null>(null);
  const [separateLinkedinAccount, setSeparateLinkedinAccount] = useState<string>('');
  const [fullSeparateLinkedinAccount, setFullSeparateLinkedinAccount] = useState<LinkedinAccount | undefined>();
  const [addSearchesLoading, setAddSearchesLoading] = useState<boolean>(false);

  const [contactsToAdd, setContactsToAdd] = useState<LinkedInCrawlerContact[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [hasLinkedinUrls, setHasLinkedinUrls] = useState(true);
  const [addContactsLoading, setAddContactsLoading] = useState<boolean>(false);

  const [linkedinAccounts, setLinkedinAccounts] = useState<LinkedinAccount[]>([]);

  const [activeBlacklistScope, setActiveBlacklistScope] = useState<BlacklistScopes>(BlacklistScopes.GLOBAL);

  const [globalBlacklists, setGlobalBlacklists] = useState<Blacklist_New[]>([]);
  const [globalBlacklistsLoading, setGlobalBlacklistsLoading] = useState(false);
  const [localBlacklists, setLocalBlacklists] = useState<Blacklist_New[]>([]);
  const [localBlacklistsLoading, setLocalBlacklistsLoading] = useState(false);

  const [removeBlacklistItemsLoading, setRemoveBlacklistItemsLoading] = useState<boolean>(false);

  const [contactFilter, setContactFilter] = useState<ContactFilter>(ContactFilter.ALL);
  const [activeQualifications, setActiveQualifications] = useState<Qualifications[]>([
    Qualifications.A,
    Qualifications.B,
    Qualifications.C,
    Qualifications.NOT_JUDGED,
  ]);

  const hasJudgingSettings = useMemo(
    () =>
      !!(campaign?.judgingSettings?.promptPlaceholders || campaign?.judgingSettings?.lastPrompt) &&
      !_.isEmpty(campaign?.judgingSettings?.selectedOptions || []),
    [campaign?.judgingSettings]
  );
  const hasConfiguredJudgingCriteria = useMemo(() => {
    const settings = campaign?.judgingSettings;

    if (!settings) return false;

    const hasSelectedOptions = !_.isEmpty(settings.selectedOptions || []);
    const hasLeadPrompt = typeof settings.lastPrompt === 'string' && settings.lastPrompt.trim() !== '';
    const hasRecruitingPrompt =
      !!settings.promptPlaceholders &&
      [settings.promptPlaceholders.required, settings.promptPlaceholders.disqualifying, settings.promptPlaceholders.preferred].some(
        (value) => typeof value === 'string' && value.trim() !== ''
      );

    return hasSelectedOptions && (hasLeadPrompt || hasRecruitingPrompt);
  }, [campaign?.judgingSettings]);

  const [contactsPage, setContactsPage] = useState(1);
  const [contactsSearchParam, setContactsSearchParam] = useState('');
  const MAX_CONTACTS_PER_PAGE = 25;
  const {
    isLoading: contactsLoading,
    contacts,
    totalContacts,
    aiJudgementProcessing: contactsAiJudgementProcessing,
    profileAnalysisProcessing: contactsProfileAnalysisProcessing,
  } = useContacts(
    campaign?._id,
    hasJudgingSettings,
    activeCampaignPipelineStep === CampaignPipelineSteps.CONTACT_OVERVIEW,
    searchesProcessing,
    contactFilter,
    activeQualifications,
    contactsPage,
    contactsSearchParam,
    MAX_CONTACTS_PER_PAGE,
    resetContactsPage
  );

  const [finalContactsPage, setFinalContactsPage] = useState(1);
  const [finalContactsSearchParam, setFinalContactsSearchParam] = useState('');
  const MAX_FINAL_CONTACTS_PER_PAGE = 25;

  const {
    isLoading: finalContactsLoading,
    finalContacts,
    totalFinalContacts,
    finalContactsProcessing: finalAiJudgementProcessing,
    profileAnalysisProcessing: finalProfileAnalysisProcessing,
  } = useFinalContacts(
    campaign?._id,
    activeCampaignPipelineStep === CampaignPipelineSteps.FINAL_CONTACT_LIST ||
      activeCampaignStepIndex === campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_TESTING) ||
      activeCampaignStepIndex === campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SUMMARY),
    finalContactsPage,
    finalContactsSearchParam,
    MAX_FINAL_CONTACTS_PER_PAGE,
    resetFinalContactsPage
  );

  const aiJudgementProcessing = useMemo(
    () =>
      activeCampaignPipelineStep === CampaignPipelineSteps.CONTACT_OVERVIEW
        ? contactsAiJudgementProcessing
        : activeCampaignPipelineStep === CampaignPipelineSteps.FINAL_CONTACT_LIST
        ? finalAiJudgementProcessing
        : false,
    [contactsAiJudgementProcessing, finalAiJudgementProcessing, activeCampaignPipelineStep]
  );
  const profileAnalysisProcessing = useMemo(
    () =>
      activeCampaignPipelineStep === CampaignPipelineSteps.CONTACT_OVERVIEW
        ? contactsProfileAnalysisProcessing
        : activeCampaignPipelineStep === CampaignPipelineSteps.FINAL_CONTACT_LIST
        ? finalProfileAnalysisProcessing
        : false,
    [contactsProfileAnalysisProcessing, finalProfileAnalysisProcessing, activeCampaignPipelineStep]
  );

  const [exportFinalContactsLoading, setExportFinalContactsLoading] = useState(false);

  const drawerContacts = useMemo(() => {
    if (activeCampaignPipelineStep === CampaignPipelineSteps.CONTACT_OVERVIEW) {
      return contacts;
    }
    if (activeCampaignPipelineStep === CampaignPipelineSteps.FINAL_CONTACT_LIST) {
      return finalContacts;
    }

    return [];
  }, [activeCampaignPipelineStep, contacts, finalContacts]);

  const {
    contactDrawer,
    handleOpenContactDrawer,
    handleGetNextContact,
    handleGetPrevContact,
    handleCloseContactDrawer,
    handleUpdateDrawerContactIsActive,
  } = useContactDrawer(refreshContactList, drawerContacts);

  const [enrichContactsSnackbar, setEnrichContactsSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);

  const setSelectedNode = useCampaignNodeStore((state) => state.setSelectedNode);
  const { getNodes, getEdges, setNodes, setEdges, setViewport } = useReactFlow();
  const nodes = getNodes();
  const edges = getEdges();

  const [campaignTesting, setCampaignTesting] = useState<CampaignTestingState>({
    selectedTestContacts: [],
    loading: false,
    testContacts: [],
    activeTestContactIndex: 0,
    activeMessageIndex: 0,
  });

  const [startCampaignLoading, setStartCampaignLoading] = useState(false);

  const isProcessing = aiJudgementProcessing || profileAnalysisProcessing;

  const [deleteMultipleContacts, setDeleteMultipleContacts] = useState(false);
  const [contactsToDelete, setContactsToDelete] = useState<FunnelContact[]>([]);

  const [successAlert, setSuccessAlert] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  let typingTimer: ReturnType<typeof setTimeout>;

  const {
    searchQuotaExceededDialog,
    handleCloseSearchQuotaExceededDialog,
    handleOpenSearchQuotaExceededAccountManagement,
  } = useSearchQuotaExceededDialog({
    searches,
    onOpenAccountManagement: () => {
      const route = getCoPilotRouteForPathAndTab(location.pathname, NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT);
      if (route) {
        navigate(route);
      } else {
        setActiveTab(NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT, false);
      }
    },
  });

  const fetchLinkedinAccounts = async () => {
    const res = await getAllLinkedinAccounts();

    if (!res.error) {
      const accounts = res.allLinkedInAccounts || [];
      if (!_.isEmpty(accounts)) {
        setLinkedinAccounts(accounts);
      } else {
        setLinkedinAccounts([]);
      }
    } else {
      handleOpenErrorDialog(res.error, '/linkedinAccounts/getAllLinkedInAccounts', 'get');
    }
  };

  const handleStartLinkedinSearch = async () => {
    await runTalentSearch(false);
  };

  const fetchGlobalBlacklists = async (showLoading: boolean) => {
    if (showLoading) {
      setGlobalBlacklistsLoading(true);
    }

    try {
      const res = await getAllBlacklists();

      if (!res.error) {
        if (!_.isNil(res.response)) {
          const resBlacklists: Blacklist_New[] = res.response;
          setGlobalBlacklists(resBlacklists);
        } else {
          setGlobalBlacklists([]);
        }
      } else {
        handleOpenErrorDialog(res.error, '/blacklists', 'get');
      }
    } finally {
      if (showLoading) {
        setGlobalBlacklistsLoading(false);
      }
    }
  };

  const fetchLocalBlacklists = async (showLoading: boolean) => {
    if (showLoading) {
      setLocalBlacklistsLoading(true);
    }

    try {
      const res = await getAllBlacklists(campaign?._id);

      if (!res.error) {
        if (!_.isNil(res.response)) {
          const resBlacklists: Blacklist_New[] = res.response;
          setLocalBlacklists(resBlacklists);
        } else {
          setLocalBlacklists([]);
        }
      } else {
        handleOpenErrorDialog(res.error, '/blacklists', 'get');
      }
    } finally {
      if (showLoading) {
        setLocalBlacklistsLoading(false);
      }
    }
  };

  const refreshBlacklistViews = async (showLoading = false) => {
    await Promise.all([fetchLocalBlacklists(showLoading), fetchGlobalBlacklists(showLoading)]);
  };

  const buildBlacklistItemsFromDialog = ({
    blacklistType,
    blacklistUploadVariant,
    individualItemsList,
    fileItemsList,
    selectedCompanies,
  }: {
    blacklistType: BlacklistTypes;
    blacklistUploadVariant: BlacklistUploadVariant;
    individualItemsList: string[];
    fileItemsList: { url?: string; company?: string }[];
    selectedCompanies: string[][];
  }): BlacklistItem[] => {
    if (blacklistUploadVariant === BlacklistUploadVariant.INDIVIDUAL) {
      return individualItemsList.map((item) => {
        if (blacklistType === BlacklistTypes.CONTACT) {
          return { contactUrl: item };
        }

        return { companyName: item, companyUrn: '' };
      });
    }

    if (blacklistUploadVariant === BlacklistUploadVariant.FILE) {
      return fileItemsList.map((item) => {
        if (blacklistType === BlacklistTypes.CONTACT) {
          return { contactUrl: item.url };
        }

        return { companyName: item.company, companyUrn: '' };
      });
    }

    if (blacklistUploadVariant === BlacklistUploadVariant.LINKEDIN_COMPANY_PICKER) {
      return selectedCompanies.map((item) => ({
        companyName: item[1],
        companyUrn: item[0],
      }));
    }

    return [];
  };

  const fetchContacts = async () => {
    await queryClient.invalidateQueries({ queryKey: ['contacts'] });
  };

  const fetchFinalContacts = async () => {
    await queryClient.invalidateQueries({ queryKey: ['finalContacts'] });
  };

  const fetchSearches = async () => {
    await queryClient.invalidateQueries({ queryKey: ['searches'] });
  };

  const fetchCampaignTemplates = async () => {
    if (_.isNil(campaign)) return;

    const { type } = campaign;

    const res = await getCampaignTemplates(type);

    if (!res.error) {
      setTemplates(res || []);
    } else {
      handleOpenErrorDialog(res?.error, '/campaigns/templates', 'get');
    }
  };

  const fetchCampaigns = async () => {
    const funnelType: FunnelTypes =
      productGroup === ProductGroups.HR_BOOSTER ? FunnelTypes.ACTIVE_SOURCING_CO_PILOT : FunnelTypes.SOCIAL_SELLING_CO_PILOT;
    const res = await getAllFunnels(funnelType, undefined, undefined, undefined, undefined, undefined, undefined, true);
    if (res && !res.error) {
      const resCampaigns: Funnel[] = res.campaigns || [];
      setCampaigns(resCampaigns);
    } else {
      handleOpenErrorDialog(res.error, '/campaigns', 'get');
    }
  };

  useEffect(() => {
    if (!_.isNil(campaign?._id)) {
      initCampaignFunctions(campaign.type, campaign.funnelFunctionsLibrary);
      fetchCampaignTemplates();

      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const paramValue = urlParams.get('stepIndex');
      if (!_.isNil(paramValue)) {
        const idx = +paramValue;
        autoSizePanel(idx);
        refreshContacts(idx);
      }
    }

    return () => {
      resetPromptLibraries();
    };
  }, [campaign?._id]);

  useEffect(() => {
    if (!_.isNil(campaign?.status)) {
      if (campaign.status === FunnelStatuses.STARTED || campaign.status === FunnelStatuses.PAUSED) {
        setActiveCampaignStepIndex(campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SUMMARY));
      }
    }
  }, [campaign?.status]);

  useEffect(() => {
    const toolRoute = getCoPilotRouteForPathAndTab(location.pathname, NavigationStates.TOOL);
    const isToolRoute = !_.isNil(toolRoute) && toolRoute === location.pathname;

    if (activeTab === NavigationStates.TOOL && isToolRoute) {
      fetchLinkedinAccounts();
    }
  }, [activeTab, location.pathname]);

  const flowHasError = () => {
    const allMessagesHaveType = nodes.filter((node) => node.type === NodeVariants.FUNCTION_NODE).every((node) => node.data?.functionName);
    const allConditionsHaveType = nodes
      .filter((node) => node.type === NodeVariants.CONDITIONAL_NODE || node.type === NodeVariants.CONDITION_WITH_DURATION_NODE)
      .every((node) => node.data?.functionName);

    const inMailNodes = nodes.filter(
      (node) =>
        node.type === NodeVariants.FUNCTION_NODE &&
        (node.data.functionName === FunnelFunctions.SEND_IN_MAIL || node.data.functionName === FunnelFunctions.SEND_IN_MAIL_WITH_CONTEXT)
    );

    const emailNodes = nodes.filter(
      (node) =>
        node.type === NodeVariants.FUNCTION_NODE &&
        (node.data.functionName === FunnelFunctions.SEND_EMAIL ||
          node.data.functionName === FunnelFunctions.SEND_EMAIL_WITH_CONTEXT ||
          node.data.functionName === FunnelFunctions.SEND_FOLLOW_UP_EMAIL)
    );

    const inMailsAreAllowed = () => {
      if (_.isEmpty(inMailNodes)) return true;

      if (fullLinkedinAccount?.liProductType === LinkedinTypes.LINKEDIN) return false;

      return true;
    };

    const emailsAreAllowed = () => {
      if (_.isEmpty(emailNodes)) return true;

      if (_.isNil(fullLinkedinAccount)) return false;

      const configOk = !_.isNil(fullLinkedinAccount.emailConfig) && !fullLinkedinAccount.emailConfig.accessRevoked;

      const accountsOk = !_.isEmpty(fullLinkedinAccount?.emailAccounts) && fullLinkedinAccount?.emailAccounts?.some((ea) => !ea.accessRevoked);

      return configOk || accountsOk;
    };

    return !allMessagesHaveType || !allConditionsHaveType || !inMailsAreAllowed() || !emailsAreAllowed();
  };

  /**
   * Create campaign dialog handlers
   */

  void useHasLicense(
    productGroup === ProductGroups.HR_BOOSTER ? Products.SAIA_RECRUITING : Products.SAIA_SALES
  );

  const handleOpenCreateCampaignDialog = () => {
    // Recruiting: der geführte Flow „Neue Kampagne“ ersetzt den Dialog; Sales behält den Dialog.
    if (productGroup === ProductGroups.HR_BOOSTER) {
      navigate(Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN);
      return;
    }
    setCreateCampaignDialog((prev) => ({ ...prev, open: true }));
    fetchCampaigns();
    // Accounts were only loaded on the Tool tab; overview users need the list here too.
    void fetchLinkedinAccounts();
  };

  // Rückweg aus der „Erweiterten Einrichtung“: alter Dialog bzw. Builder-Entwurf zurück in den Assistenten.
  const handleOpenWizardFromDialog = () => {
    handleCloseCreateCampaignDialog();
    navigate(Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN);
  };

  const handleOpenWizardFromBuilder = () => {
    if (campaign?._id) navigate(`${Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN}/${campaign._id}`);
  };

  const builderDraftInWizard = editsInWizard(
    productGroup === ProductGroups.HR_BOOSTER,
    campaign?.status === FunnelStatuses.CREATED,
    campaign as WizardAware | null
  );

  const handleCloseCreateCampaignDialog = () => {
    setCreateCampaignDialog({
      open: false,
      loading: false,
      nameTakenError: false,
      name: '',
      selectedLinkedinAccount: '',
      startNewOrClone: StartNewOrClone.START_NEW,
      selectedCampaignToClone: '',
      cloneContacts: false,
    });
  };

  const handleCampaignNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateCampaignDialog((prev) => ({ ...prev, name: event.target.value, nameTakenError: false }));
  };

  const handleSelectedLinkedinAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateCampaignDialog((prev) => ({ ...prev, selectedLinkedinAccount: event.target.value }));
  };

  const handleStartNewOrCloneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateCampaignDialog((prev) => ({ ...prev, startNewOrClone: event.target.value as StartNewOrClone }));
  };

  const handleSelectedCampaignToCloneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateCampaignDialog((prev) => ({ ...prev, selectedCampaignToClone: event.target.value }));
  };

  const handleCloneContactsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateCampaignDialog((prev) => ({ ...prev, cloneContacts: event.target.checked }));
  };

  const handleCreateCampaign = async () => {
    setCreateCampaignDialog((prev) => ({ ...prev, loading: true }));

    const { name, selectedLinkedinAccount, startNewOrClone, selectedCampaignToClone, cloneContacts } = createCampaignDialog;

    // Derselbe Schreibweg, den der Chat benutzt, wenn er dieses Fenster öffnet. Zwei
    // Fassungen wären zwei Wahrheiten — siehe create-campaign.api.ts.
    const res = await createOrCloneCampaign({
      name,
      campaignType: productGroup === ProductGroups.HR_BOOSTER ? 'recruiting' : 'sales',
      linkedinAccountId: selectedLinkedinAccount,
      cloneFromCampaignId: startNewOrClone === StartNewOrClone.START_NEW ? undefined : selectedCampaignToClone,
      cloneContacts,
    });

    if (res.campaign) {
      // Ein Klon bringt Briefing, Flow und Einstellungen schon mit und landet deshalb auf der
      // Zusammenfassung; eine leere Kampagne fängt beim ersten Schritt an.
      const summaryIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SUMMARY);
      const stepIndex = startNewOrClone === StartNewOrClone.START_NEW ? undefined : summaryIndex;

      // Der Builder ist eine eigene Route (`.../campaign`), die Übersicht eine andere
      // (`.../projects`). Wird das Fenster aus der Übersicht geöffnet, reicht `?_id=` nicht:
      // Der Nutzer bliebe auf der Liste stehen, die von der neuen Kampagne nichts weiß, und
      // müsste neu laden. Also denselben Weg gehen wie „Bearbeiten“ in der Übersicht — zum
      // Builder navigieren; der Erfolgsdialog reist im Router-State mit, weil die Seite dabei
      // neu aufgebaut wird und ihren Dialog-State verliert.
      const toolRoute = getCoPilotRouteForPathAndTab(location.pathname, NavigationStates.TOOL);
      if (!_.isNil(toolRoute) && toolRoute !== location.pathname) {
        handleCloseCreateCampaignDialog();
        navigate(
          {
            pathname: toolRoute,
            search: _.isNil(stepIndex) ? `?_id=${res.campaign._id}` : `?_id=${res.campaign._id}&stepIndex=${stepIndex}`,
          },
          { state: { createCampaignSuccess: true } satisfies CampaignCoPilotLocationState }
        );
        return;
      }

      if (_.isNil(stepIndex)) {
        setCampaignWithId(res.campaign);
      } else {
        setCampaignWithId(res.campaign, stepIndex);
        autoSizePanel(stepIndex);
        refreshContacts(stepIndex);
      }

      handleOpenCreateCampaignSuccessDialog();

      handleCloseCreateCampaignDialog();
    } else if (res.nameTaken) {
      setCreateCampaignDialog((prev) => ({
        ...prev,
        loading: false,
        nameTakenError: true,
      }));
    } else {
      setCreateCampaignDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, '/campaigns', 'post');
    }
  };

  /**
   * Create campaign success dialog handlers
   */

  const handleOpenCreateCampaignSuccessDialog = () => {
    setCreateCampaignSuccessDialog((prev) => ({ ...prev, open: true }));
  };

  // Angekommen aus der Übersicht (siehe `handleCreateCampaign`): Dialog öffnen und den
  // Merker aus der History nehmen, damit Zurück/Neuladen ihn nicht noch einmal zeigt.
  useEffect(() => {
    const state = location.state as CampaignCoPilotLocationState | null;
    if (state?.createCampaignSuccess) {
      handleOpenCreateCampaignSuccessDialog();
      navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    }
    if (state?.openLegacyCreateDialog) {
      setCreateCampaignDialog((prev) => ({ ...prev, open: true }));
      fetchCampaigns();
      void fetchLinkedinAccounts();
      navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    }
  }, [location.state]);

  const handleCloseCreateCampaignSuccessDialog = () => {
    setCreateCampaignSuccessDialog({ open: false });
  };

  /**
   * Campaign step handlers
   */

  const getOfferPayload = () => {
    let offerPayload: any = {};

    if (productGroup === ProductGroups.HR_BOOSTER) {
      const { jobTitle, jobOffer } = offer;
      offerPayload = { jobTitle, jobOffer };
    } else if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const { problems, features, benefits, impact } = offer;
      offerPayload = {
        problems,
        features,
        benefits,
        impact,
      };
    }

    // Preserve custom placeholders regardless of product group
    if ((offer as any)?.customPlaceholders) {
      offerPayload.customPlaceholders = (offer as any).customPlaceholders;
    }

    return offerPayload;
  };

  const increaseCampaignStepIndex = async () => {
    if (_.isNil(campaign)) return;

    if (activeCampaignStepIndex < campaignStepsArray.length - 1) {
      const newStep = activeCampaignStepIndex + 1;

      setActiveCampaignStepIndex(newStep);
      autoSizePanel(newStep);
      campaignStepChecks(newStep);
      refreshContacts(newStep);
    }
  };

  const decreaseCampaignStepIndex = async () => {
    if (_.isNil(campaign)) return;

    if (activeCampaignStepIndex > 0) {
      const newStep = activeCampaignStepIndex - 1;
      setActiveCampaignStepIndex(newStep);
      autoSizePanel(newStep);
      campaignStepChecks(newStep);
      refreshContacts(newStep);
    }
  };

  const selectCampaignStep = (idx: number) => {
    return () => {
      setActiveCampaignStepIndex(idx);
      autoSizePanel(idx);
      campaignStepChecks(idx);
      refreshContacts(idx);

      const campaignPipelineIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST);

      if (idx === campaignPipelineIndex) {
        const hasAnyContacts = totalContacts > 0 || (campaign?.funnelContactsLength || 0) > 0;
        setActiveCampaignPipelineStep(
          hasAnyContacts ? CampaignPipelineSteps.CONTACT_OVERVIEW : CampaignPipelineSteps.MANAGE_SEARCHES
        );
      }
    };
  };

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const autoSizePanel = (newIdx: number) => {
    const campaignFlowIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_FLOW);
    const briefAiIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_BRIEFING);
    const campaignPipelineIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST);
    const campaignSettingsIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SETTINGS);
    const campaignTestingIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_TESTING);
    const campaignOverviewIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SUMMARY);

    if (newIdx === campaignFlowIndex || newIdx === campaignTestingIndex) {
      setExpanded(false);
    }

    if (newIdx === briefAiIndex || newIdx === campaignPipelineIndex || newIdx === campaignSettingsIndex || newIdx === campaignOverviewIndex) {
      setExpanded(true);
    }
  };

  const campaignStepChecks = (newIdx: number) => {
    const campaignFlowIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_FLOW);

    if (newIdx === campaignFlowIndex) {
      if (!_.isNil(campaign) && _.isNil(campaign.template) && !_.isNil(campaign.reactFlow) && _.isEmpty(campaign.reactFlow.nodes)) {
        handleOpenFlowCreationDialog();
      }
    }
  };

  const refreshContacts = async (newIdx: number) => {
    const campaignContactListIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST);
    const campaignTestingIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_TESTING);
    const campaignOverviewIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SUMMARY);

    if (newIdx === campaignTestingIndex || newIdx === campaignOverviewIndex) {
      fetchFinalContacts();
    }

    if (newIdx === campaignContactListIndex) {
      if (activeCampaignPipelineStep === CampaignPipelineSteps.MANAGE_SEARCHES) {
        fetchSearches();
      }
      if (activeCampaignPipelineStep === CampaignPipelineSteps.CONTACT_OVERVIEW) {
        fetchContacts();
      }
      if (activeCampaignPipelineStep === CampaignPipelineSteps.MANAGE_BLACKLISTS) {
        fetchGlobalBlacklists(true);
        fetchLocalBlacklists(true);
      }
      if (activeCampaignPipelineStep === CampaignPipelineSteps.FINAL_CONTACT_LIST) {
        fetchFinalContacts();
      }
    }
  };

  const handleOpenStopRunningCampaignDialog = (campaignBuilderStep: CampaignBuilderSteps) => {
    return () => {
      setStopRunningCampaignDialog((prev) => ({ ...prev, open: true, campaignBuilderStep }));
    };
  };

  const handleCloseStopRunningCampaignDialog = () => {
    setStopRunningCampaignDialog({ open: false, loading: false, campaignBuilderStep: null });
  };

  const handleStopRunningCampaignAndProceed = async () => {
    if (_.isNil(campaign)) return;

    setStopRunningCampaignDialog((prev) => ({ ...prev, loading: true }));

    const res = await changeFunnelStatus(campaign._id, FunnelStatuses.SUSPENDED);

    if (!res.error) {
      const { campaignBuilderStep } = stopRunningCampaignDialog;
      setCampaign((prev) => ({ ...prev!, status: FunnelStatuses.SUSPENDED }));
      selectCampaignStep(campaignStepsArray.findIndex((step) => step === campaignBuilderStep))();
      handleCloseStopRunningCampaignDialog();
    } else {
      if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
        handleOpenCopilotLicenseExpiredDialog();
      } else {
        handleOpenErrorDialog(res.error, '/campaigns/:id/status', 'post');
      }
      setStopRunningCampaignDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  /**
   * AI briefing handlers
   */

  useEffect(() => {
    if (!_.isNil(campaign) && !_.isNil(campaign.offer)) {
      if (campaign.type === FunnelTypes.ACTIVE_SOURCING_CO_PILOT) {
        const { jobOffer, jobTitle } = campaign.offer;

        if (jobOffer) {
          setOffer((prev) => ({ ...prev, jobOffer }));
        }

        if (jobTitle) {
          setOffer((prev) => ({ ...prev, jobTitle }));
        }
      }

      if (campaign.type === FunnelTypes.SOCIAL_SELLING_CO_PILOT) {
        const { problems, features, benefits, impact } = campaign.offer;

        if (problems) {
          setOffer((prev) => ({ ...prev, problems }));
        }
        if (features) {
          setOffer((prev) => ({ ...prev, features }));
        }
        if (benefits) {
          setOffer((prev) => ({ ...prev, benefits }));
        }
        if (impact) {
          setOffer((prev) => ({ ...prev, impact }));
        }
      }
    }
  }, [campaign?._id, campaign?.offer]);

  const resetPerplexityErrors = () => {
    setPerplexityError({ noJobAd: false, noJobTitle: false, noProblems: false, noFeatures: false, noBenefits: false, noImpact: false });
  };

  const handleOfferInputChange = (type: keyof Offer) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      // Update local offer state to new object shape
      setOffer((prev) => ({
        ...prev,
        [type]: { inUse: true, value: newValue },
      }));

      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }

      typingTimerRef.current = setTimeout(() => {
        handleUpdateCampaign({
          offer: { ...getOfferPayload(), [type]: { inUse: true, value: newValue } },
        });
      }, 1500);

      resetPerplexityErrors();
    };
  };

  // Toggle the usage of an offer field (show/hide in prompts)
  const handleOfferToggleChange = (type: keyof Offer) => {
    return (inUse: boolean) => {
      const currentField: any = (offer as any)[type];
      const currentValue = typeof currentField === 'string' ? currentField : currentField?.value || '';

      const preservedPlaceholders = (offer as any)?.customPlaceholders || (campaign as any)?.offer?.customPlaceholders;

      // Update local state so UI reflects immediately, preserving placeholders
      setOffer((prev) => ({
        ...prev,
        customPlaceholders: preservedPlaceholders,
        [type]: { inUse, value: currentValue },
      }));

      handleUpdateCampaign({
        offer: { ...getOfferPayload(), customPlaceholders: preservedPlaceholders, [type]: { inUse, value: currentValue } },
      });
    };
  };

  const handlePerplexityUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPerplexityUrl(event.target.value);
  };

  const handleFetchPerplexitySummary = async (url: string = perplexityUrl) => {
    setPerplexityLoading(true);
    resetPerplexityErrors();
    const lang = i18n.languages[0]?.includes('de') ? 'de' : 'en';

    const res = await getPerplexitySummary(url, productGroup, lang);
    if (!res.error) {
      const statuses = [t('scrapingData'), t('summarizingResults')];

      for (let index = 0; index < statuses.length; index++) {
        setPerplexityStatusMessage(statuses[index]);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      setPerplexityLoading(false);

      const perplexitySummary = res;
      let tempOffer: any = {};

      if (!_.isNil(perplexitySummary)) {
        if (productGroup === ProductGroups.HR_BOOSTER) {
          const { jobtitle, jobadtext } = perplexitySummary as PerplexityTalentResponse;
          tempOffer = { jobTitle: jobtitle || '', jobOffer: jobadtext || '' };

          if (!jobtitle) {
            setPerplexityError((prev) => ({ ...prev, noJobTitle: true }));
          }
          if (!jobadtext) {
            setPerplexityError((prev) => ({ ...prev, noJobAd: true }));
          }
        } else if (productGroup === ProductGroups.LEAD_BOOSTER) {
          const { problems, features, benefits, impact } = perplexitySummary as PerplexityLeadResponse;
          tempOffer = {
            problems: problems || '',
            features: features || '',
            benefits: benefits || '',
            impact: impact || '',
          };
          if (!problems) {
            setPerplexityError((prev) => ({ ...prev, noProblems: true }));
          }
          if (!features) {
            setPerplexityError((prev) => ({ ...prev, noFeatures: true }));
          }
          if (!benefits) {
            setPerplexityError((prev) => ({ ...prev, noBenefits: true }));
          }
          if (!impact) {
            setPerplexityError((prev) => ({ ...prev, noImpact: true }));
          }
        }

        setOffer((prev) => ({ ...prev, ...tempOffer }));
        handleUpdateCampaign({ offer: { ...getOfferPayload(), ...tempOffer } });
      }
    } else {
      setPerplexityLoading(false);
      handleOpenErrorDialog(res?.error, '/ai/perplexity-summary', 'get');
    }
  };

  const handlePerplexityPaste = (event: React.ClipboardEvent) => {
    const pastedText = event.clipboardData.getData('text');
    setPerplexityUrl(pastedText);

    const isUrlValid = validator.isURL(pastedText);

    if (!isUrlValid) return;

    handleFetchPerplexitySummary(pastedText);
  };

  const handlePerplexityKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleFetchPerplexitySummary();
    }
  };

  const handlePerplexityRefresh = () => {
    handleFetchPerplexitySummary();
  };

  /**
   * Campaign pipeline
   */

  const handleActiveCampaignPipelineStepChange = (newStep: CampaignPipelineSteps) => {
    return async () => {
      if (newStep !== activeCampaignPipelineStep) {
        if (newStep === CampaignPipelineSteps.MANAGE_SEARCHES) {
          fetchSearches();
        }

        if (newStep === CampaignPipelineSteps.CONTACT_OVERVIEW) {
          fetchContacts();
          fetchSearches();
        }

        if (newStep === CampaignPipelineSteps.MANAGE_BLACKLISTS) {
          fetchGlobalBlacklists(true);
          fetchLocalBlacklists(true);
        }

        if (newStep === CampaignPipelineSteps.FINAL_CONTACT_LIST) {
          fetchFinalContacts();
        }

        setActiveCampaignPipelineStep(newStep);
      }
    };
  };

  /**
   * Search method handlers
   */

  const handleContactSearchMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMethod = event.target.value as ContactSearchMethods;

    if (newMethod !== contactSearchMethod) {
      if (newMethod === ContactSearchMethods.WE_SEARCH) {
        resetOwnContactSearch();
        resetContactUpload();
        setSeparateLinkedinAccount(campaign?.linkedinAccount || '');
      }
      if (newMethod === ContactSearchMethods.OWN_SEARCH) {
        resetPaiContactSearch();
        resetContactUpload();
        setSeparateLinkedinAccount(campaign?.linkedinAccount || '');
      }
      if (newMethod === ContactSearchMethods.UPLOAD_CONTACT_LIST) {
        resetPaiContactSearch();
        resetOwnContactSearch();
      }

      setContactSearchMethod(newMethod);
    } else {
      setContactSearchMethod('');
    }
  };

  const handleMoveToNewSearch = () => {
    setActiveCampaignPipelineStep(CampaignPipelineSteps.ADD_CONTACTS);
    setContactSearchMethod(ContactSearchMethods.WE_SEARCH);
    resetOwnContactSearch();
    resetContactUpload();
    setSeparateLinkedinAccount(campaign?.linkedinAccount || '');
  };

  /**
   * PAI search handlers
   */

  const handleResetLinkedinSearch = () => {
    setLinkedinSearch({ loading: false, statusMessage: '', result: null, searchResults: null, openIndex: null, addedIndices: [], isEmpty: false, feedback: undefined });
  };

  const handleFeedbackAcceptTitle = (suggestedTitle: string) => {
    setOffer((prev) => ({ ...prev, jobTitle: suggestedTitle }));
    handleUpdateCampaign({ offer: { ...getOfferPayload(), jobTitle: suggestedTitle } });
  };

  const handleFeedbackRetrySearch = () => {
    setFeedbackDialogOpen(false);
    handleStartLinkedinSearch();
  };

  const handleResetLocationSettings = () => {
    setLocationSettings({
      regularLocationConstraints: { query: '', options: [], selected: [], isEmpty: false, loading: false },
      recruiterLocationConstraints: {
        geo: { query: '', options: [], selected: [], isEmpty: false, loading: false },
        zip: { query: '', options: [], selected: [], isEmpty: false, loading: false },
        area: '25',
      },
    });
  };

  const handleLinkedinProductChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newProduct = event.target.value as LinkedinTypes;

    if (newProduct !== linkedinProduct) {
      setLinkedinProduct(newProduct);
      handleResetLocationSettings();
      handleResetLinkedinSearch();
    }
  };

  const handlePaiSearchLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setPaiSearchLanguage((prev) => {
      const updateSearchLanguage = [...prev];

      if (updateSearchLanguage.includes(value)) {
        return updateSearchLanguage.filter((lang) => lang !== value);
      } else {
        updateSearchLanguage.push(value);

        return updateSearchLanguage;
      }
    });

    handleResetLinkedinSearch();
  };

  const handleIncludeOwnContactsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeOwnContacts(event.target.checked);
    handleResetLinkedinSearch();
  };

  /**
   * Location search handlers
   */

  const handleLocationFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocationSettings((prev) => ({
      ...prev,
      regularLocationConstraints: { ...prev.regularLocationConstraints, query: event.target.value, isEmpty: false },
    }));
  };

  const handleSearchLocations = async () => {
    if (_.isNil(campaign)) return;

    const { regularLocationConstraints } = locationSettings;
    const { query } = regularLocationConstraints;
    const linkedinAccountId = separateLinkedinAccount || campaign.linkedinAccount;
    const lang = i18n.languages[0];

    if (query.trim() === '') {
      setLocationSettings((prev) => ({ ...prev, regularLocationConstraints: { ...prev.regularLocationConstraints, options: [] } }));
      return;
    }

    setLocationSettings((prev) => ({ ...prev, regularLocationConstraints: { ...prev.regularLocationConstraints, loading: true } }));

    if (!_.isNil(linkedinAccountId)) {
      const res = await getLinkedinLocations(query, lang, linkedinAccountId);

      if (!res.error) {
        const resLocations = res || [];

        if (resLocations.length === 0) {
          setLocationSettings((prev) => ({
            ...prev,
            regularLocationConstraints: { ...prev.regularLocationConstraints, options: [], isEmpty: true },
          }));
        } else {
          setLocationSettings((prev) => ({
            ...prev,
            regularLocationConstraints: { ...prev.regularLocationConstraints, options: _.take(resLocations, 5), isEmpty: false },
          }));
        }
      } else if (isLinkedinResyncRequiredError(res.error)) {
        pendingLinkedinRecoveryActionRef.current = { type: 'regular-location' };
        startCheckAccountStatusInterval(linkedinAccountId);
      } else {
        setLocationSettings((prev) => ({ ...prev, regularLocationConstraints: { ...prev.regularLocationConstraints, options: [] } }));
        handleOpenErrorDialog(res?.error, '/linkedin/lookups/geo', 'post');
      }
    }

    setLocationSettings((prev) => ({ ...prev, regularLocationConstraints: { ...prev.regularLocationConstraints, loading: false } }));
  };

  const searchKeyUp = () => {
    clearTimeout(typingTimer);

    const { regularLocationConstraints } = locationSettings;
    const { query } = regularLocationConstraints;

    if (query !== '') {
      typingTimer = setTimeout(() => {
        handleSearchLocations();
      }, 1000);
    } else {
      setLocationSettings((prev) => ({ ...prev, regularLocationConstraints: { ...prev.regularLocationConstraints, options: [] } }));
    }
  };

  const searchKeyDown = () => {
    clearTimeout(typingTimer);
  };

  const handleMultiSelectLocationsChange = (value: string) => {
    return () => {
      const newLocation = value;
      const { regularLocationConstraints } = locationSettings;
      const { options, selected } = regularLocationConstraints;
      const updateSelectedLocations = _.cloneDeep(selected);
      const selectedLocationURNs = selected.map((item) => item[0]);

      if (selectedLocationURNs.includes(newLocation)) {
        const findSelected = updateSelectedLocations.find((location) => location[0] === newLocation);

        if (!_.isNil(findSelected)) {
          _.pull(updateSelectedLocations, findSelected);
        }
      } else {
        const findLocation = options.find((location) => location[0] === newLocation);

        if (!_.isNil(findLocation)) {
          updateSelectedLocations.push(findLocation);
        }
      }

      handleResetLinkedinSearch();
      setLocationSettings((prev) => ({
        ...prev,
        regularLocationConstraints: { ...prev.regularLocationConstraints, selected: updateSelectedLocations },
      }));
    };
  };

  const handleMultiSelectLocationsRemove = (value: string) => {
    return () => {
      const newLocation = value;
      const { regularLocationConstraints } = locationSettings;
      const { selected } = regularLocationConstraints;
      const updateSelectedLocations = _.cloneDeep(selected);

      const findSelected = updateSelectedLocations.find((location) => location[0] === newLocation);

      if (!_.isNil(findSelected)) {
        _.pull(updateSelectedLocations, findSelected);
      }

      handleResetLinkedinSearch();
      setLocationSettings((prev) => ({
        ...prev,
        regularLocationConstraints: { ...prev.regularLocationConstraints, selected: updateSelectedLocations },
      }));
    };
  };

  /**
   * Recruiter locations
   */

  const handleGetRecruiterLocationConstraints = (searchType: 'geo' | 'zip') => {
    return async () => {
      if (_.isNil(campaign)) return;

      const { linkedinAccount } = campaign;

      if (_.isNil(linkedinAccount)) return;
      const linkedinAccountId = separateLinkedinAccount || linkedinAccount;

      const { recruiterLocationConstraints } = locationSettings;

      const constraint = recruiterLocationConstraints[searchType];
      const query = constraint.query;
      const lang = i18n.languages[0];

      setLocationSettings((prev) => {
        const updateConstraints = { ...prev.recruiterLocationConstraints };
        updateConstraints[searchType].loading = true;

        return { ...prev, recruiterLocationConstraints: updateConstraints };
      });

      interface PostalCodeResponse {
        error?: any;
        geoUrn?: string;
        locations?: Array<{ id: string; text: string; selectionType?: string }>;
        results?: Array<{ id: string; text: string; selectionType?: string }>;
        data?: {
          locations?: Array<{ id: string; text: string; selectionType?: string }>;
          results?: Array<{ id: string; text: string; selectionType?: string }>;
        };
      }

      interface RecruiterConstraintsResponse {
        error?: any;
        values?: Array<{ entityUrn: string; text: string; titleType?: string }>;
      }

      let res: PostalCodeResponse | RecruiterConstraintsResponse;

      if (searchType === 'zip' && linkedinProduct === LinkedinTypes.SALES_NAV) {
        res = await getPostalCodeLocation(query, linkedinAccountId);

        if (!res.error) {
          setLocationSettings((prev) => {
            const updateConstraints = { ...prev.recruiterLocationConstraints };
            const typed = res as PostalCodeResponse;
            const rawItems = typed.locations ?? typed.results ?? typed.data?.locations ?? typed.data?.results ?? [];
            const options = rawItems.map((item: { id: string; text: string; selectionType?: string }) => ({
              label: item.text,
              value: String(item.id),
            }));

            updateConstraints[searchType].options = options;
            updateConstraints[searchType].loading = false;
            updateConstraints[searchType].isEmpty = _.isEmpty(options);

            return { ...prev, recruiterLocationConstraints: updateConstraints };
          });
        } else if (isLinkedinResyncRequiredError(res.error)) {
          pendingLinkedinRecoveryActionRef.current = { type: 'recruiter-location', searchType };
          startCheckAccountStatusInterval(linkedinAccountId);
          setLocationSettings((prev) => {
            const updateConstraints = { ...prev.recruiterLocationConstraints };
            updateConstraints[searchType].loading = false;

            return { ...prev, recruiterLocationConstraints: updateConstraints };
          });
        } else {
          setLocationSettings((prev) => {
            const updateConstraints = { ...prev.recruiterLocationConstraints };
            updateConstraints[searchType].options = [];
            updateConstraints[searchType].loading = false;
            updateConstraints[searchType].isEmpty = true;

            return { ...prev, recruiterLocationConstraints: updateConstraints };
          });
        }
        return;
      }

      res = await getRecruiterConstraints(searchType, query, lang, linkedinAccountId);

      if (!res.error) {
        setLocationSettings((prev) => {
          const updateConstraints = { ...prev.recruiterLocationConstraints };
          const options =
            (res as RecruiterConstraintsResponse).values?.map((item: { entityUrn: string; text: string; titleType?: string }) => ({
              label: item.text,
              value: item.entityUrn,
              titleType: item.titleType || undefined,
            })) || [];

          updateConstraints[searchType].options = options;
          updateConstraints[searchType].loading = false;
          updateConstraints[searchType].isEmpty = _.isEmpty(options);

          return { ...prev, recruiterLocationConstraints: updateConstraints };
        });
      } else {
        if (isLinkedinResyncRequiredError(res.error)) {
          pendingLinkedinRecoveryActionRef.current = { type: 'recruiter-location', searchType };
          startCheckAccountStatusInterval(linkedinAccountId);
          setLocationSettings((prev) => {
            const updateConstraints = { ...prev.recruiterLocationConstraints };
            updateConstraints[searchType].loading = false;

            return { ...prev, recruiterLocationConstraints: updateConstraints };
          });
        } else {
          setLocationSettings((prev) => {
            const updateConstraints = { ...prev.recruiterLocationConstraints };
            updateConstraints[searchType].options = [];
            updateConstraints[searchType].loading = false;
            updateConstraints[searchType].isEmpty = true;

            return { ...prev, recruiterLocationConstraints: updateConstraints };
          });
        }
      }
    };
  };

  const handleSelectRecruiterLocationConstraint = (searchType: 'geo' | 'zip') => {
    return (value: string) => {
      return () => {
        const updateConstraints = { ...locationSettings.recruiterLocationConstraints };

        const constraintObject = updateConstraints[searchType].options.find((o) => o.value === value);

        if (_.isNil(constraintObject)) return;

        if (updateConstraints[searchType].selected.some((item) => item.value === constraintObject.value)) {
          _.remove(updateConstraints[searchType].selected, (item) => item.value === value);
        } else {
          updateConstraints[searchType].selected.push(constraintObject);
        }

        if (searchType === 'zip') {
          updateConstraints.geo.selected = [];
          updateConstraints.geo.options = [];
          updateConstraints.geo.query = '';
        }

        if (searchType === 'geo') {
          updateConstraints.zip.selected = [];
          updateConstraints.zip.options = [];
          updateConstraints.zip.query = '';
          updateConstraints.area = '25';
        }

        handleResetLinkedinSearch();
        setLocationSettings((prev) => ({ ...prev, recruiterLocationConstraints: updateConstraints }));
      };
    };
  };

  const handleRemoveRecruiterLocationConstraint = (searchType: 'geo' | 'zip', urn: string) => {
    return () => {
      const updateConstraints = { ...locationSettings.recruiterLocationConstraints };

      if (updateConstraints[searchType].selected.some((item) => item.value === urn)) {
        _.remove(updateConstraints[searchType].selected, (item) => item.value === urn);

        if (searchType === 'zip' && updateConstraints[searchType].selected.length === 0) {
          updateConstraints.area = '25';
        }
      }

      handleResetLinkedinSearch();
      setLocationSettings((prev) => ({ ...prev, recruiterLocationConstraints: updateConstraints }));
    };
  };

  const handleRecruiterLocationConstraintQueryChange = (searchType: 'geo' | 'zip') => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocationSettings((prev) => {
        const updateConstraints = { ...prev.recruiterLocationConstraints };
        updateConstraints[searchType].query = event.target.value;
        updateConstraints[searchType].isEmpty = false;

        return { ...prev, recruiterLocationConstraints: updateConstraints };
      });
    };
  };

  const searchRecruiterLocationKeyUp = (searchType: 'geo' | 'zip') => {
    return () => {
      clearTimeout(typingTimer);

      const { recruiterLocationConstraints } = locationSettings;
      const constraint = recruiterLocationConstraints[searchType];
      const query = constraint.query;

      if (query !== '') {
        typingTimer = setTimeout(() => {
          handleGetRecruiterLocationConstraints(searchType)();
        }, 1000);
      } else {
        setLocationSettings((prev) => {
          const updateConstraints = { ...prev.recruiterLocationConstraints };
          updateConstraints[searchType].options = [];
          updateConstraints[searchType].loading = false;

          return { ...prev, recruiterLocationConstraints: updateConstraints };
        });
      }
    };
  };

  const searchRecruiterLocationKeyDown = () => {
    clearTimeout(typingTimer);
  };

  const handleRecruiterLocationAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleResetLinkedinSearch();
    setLocationSettings((prev) => {
      const updateConstraints = { ...prev.recruiterLocationConstraints };
      updateConstraints.area = event.target.value;

      return { ...prev, recruiterLocationConstraints: updateConstraints };
    });
  };

  /**
   * PAI search handlers
   */

  const getTalentSearchStreamMessage = (event: TalentSearchStreamEvent) => {
    const formatProgress = (completed?: number, total?: number) => {
      if (_.isNumber(completed) && _.isNumber(total) && total > 0) {
        return ` (${completed}/${total})`;
      }
      return '';
    };

    if (event.type === 'constraint') {
      const countText = _.isNumber(event.linkedinFreq) ? `${event.linkedinFreq.toLocaleString('de-DE')} Treffer` : 'Treffer geprüft';
      return `Suchabfrage bewertet${formatProgress(event.completed, event.total)}: ${countText}`;
    }

    if (event.type === 'constraint_error') {
      return `Eine Suchabfrage konnte nicht geprüft werden${formatProgress(event.completed, event.total)}.`;
    }

    if (event.type === 'summary') {
      if (_.isNumber(event.total)) {
        return `Suche abgeschlossen: ${event.total.toLocaleString('de-DE')} passende Suchvorschläge gefunden.`;
      }
      return 'Suche abgeschlossen. Passende Suchvorschläge gefunden.';
    }

    if (event.type === 'done' || event.type === 'completed') {
      return 'Suchvorschläge werden gespeichert und Kontakte werden vorbereitet...';
    }

    if (event.phase === 'request_received') return 'Talent Search gestartet...';
    if (event.phase === 'resolve_session') return `LinkedIn-Session wird geprüft${formatProgress(event.completed, event.total)}...`;
    if (event.phase === 'account_resync') return 'LinkedIn-Account wird synchronisiert...';
    if (event.phase === 'initializing') return 'Suche wird initialisiert...';
    if (event.phase === 'industry_analysis') return 'Branchen werden analysiert...';
    if (event.phase === 'industry_analysis_done') return 'Branchenanalyse abgeschlossen.';
    if (event.phase === 'constraint_extraction') return 'KI erstellt optimale Suchabfragen...';
    if (event.phase === 'constraint_extraction_done') return 'Suchabfragen erstellt. Starte LinkedIn-Checks...';
    if (event.phase === 'linkedin_count_checks') return `LinkedIn-Checks laufen${formatProgress(event.completed, event.total)}...`;
    if (event.phase === 'autosave') return 'Suchvorschläge werden zur Kampagne hinzugefügt...';
    if (event.message) return event.message;

    return 'Talent Search läuft...';
  };

  const runTalentSearch = async (bypassCriteriaCheck: boolean = false) => {
    if (_.isNil(campaign)) return;

    // Offer fields may live as `{ inUse, value }` after the user has touched them
    // in the campaign builder. Coerce to plain strings before sending them to the
    // /talent-search API (which expects strings — see TalentSearchBodySchema).
    // Sales has no job ad: the AI reads the offering and searches the people who buy it.
    const isLeadSearch = productGroup === ProductGroups.LEAD_BOOSTER;
    const jobTitle = isLeadSearch ? campaign.name || '' : coerceOfferFieldToString(offer?.jobTitle);
    const jobOffer = isLeadSearch
      ? (
          [
            ['Problems', coerceOfferFieldToString(offer?.problems)],
            ['Features', coerceOfferFieldToString(offer?.features)],
            ['Benefits', coerceOfferFieldToString(offer?.benefits)],
            ['Impact', coerceOfferFieldToString(offer?.impact)],
          ] as const
        )
          .filter(([, value]) => !_.isEmpty(_.trim(value)))
          .map(([label, value]) => `${label}: ${value}`)
          .join('\n')
      : coerceOfferFieldToString(offer?.jobOffer);

    if (_.isEmpty(_.trim(jobOffer)) && (isLeadSearch || _.isEmpty(_.trim(jobTitle)))) {
      setLinkedinSearch((prev) => ({
        ...prev,
        blockedMessage: t(isLeadSearch ? 'kiSearchBriefingMissing_LeadBooster' : 'kiSearchBriefingMissing_ActiveSourcing'),
      }));
      return;
    }

    if (!bypassCriteriaCheck && !hasConfiguredJudgingCriteria) {
      pendingTalentSearchStartRef.current = true;
      handleOpenQualifyContactsDialog();
      return;
    }

    pendingTalentSearchStartRef.current = false;

    setLinkedinSearch((prev) => ({
      ...prev,
      loading: true,
      statusMessage: 'AI is analyzing the best talent searches...',
      result: null,
      searchResults: null,
      openIndex: null,
      addedIndices: [],
      isEmpty: false,
      feedback: undefined,
      blockedMessage: undefined,
    }));

    const { regularLocationConstraints, recruiterLocationConstraints } = locationSettings;

    const linkedinAccountId = separateLinkedinAccount || campaign.linkedinAccount;

    const selectedLocationURNs = regularLocationConstraints.selected.map((item) => item[0]);

    const recruiterLocations: any = {};

    const mappedGeoConstraint = recruiterLocationConstraints.geo.selected.map((item) => ({
      entityUrn: item.value,
      text: item.label,
    }));

    if (!_.isEmpty(mappedGeoConstraint)) {
      recruiterLocations.geoUrns = mappedGeoConstraint;
    }

    const mappedZipConstraint = recruiterLocationConstraints.zip.selected.map((item) => ({
      entityUrn: item.value,
      text: item.label,
    }));

    if (!_.isEmpty(mappedZipConstraint)) {
      recruiterLocations.postalCodes = mappedZipConstraint;
      recruiterLocations.area = recruiterLocationConstraints.area;
    }

    try {
      if (!_.isNil(linkedinAccountId)) {
        let res:
          | {
              talentSearches?: any[];
              feedback?: TalentSearchFeedback;
              autosave?: { savedSearches: number; duplicateSearches: number; saveError?: any };
              error?: any;
            }
          | undefined;

        if (linkedinProduct) {
          let streamedSearchResults: LinkedinSearchResult[] = [];
          let streamedFeedback: TalentSearchFeedback | undefined;
          let streamedAutosave: { savedSearches: number; duplicateSearches: number; saveError?: any } | undefined;
          let streamedError: { code?: string; message?: string; error?: string } | null = null;
          const streamResult = await streamLinkedinTalents(
            {
              title: jobTitle,
              geoUrns: selectedLocationURNs,
              product: linkedinProduct,
              text: jobOffer,
              campaignId: campaign._id,
              linkedinAccount: linkedinAccountId,
              recruiterLocations,
              languages: paiSearchLanguage,
              includeOwnContacts,
              audience: isLeadSearch ? 'leads' : 'candidates',
            },
            (event: TalentSearchStreamEvent) => {
              if (event.type === 'error') {
                streamedError = {
                  code: event.error,
                  message: event.message || 'Talent search failed.',
                };
                return;
              }

              if ((event.type === 'done' || event.type === 'completed') && Array.isArray(event.talentSearches)) {
                streamedSearchResults = event.talentSearches as LinkedinSearchResult[];
                if (event.feedback) {
                  streamedFeedback = event.feedback;
                }
                if (event.autosave) {
                  streamedAutosave = event.autosave;
                }
              }

              const uiMessage = getTalentSearchStreamMessage(event);
              setLinkedinSearch((prev) => ({ ...prev, statusMessage: uiMessage }));
            }
          );

          if (!streamResult?.error) {
            if (!_.isNil(streamedError)) {
              res = { error: streamedError };
            } else {
              res = { talentSearches: streamedSearchResults, feedback: streamedFeedback, autosave: streamedAutosave };
            }
          } else {
            // Fallback path for environments where SSE is not available.
            res = await searchLinkedinTalents(
              jobTitle,
              selectedLocationURNs,
              linkedinProduct,
              jobOffer,
              campaign._id,
              linkedinAccountId,
              recruiterLocations,
              paiSearchLanguage,
              includeOwnContacts,
              isLeadSearch ? 'leads' : 'candidates'
            );
          }
        }

        if (!res?.error) {
          const searchResults = res?.talentSearches || [];
          const autosave = res?.autosave;

          if ((autosave?.savedSearches || 0) > 0) {
            handleOpenSuccessAlert(
              (autosave?.duplicateSearches || 0) > 0
                ? `Saved ${autosave?.savedSearches} AI-selected searches. ${autosave?.duplicateSearches} already existed in this campaign.`
                : `Saved ${autosave?.savedSearches} AI-selected searches to this campaign.`
            );
            resetPaiContactSearch();
            setContactSearchMethod('');
            setActiveCampaignPipelineStep(CampaignPipelineSteps.CONTACT_OVERVIEW);
            fetchSearches();
            fetchContacts();
          } else if ((autosave?.duplicateSearches || 0) > 0) {
            handleOpenSuccessAlert(
              autosave?.duplicateSearches === 1
                ? 'The AI-selected search was already added to this campaign.'
                : `All ${autosave?.duplicateSearches} AI-selected searches were already added to this campaign.`
            );
            resetPaiContactSearch();
            setContactSearchMethod('');
            setActiveCampaignPipelineStep(CampaignPipelineSteps.CONTACT_OVERVIEW);
            fetchSearches();
            fetchContacts();
          } else if (autosave?.saveError) {
            handleOpenErrorDialog(autosave.saveError, '/campaigns/:campaignId/search', 'post');
          } else if (!_.isEmpty(searchResults)) {
            handleOpenErrorDialog(
              { code: 500, message: 'Talent search found suggestions, but backend autosave did not complete.' },
              '/talent-search/stream',
              'post'
            );
          } else {
            setLinkedinSearch((prev) => ({
              ...prev,
              isEmpty: true,
              feedback: res?.feedback,
            }));
            if (res?.feedback) setFeedbackDialogOpen(true);
          }
        } else if (isLinkedinResyncRequiredError(res.error)) {
          pendingLinkedinRecoveryActionRef.current = { type: 'talent-search' };
          startCheckAccountStatusInterval(linkedinAccountId);
        } else {
          handleOpenErrorDialog(res?.error, '/talent-search', 'post');
        }
      }
    } finally {
      setLinkedinSearch((prev) => ({ ...prev, loading: false, statusMessage: '' }));
    }
  };

  useEffect(() => {
    if (!autoSyncSuccessful || _.isNil(pendingLinkedinRecoveryActionRef.current)) {
      return;
    }

    const pendingAction = pendingLinkedinRecoveryActionRef.current;
    pendingLinkedinRecoveryActionRef.current = null;

    if (pendingAction.type === 'regular-location') {
      handleSearchLocations();
      return;
    }

    if (pendingAction.type === 'recruiter-location') {
      handleGetRecruiterLocationConstraints(pendingAction.searchType)();
      return;
    }

    handleStartLinkedinSearch();
  }, [autoSyncSuccessful, handleGetRecruiterLocationConstraints, handleSearchLocations, handleStartLinkedinSearch]);

  /**
   * Refine linkedin search dialog handlers
   */

  const handleOpenRefineLinkedinSearchDialog = (linkedinSearch: LinkedinSearchResult, idx: number) => {
    return () => {
      const { URL, payload } = linkedinSearch;

      setLinkedinSearch((prev) => ({ ...prev, openIndex: idx }));

      // Check the URL
      if (!_.isNil(URL)) {
        setRefineLinkedinSearchDialog((prev) => ({
          ...prev,
          open: true,
          originalLinkedinUrl: URL,
        }));
      }

      // Check the payload
      if (!_.isNil(payload)) {
        if (productGroup === ProductGroups.HR_BOOSTER) {
          handleOpenRefineRecruiterSearchDialog(payload as RecruiterTalentPayload)();
        }

        if (productGroup === ProductGroups.LEAD_BOOSTER) {
          handleOpenRefineRecruiterSearchDialog(payload as RecruiterLeadPayload)();
        }
      }
    };
  };

  const handleCloseRefineLinkedinSearchDialog = () => {
    setRefineLinkedinSearchDialog({ open: false, loading: false, originalLinkedinUrl: '', alteredLinkedinUrl: '', urlError: false });
  };

  const isLinkedinSearchUrl = (url: string) => {
    return url.includes('www.linkedin.com');
  };

  const handleAlteredLinkedinUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRefineLinkedinSearchDialog((prev) => ({
      ...prev,
      alteredLinkedinUrl: event.target.value,
      urlError: !isLinkedinSearchUrl(event.target.value) && event.target.value !== '',
    }));
  };

  const handlePasteAlteredLinkedinUrl = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');

    setRefineLinkedinSearchDialog((prev) => ({ ...prev, alteredLinkedinUrl: pastedText, urlError: !isLinkedinSearchUrl(pastedText) }));
  };

  const handleAcceptRefinedLinkedinUrl = (finalUrl: string) => {
    setLinkedinSearch((prev) => ({
      ...prev,
      result: {
        ...prev.result,
        URL: finalUrl,
      },
    }));
    handleCloseRefineLinkedinSearchDialog();

    if (
      _.isNil(campaign?.judgingSettings) ||
      (_.isNil(campaign?.judgingSettings.promptPlaceholders) && _.isNil(campaign?.judgingSettings.lastPrompt))
    ) {
      handleOpenQualifyContactsDialog();
    } else {
      handleOpenCreateNewSearchDialog();
    }
  };

  /**
   * Refine recruiter search dialog
   */

  const handleOpenRefineRecruiterSearchDialog = (payload: RecruiterTalentPayload | RecruiterLeadPayload | RefinedRecruiterSearchPayload) => {
    return () => {
      // If searchId is present, we assume we need to find the corresponding search
      setRefineRecruiterSearchDialog((prev) => ({
        ...prev,
        open: true,
      }));

      fetchInitialRecruiterContacts(payload as RecruiterTalentPayload | RecruiterLeadPayload);
    };
  };

  const handleCloseRefineRecruiterSearchDialog = () => {
    setRefineRecruiterSearchDialog({
      open: false,
      loading: false,
      crawlerLinkedinAccount: null,
      preLoadingFields: false,
      contactsLoading: false,
      constraints: {
        occupation: { query: '', options: [], selected: [], titleType: undefined, loading: false },
        skill: { query: '', options: [], selected: [], loading: false },
        industry: { query: '', options: [], selected: [], loading: false },
        company: { query: '', options: [], selected: [], loading: false },
        geo: { query: '', options: [], selected: [], loading: false },
        zip: { query: '', options: [], selected: [], loading: false },
        area: '25',
        keywords: '',
        yearsOfExperience: [0, 30],
      },
      contacts: [],
      totalContacts: 0,
      pageNumber: 1,
      searchRequestId: undefined,
      searchHistoryId: undefined,
      searchContextId: undefined,
    });

    if (
      _.isNil(campaign?.judgingSettings) ||
      (_.isNil(campaign?.judgingSettings.promptPlaceholders) && _.isNil(campaign?.judgingSettings.lastPrompt))
    ) {
      handleOpenQualifyContactsDialog();
    } else {
      handleOpenCreateNewSearchDialog();
    }
  };

  const preSelectConstraint = (searchType: RecruiterSearchKeys, value: { entityUrn: string; text: string; titleType?: string }[]) => {
    setRefineRecruiterSearchDialog((prev) => {
      const updateConstraints = { ...prev.constraints };
      const mappedValue = value.map((item) => ({
        label: item.text,
        value: !_.isNil(item.titleType) ? `${item.titleType}::${item.entityUrn || item.text}` : `${item.entityUrn ? item.entityUrn : item.text}`,
        exclude: false,
        titleType: item.titleType,
      }));
      updateConstraints[searchType].selected = mappedValue;

      return { ...prev, constraints: updateConstraints };
    });
  };

  const preSelectRecruiterConstraints = (payload: any) => {
    if (!_.isNil(payload?.titles)) {
      preSelectConstraint(RecruiterSearchTypes.OCCUPATION, payload.titles);
    }

    if (!_.isNil(payload?.skills)) {
      preSelectConstraint(RecruiterSearchTypes.SKILL, payload.skills);
    }

    if (!_.isNil(payload?.industries)) {
      preSelectConstraint(RecruiterSearchTypes.INDUSTRY, payload.industries);
    }

    if (!_.isNil(payload?.companies)) {
      preSelectConstraint(RecruiterSearchTypes.COMPANY, payload.companies);
    }

    if (!_.isNil(payload?.geoUrns)) {
      preSelectConstraint(RecruiterSearchTypes.LOCATION, payload.geoUrns);
    }

    if (!_.isNil(payload?.postalCodes)) {
      preSelectConstraint(RecruiterSearchTypes.ZIP, payload.postalCodes);
    }

    if (payload?.yearsOfExperience) {
      setRefineRecruiterSearchDialog((prev) => ({
        ...prev,
        constraints: { ...prev.constraints, yearsOfExperience: payload.yearsOfExperience },
      }));
    }

    if (payload?.area) {
      setRefineRecruiterSearchDialog((prev) => ({
        ...prev,
        constraints: { ...prev.constraints, area: payload.area },
      }));
    }

    if (payload?.keywords) {
      setRefineRecruiterSearchDialog((prev) => ({
        ...prev,
        constraints: { ...prev.constraints, keywords: payload.keywords },
      }));
    }
  };

  const getRecruiterSearchPayload = (): RefinedRecruiterSearchPayload => {
    const refinedSearchPayload: any = {};

    const { occupation, skill, industry, company, geo, zip, area, keywords, yearsOfExperience } = refineRecruiterSearchDialog.constraints;

    if (!_.isEmpty(occupation.selected)) {
      refinedSearchPayload.titles = occupation.selected.map((item) => ({
        entityUrn: !_.isNil(item.titleType) ? item.value.split('::')[1] : item.value,
        text: item.label,
        titleType: item.titleType,
        exclude: item.exclude,
      }));
    }
    if (!_.isEmpty(skill.selected)) {
      refinedSearchPayload.skills = skill.selected.map((item) => ({ entityUrn: item.value, text: item.label, exclude: item.exclude }));
    }
    if (!_.isEmpty(industry.selected)) {
      refinedSearchPayload.industries = industry.selected.map((item) => ({ entityUrn: item.value, text: item.label, exclude: item.exclude }));
    }
    if (!_.isEmpty(company.selected)) {
      refinedSearchPayload.companies = company.selected.map((item) => ({ entityUrn: item.value, text: item.label, exclude: item.exclude }));
    }
    if (!_.isEmpty(geo.selected)) {
      refinedSearchPayload.geoUrns = geo.selected.map((item) => ({ entityUrn: item.value, text: item.label, exclude: item.exclude }));
    }
    if (!_.isEmpty(zip.selected)) {
      refinedSearchPayload.postalCodes = zip.selected.map((item) => ({ entityUrn: item.value, text: item.label, exclude: item.exclude }));
    }
    if (area !== '') {
      refinedSearchPayload.area = area;
    }
    if (keywords !== '') {
      refinedSearchPayload.keywords = keywords;
    }
    if (!_.isEmpty(yearsOfExperience)) {
      refinedSearchPayload.yearsOfExperience = yearsOfExperience;
    }

    return refinedSearchPayload;
  };

  const fetchInitialRecruiterContacts = async (payload: RecruiterTalentPayload | RecruiterLeadPayload, crawlerLinkedinAccountId?: string) => {
    if (_.isNil(campaign)) return;
    const { linkedinAccount } = campaign;
    if (_.isNil(linkedinAccount)) return;

    const linkedinAccountId = crawlerLinkedinAccountId || separateLinkedinAccount || linkedinAccount;

    setRefineRecruiterSearchDialog((prev) => ({ ...prev, preLoadingFields: true }));

    const res = await getRecruiterContacts(1, linkedinAccountId, payload, undefined);

    if (!res.error) {
      setRefineRecruiterSearchDialog((prev) => ({ ...prev, preLoadingFields: false }));

      const { contacts, totalContacts, selectedConstraints, searchRequestId, searchHistoryId, searchContextId } = res;
      setRefineRecruiterSearchDialog((prev) => ({
        ...prev,
        contacts,
        totalContacts,
        pageNumber: 1,
        searchRequestId,
        searchHistoryId,
        searchContextId,
      }));

      preSelectRecruiterConstraints(selectedConstraints);
    } else if (isLinkedinResyncRequiredError(res.error)) {
      startCheckAccountStatusInterval(linkedinAccountId);
      handleCloseRefineRecruiterSearchDialog();
    } else {
      handleCloseRefineRecruiterSearchDialog();
      handleOpenErrorDialog(res.error, '/talent-search/recruiter-contacts', 'post');
    }
  };

  const fetchRecruiterContacts = async (
    newPage: number = refineRecruiterSearchDialog.pageNumber,
    newSearch: RefinedRecruiterSearchPayload,
    param?: keyof RecruiterSearch,
    recruiterIds?: { searchRequestId: string | undefined; searchHistoryId: string | undefined; searchContextId: string | undefined },
    initialLoading?: boolean,
    crawlerLinkedinAccountId?: string
  ) => {
    if (_.isNil(campaign)) return;

    const { linkedinAccount } = campaign;

    if (_.isNil(linkedinAccount)) return;

    const linkedinAccountId =
      refineRecruiterSearchDialog.crawlerLinkedinAccount || crawlerLinkedinAccountId || separateLinkedinAccount || linkedinAccount;

    setRefineRecruiterSearchDialog((prev) => ({ ...prev, contactsLoading: true, preLoadingFields: !!initialLoading }));

    let searchRequestId = refineRecruiterSearchDialog.searchRequestId;
    let searchHistoryId = refineRecruiterSearchDialog.searchHistoryId;
    let searchContextId = refineRecruiterSearchDialog.searchContextId;

    if (!_.isNil(recruiterIds)) {
      searchRequestId = recruiterIds.searchRequestId;
      searchHistoryId = recruiterIds.searchHistoryId;
      searchContextId = recruiterIds.searchContextId;
    }

    const res = await getRecruiterContacts(
      newPage,
      linkedinAccountId,
      undefined,
      newSearch,
      searchRequestId,
      searchHistoryId,
      searchContextId,
      param
    );

    if (!res.error) {
      setRefineRecruiterSearchDialog((prev) => ({ ...prev, contactsLoading: false, preLoadingFields: false }));

      const {
        contacts,
        totalContacts,
        searchRequestId: newSearchRequestId,
        searchHistoryId: newSearchHistoryId,
        searchContextId: newSearchContextId,
        correctedKeywords,
      } = res;
      setRefineRecruiterSearchDialog((prev) => {
        const updateConstraints = { ...prev.constraints };

        if (!_.isNil(correctedKeywords)) {
          updateConstraints.keywords = correctedKeywords;
        }

        return {
          ...prev,
          contacts,
          totalContacts,
          searchRequestId: newSearchRequestId,
          searchHistoryId: newSearchHistoryId,
          searchContextId: newSearchContextId,
          constraints: updateConstraints,
        };
      });
    } else if (isLinkedinResyncRequiredError(res.error)) {
      startCheckAccountStatusInterval(linkedinAccountId);
      setRefineRecruiterSearchDialog((prev) => ({ ...prev, contactsLoading: false, preLoadingFields: false }));
    } else {
      setRefineRecruiterSearchDialog((prev) => ({ ...prev, contactsLoading: false, preLoadingFields: false }));
      handleOpenErrorDialog(res.error, '/talent-search/recruiter-contacts', 'post');
    }
  };

  const handleGetRecruiterConstraints = (searchType: RecruiterSearchKeys) => {
    return async () => {
      if (_.isNil(campaign)) return;

      const { linkedinAccount } = campaign;

      if (_.isNil(linkedinAccount)) return;

      const { constraints, crawlerLinkedinAccount } = refineRecruiterSearchDialog;

      const linkedinAccountId = crawlerLinkedinAccount || separateLinkedinAccount || linkedinAccount;

      const constraint = constraints[searchType];
      const query = constraint.query;
      const lang = i18n.languages[0];

      setRefineRecruiterSearchDialog((prev) => {
        const updateConstraints = { ...prev.constraints };
        updateConstraints[searchType].loading = true;

        return { ...prev, constraints: updateConstraints };
      });

      if (searchType === 'zip' && linkedinProduct === LinkedinTypes.SALES_NAV) {
        const postal = await getPostalCodeLocation(query, linkedinAccountId);
        if (!postal.error) {
          setRefineRecruiterSearchDialog((prev) => {
            const updateConstraints = { ...prev.constraints };
            const rawItems = postal.locations ?? postal.results ?? postal.data?.locations ?? postal.data?.results ?? [];
            const options = rawItems.map((item: { id: string; text: string }) => ({
              label: item.text,
              value: String(item.id),
              exclude: false,
            }));
            updateConstraints[searchType].options = options;
            updateConstraints[searchType].loading = false;
            return { ...prev, constraints: updateConstraints };
          });
        } else if (isLinkedinResyncRequiredError(postal.error)) {
          startCheckAccountStatusInterval(linkedinAccountId);
          setRefineRecruiterSearchDialog((prev) => {
            const updateConstraints = { ...prev.constraints };
            updateConstraints[searchType].loading = false;
            return { ...prev, constraints: updateConstraints };
          });
        } else {
          setRefineRecruiterSearchDialog((prev) => {
            const updateConstraints = { ...prev.constraints };
            updateConstraints[searchType].loading = false;
            return { ...prev, constraints: updateConstraints };
          });
          handleOpenErrorDialog(postal.error, '/linkedin/lookups/geo/postal-code', 'post');
        }
        return;
      }

      const res = await getRecruiterConstraints(searchType, query, lang, linkedinAccountId);

      if (!res.error) {
        setRefineRecruiterSearchDialog((prev) => {
          const updateConstraints = { ...prev.constraints };
          const options =
            res.values?.map((item: { entityUrn: string; text: string; titleType?: string }) => ({
              label: item.text,
              value: !_.isNil(item.titleType) ? `${item.titleType}::${item.entityUrn}` : item.entityUrn,
              titleType: item.titleType,
              exclude: false,
            })) || [];
          updateConstraints[searchType].options = options;
          updateConstraints[searchType].loading = false;

          return { ...prev, constraints: updateConstraints };
        });
      } else if (isLinkedinResyncRequiredError(res.error)) {
        startCheckAccountStatusInterval(linkedinAccountId);

        setRefineRecruiterSearchDialog((prev) => {
          const updateConstraints = { ...prev.constraints };
          updateConstraints[searchType].loading = false;

          return { ...prev, constraints: updateConstraints };
        });
      } else {
        setRefineRecruiterSearchDialog((prev) => {
          const updateConstraints = { ...prev.constraints };
          updateConstraints[searchType].loading = false;

          return { ...prev, constraints: updateConstraints };
        });
        handleOpenErrorDialog(res?.error, '/linkedin/lookups/recruiter-constraints', 'post');
      }
    };
  };

  const handleSelectRecruiterConstraint = (searchType: RecruiterSearchKeys) => {
    return (value: string) => {
      return () => {
        const updateConstraints = { ...refineRecruiterSearchDialog.constraints };

        const constraintObject = updateConstraints[searchType].options.find((o) => o.value === value);

        if (_.isNil(constraintObject)) return;

        if (updateConstraints[searchType].selected.some((item) => item.value === constraintObject.value)) {
          _.remove(updateConstraints[searchType].selected, (item) => item.value === value);
        } else {
          updateConstraints[searchType].selected.push(constraintObject);
        }

        if (searchType === 'zip') {
          updateConstraints.geo.selected = [];
        }

        if (searchType === 'geo') {
          updateConstraints.zip.selected = [];
          updateConstraints.area = '25';
        }

        setRefineRecruiterSearchDialog((prev) => ({ ...prev, constraints: updateConstraints }));

        const mappedConstraint = updateConstraints[searchType].selected.map((item) => ({
          entityUrn: !_.isNil(item.titleType) ? item.value.split('::')[1] : item.value,
          text: item.label,
          exclude: item.exclude,
          titleType: item.titleType,
        }));
        const payload: any = { [RefinedRecruiterSearchPayloadFields[searchType]]: mappedConstraint };

        if (searchType === 'zip') {
          payload.area = updateConstraints.area;
        }

        fetchRecruiterContacts(1, payload, searchType);
      };
    };
  };

  const handleRemoveRecruiterConstraint = (searchType: RecruiterSearchKeys, urn: string) => {
    return () => {
      const updateConstraints = { ...refineRecruiterSearchDialog.constraints };

      if (updateConstraints[searchType].selected.some((item) => item.value === urn)) {
        _.remove(updateConstraints[searchType].selected, (item) => item.value === urn);

        if (searchType === 'zip' && updateConstraints[searchType].selected.length === 0) {
          updateConstraints.area = '25';
        }
      }

      setRefineRecruiterSearchDialog((prev) => ({ ...prev, constraints: updateConstraints }));

      const mappedConstraint = updateConstraints[searchType].selected.map((item) => ({
        entityUrn: !_.isNil(item.titleType) ? item.value.split('::')[1] : item.value,
        text: item.label,
        exclude: item.exclude,
        titleType: item.titleType,
      }));
      const payload: any = { [RefinedRecruiterSearchPayloadFields[searchType]]: mappedConstraint };

      if (searchType === 'zip') {
        payload.area = updateConstraints.area;
      }

      fetchRecruiterContacts(1, payload, searchType);
    };
  };

  const handleExcludeChange = (searchType: RecruiterSearchKeys, urn: string) => {
    return (value: boolean) => {
      return () => {
        const updateConstraints = { ...refineRecruiterSearchDialog.constraints };

        const constraint = updateConstraints[searchType].selected.find((c) => c.value === urn);

        if (!_.isNil(constraint)) {
          constraint.exclude = value;
        }

        setRefineRecruiterSearchDialog((prev) => ({ ...prev, constraints: updateConstraints }));

        const mappedConstraint = updateConstraints[searchType].selected.map((item) => ({
          entityUrn: !_.isNil(item.titleType) ? item.value.split('::')[1] : item.value,
          text: item.label,
          exclude: item.exclude,
          titleType: item.titleType,
        }));
        const payload: any = { [RefinedRecruiterSearchPayloadFields[searchType]]: mappedConstraint };

        if (searchType === 'zip') {
          payload.area = updateConstraints.area;
        }

        fetchRecruiterContacts(1, payload, searchType);
      };
    };
  };

  const handleRecruiterConstraintQueryChange = (searchType: RecruiterSearchKeys) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setRefineRecruiterSearchDialog((prev) => {
        const updateConstraints = { ...prev.constraints };
        updateConstraints[searchType].query = event.target.value;

        return { ...prev, constraints: updateConstraints };
      });
    };
  };

  const searchConstraintsKeyUp = (searchType: RecruiterSearchKeys) => {
    return () => {
      clearTimeout(typingTimer);

      const { constraints } = refineRecruiterSearchDialog;
      const constraint = constraints[searchType];
      const query = constraint.query;

      if (query !== '') {
        typingTimer = setTimeout(() => {
          handleGetRecruiterConstraints(searchType)();
        }, 1000);
      } else {
        setRefineRecruiterSearchDialog((prev) => {
          const updateConstraints = { ...prev.constraints };
          updateConstraints[searchType].options = [];
          updateConstraints[searchType].loading = false;

          return { ...prev, constraints: updateConstraints };
        });
      }
    };
  };

  const searchConstraintsKeyDown = () => {
    clearTimeout(typingTimer);
  };

  const handleKeywordsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRefineRecruiterSearchDialog((prev) => {
      const updateConstraints = { ...prev.constraints };
      updateConstraints.keywords = event.target.value;

      return { ...prev, constraints: updateConstraints };
    });
  };

  const keywordKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      const { keywords } = refineRecruiterSearchDialog.constraints;

      if (keywords) {
        fetchRecruiterContacts(1, { keywords }, 'keywords');
      } else {
        fetchRecruiterContacts(1, { keywords: undefined }, 'keywords');
      }
    }
    clearTimeout(typingTimer);
  };

  const handleAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRefineRecruiterSearchDialog((prev) => {
      const updateConstraints = { ...prev.constraints };
      updateConstraints.area = event.target.value;

      return { ...prev, constraints: updateConstraints };
    });

    const { zip } = refineRecruiterSearchDialog.constraints;

    const mappedConstraint = zip.selected.map((item) => ({
      entityUrn: item.value,
      text: item.label,
      exclude: item.exclude,
    }));
    const payload: any = { area: event.target.value, postalCodes: mappedConstraint };

    fetchRecruiterContacts(1, payload, 'zip');
  };

  const handleTextFieldYearsOfExperienceChange = (index: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = +event.target.value;

      setRefineRecruiterSearchDialog((prev) => {
        const updateConstraints = { ...prev.constraints };
        updateConstraints.yearsOfExperience[index] = newValue;

        return { ...prev, constraints: updateConstraints };
      });
    };
  };

  const handleYearsOfExperienceChange = (event: Event, newValue: number | number[], activeThumb: number) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    const minDistance = 1;

    if (activeThumb === 0) {
      setRefineRecruiterSearchDialog((prev) => {
        const updateConstraints = { ...prev.constraints };
        const yearsOfExperience = updateConstraints.yearsOfExperience;
        updateConstraints.yearsOfExperience = [Math.min(newValue[0], yearsOfExperience[1] - minDistance), yearsOfExperience[1]];

        return { ...prev, constraints: updateConstraints };
      });
    } else {
      setRefineRecruiterSearchDialog((prev) => {
        const updateConstraints = { ...prev.constraints };
        const yearsOfExperience = updateConstraints.yearsOfExperience;
        updateConstraints.yearsOfExperience = [yearsOfExperience[0], Math.max(newValue[1], yearsOfExperience[0] + minDistance)];

        return { ...prev, constraints: updateConstraints };
      });
    }
  };

  const handleResetYearsOfExperience = () => {
    setRefineRecruiterSearchDialog((prev) => {
      const updateConstraints = { ...prev.constraints };
      updateConstraints.yearsOfExperience = [0, 30];

      return { ...prev, constraints: updateConstraints };
    });

    fetchRecruiterContacts(1, { yearsOfExperience: [0, 30] }, 'yearsOfExperience');
  };

  const handleUpdateYearsOfExperience = () => {
    const { yearsOfExperience } = refineRecruiterSearchDialog.constraints;

    const newYearsOfExperience = _.cloneDeep(yearsOfExperience);

    if (newYearsOfExperience[1] < 30 && newYearsOfExperience[1] !== 0) {
      newYearsOfExperience[1] -= 1;
    }

    fetchRecruiterContacts(1, { yearsOfExperience: newYearsOfExperience }, 'yearsOfExperience');
  };

  const handleChangeRecruiterSearchPage = (event: unknown, newPage: number) => {
    setRefineRecruiterSearchDialog((prev) => ({ ...prev, pageNumber: newPage }));
    fetchRecruiterContacts(newPage, {});
  };

  const handleAcceptRefinedRecruiterSearch = () => {
    const payload = getRecruiterSearchPayload();

    const { searchContextId, searchHistoryId, searchRequestId } = refineRecruiterSearchDialog;

    setLinkedinSearch((prev) => ({
      ...prev,
      result: { ...prev.result, payload, searchContextId, searchHistoryId, searchRequestId },
    }));
    handleCloseRefineRecruiterSearchDialog();

    if (
      _.isNil(campaign?.judgingSettings) ||
      (_.isNil(campaign?.judgingSettings.promptPlaceholders) && _.isNil(campaign?.judgingSettings.lastPrompt))
    ) {
      handleOpenQualifyContactsDialog();
    } else {
      handleOpenCreateNewSearchDialog();
    }
  };

  /**
   * Create new search handlers
   */

  const handleOpenCreateNewSearchDialog = () => {
    setCreateNewSearchDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseCreateNewSearchDialog = () => {
    setCreateNewSearchDialog({ open: false, loading: false, searchName: '', numberOfContactsToLoad: 50 });
  };

  const handleCreateSearchNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateNewSearchDialog((prev) => ({ ...prev, searchName: event.target.value }));
  };

  const handleCreateSearchNumberOfContactsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateNewSearchDialog((prev) => ({ ...prev, numberOfContactsToLoad: +event.target.value }));
  };

  const handleCreateNewSearch = async () => {
    if (_.isNil(campaign)) return;

    setCreateNewSearchDialog((prev) => ({ ...prev, loading: true }));

    const { searchName, numberOfContactsToLoad } = createNewSearchDialog;
    const { result, openIndex } = linkedinSearch;

    let urls;
    let payloads;

    if (!_.isNil(result)) {
      if (!_.isNil(result.URL)) {
        urls = [result.URL];
      }

      if (!_.isNil(result.payload)) {
        payloads = [result.payload];
      }
    }

    const res = await createSearches(
      campaign._id,
      LinkedinCrawlerSearchTypes.PEOPLE_SEARCH,
      urls,
      payloads,
      undefined,
      separateLinkedinAccount || undefined,
      undefined,
      undefined,
      searchName,
      numberOfContactsToLoad
    );

    if (!res.error) {
      handleCloseCreateNewSearchDialog();
      setLinkedinSearch((prev) => ({ ...prev, result: null }));
      if (!_.isNil(openIndex)) {
        setLinkedinSearch((prev) => ({ ...prev, openIndex: null, addedIndices: [...prev.addedIndices, openIndex] }));
      }
      handleOpenAddMoreSearchesDialog(searchName);
    } else {
      setCreateNewSearchDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:campaignId/search', 'post');
    }
  };

  /**
   * Add more searches dialog handlers
   */

  const handleOpenAddMoreSearchesDialog = (savedSearchName: string) => {
    setAddMoreSearchesDialog((prev) => ({ ...prev, open: true, searchName: savedSearchName }));
  };

  const handleCloseAddMoreSearchesDialog = () => {
    setAddMoreSearchesDialog({ open: false, searchName: '' });
  };

  const handleCloseAndProceedAddMoreSearchesDialog = () => {
    setAddMoreSearchesDialog({ open: false, searchName: '' });
    resetPaiContactSearch();
    setActiveCampaignPipelineStep(CampaignPipelineSteps.CONTACT_OVERVIEW);
    setContactSearchMethod('');
    fetchSearches();
  };

  /**
   * Qualify contacts dialog handlers
   */

  /**
   * Der Qualifizierungs-Dialog liest aus der Kampagne, nicht aus lokalen Standardwerten.
   *
   * Vorher setzte `handleOpenQualifyContactsDialog` nur `open: true`. Der Dialog zeigte
   * damit **immer** leere Kriterien und die Vorauswahl A+B — auch für eine Kampagne, in der
   * längst Kriterien und eine A/B/C-Auswahl gespeichert waren. Wer dann auf „Qualifizieren"
   * ging, überschrieb seine gespeicherten Kriterien mit dem leeren Formular. Das ist nicht
   * „speichert nicht", das ist stiller Datenverlust (Ticket 0ee28daa).
   *
   * Die Standardwerte greifen nur noch, wenn wirklich nichts gespeichert ist.
   */
  const qualifyDialogStateFromCampaign = () => {
    const judging = campaign?.judgingSettings;
    const gespeichert = judging?.promptPlaceholders;
    const auswahl = judging?.selectedOptions;

    return {
      loading: false,
      step: 0,
      promptPlaceholders: {
        required: gespeichert?.required || '',
        disqualifying: gespeichert?.disqualifying || '',
        preferred: gespeichert?.preferred || '',
      },
      selectedOptions: !_.isEmpty(auswahl) ? [...auswahl!] : [Qualifications.A, Qualifications.B],
      prompt: judging?.lastPrompt || '',
    };
  };

  const handleOpenQualifyContactsDialog = () => {
    setQualifyContactsDialog({ ...qualifyDialogStateFromCampaign(), open: true });
  };

  /**
   * Beim Schließen wird eine geänderte Auswahl gesichert, statt verworfen.
   *
   * Gespeichert wurde bisher ausschließlich in `startQualifyingContacts*`. Wer die Haken
   * bei A/B/C umsetzte und das Fenster schloss, verlor die Änderung — und sah beim nächsten
   * Öffnen wieder den Standard, ohne Hinweis darauf, dass nichts übernommen wurde.
   *
   * Bewusst ohne `isJudging` und ohne `rejudgeExistingContacts`: das Schließen eines
   * Fensters darf keine Bewertung starten und keine bestehende neu aufrollen.
   */
  const persistQualifySelectionIfChanged = async () => {
    if (_.isNil(campaign)) return;

    const aktuell = qualifyContactsDialog.selectedOptions || [];
    const gespeichert = campaign.judgingSettings?.selectedOptions || [];
    const gleich = aktuell.length === gespeichert.length && aktuell.every((o) => gespeichert.includes(o));
    if (gleich || _.isEmpty(aktuell)) return;

    const res = await updateJudgingSettings(campaign._id, { selectedOptions: aktuell });
    if (!res?.error) {
      setCampaign((prev) => (prev ? { ...prev, judgingSettings: { ...prev.judgingSettings!, selectedOptions: aktuell } } : prev));
    }
  };

  const handleCloseAndProceedQualifyContactsDialog = async () => {
    if (pendingTalentSearchStartRef.current) {
      handleCloseQualifyContactsDialog();
      await runTalentSearch(true);
      return;
    }

    void persistQualifySelectionIfChanged();
    setQualifyContactsDialog({
      ...qualifyDialogStateFromCampaign(),
      open: false,
    });

    if (
      _.isNil(campaign?.judgingSettings) ||
      (_.isNil(campaign?.judgingSettings.promptPlaceholders) && _.isNil(campaign?.judgingSettings.lastPrompt))
    ) {
      if (contactSearchMethod === ContactSearchMethods.WE_SEARCH) {
        handleOpenCreateNewSearchDialog();
      }
      if (contactSearchMethod === ContactSearchMethods.OWN_SEARCH) {
        addOwnLinkedinSearches();
      }
    }
  };

  const handleCloseQualifyContactsDialog = () => {
    pendingTalentSearchStartRef.current = false;
    void persistQualifySelectionIfChanged();
    // Zurückgesetzt wird auf den **gespeicherten** Stand, nicht auf einen Standardwert.
    // Die beiden Schließ-Pfade widersprachen sich hier sogar: einer setzte auf ['A'],
    // der andere auf ['A','B'] — je nachdem, wie man das Fenster verließ.
    setQualifyContactsDialog({
      ...qualifyDialogStateFromCampaign(),
      open: false,
    });
  };

  const handleNextQualifyContactsStep = () => {
    setQualifyContactsDialog((prev) => ({ ...prev, step: prev.step + 1 }));
  };

  const handlePrevQualifyContactsStep = () => {
    setQualifyContactsDialog((prev) => ({ ...prev, step: prev.step - 1 }));
  };

  const handleAiBriefingDetailsChange = (type: keyof QualificationPromptPlaceholders) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setQualifyContactsDialog((prev) => {
      const updateDialog = { ...prev };
      updateDialog.promptPlaceholders[type] = event.target.value;
      return updateDialog;
    });
  };

  const handleAiBriefingDetailsChangeForLeadBooster = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQualifyContactsDialog((prev) => {
      const updated = { ...prev };
      updated.prompt = event.target.value;
      return updated;
    });
  };

  const handleSelectedOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const option = event.target.value;
    setQualifyContactsDialog((prev) => ({
      ...prev,
      selectedOptions: event.target.checked ? [...prev.selectedOptions, option] : prev.selectedOptions.filter((o) => o !== option),
    }));
  };

  const startQualifyingContactsRecruiter = async () => {
    if (_.isNil(campaign)) return;

    const { promptPlaceholders, selectedOptions } = qualifyContactsDialog;

    setQualifyContactsDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateJudgingSettings(campaign._id, {
      promptPlaceholders,
      selectedOptions,
      rejudgeExistingContacts: false,
      isJudging: true,
    });

    if (!res.error) {
      const shouldResumeTalentSearch = pendingTalentSearchStartRef.current;
      setCampaign((prev) => ({ ...prev!, judgingSettings: { ...prev!.judgingSettings!, promptPlaceholders, selectedOptions } }));
      handleCloseQualifyContactsDialog();

      if (shouldResumeTalentSearch) {
        await runTalentSearch(true);
      } else if (contactSearchMethod === ContactSearchMethods.WE_SEARCH) {
        handleOpenCreateNewSearchDialog();
      }
      if (!shouldResumeTalentSearch && contactSearchMethod === ContactSearchMethods.OWN_SEARCH) {
        addOwnLinkedinSearches();
      }
    } else {
      setQualifyContactsDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:id', 'patch');
    }
  };

  const startQualifyingContactsLead = async () => {
    if (_.isNil(campaign)) return;

    const { prompt, selectedOptions } = qualifyContactsDialog;

    setQualifyContactsDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateJudgingSettings(campaign._id, {
      lastPrompt: prompt,
      selectedOptions,
      rejudgeExistingContacts: false,
      isJudging: true,
    });

    if (!res.error) {
      const shouldResumeTalentSearch = pendingTalentSearchStartRef.current;
      setCampaign((prev) => ({ ...prev!, judgingSettings: { ...prev!.judgingSettings!, lastPrompt: prompt, selectedOptions } }));
      handleCloseQualifyContactsDialog();

      if (shouldResumeTalentSearch) {
        await runTalentSearch(true);
      } else if (contactSearchMethod === ContactSearchMethods.WE_SEARCH) {
        handleOpenCreateNewSearchDialog();
      }
      if (!shouldResumeTalentSearch && contactSearchMethod === ContactSearchMethods.OWN_SEARCH) {
        addOwnLinkedinSearches();
      }
    } else {
      setQualifyContactsDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:id', 'patch');
    }
  };

  /**
   * AI contact qualifier drawer handlers
   */

  useEffect(() => {
    if (_.isNil(campaign?._id)) return;

    const { judgingSettings } = campaign;

    const promptPlaceholders: QualificationPromptPlaceholders = {
      required: '',
      disqualifying: '',
      preferred: '',
      additionalContactInfo: '',
      companyCharacteristics: '',
      companyChallenges: '',
      organizationalWorkingCultureValues: '',
    };

    let prompt = '';
    let hasOldKeys = false;

    if (!_.isNil(judgingSettings?.promptPlaceholders)) {
      promptPlaceholders.required = judgingSettings.promptPlaceholders.required ?? '';
      promptPlaceholders.disqualifying = judgingSettings.promptPlaceholders.disqualifying ?? '';
      promptPlaceholders.preferred = judgingSettings.promptPlaceholders.preferred ?? '';

      if (!_.isNil(judgingSettings.promptPlaceholders.additionalContactInfo) && judgingSettings.promptPlaceholders.additionalContactInfo !== '') {
        promptPlaceholders.additionalContactInfo = judgingSettings.promptPlaceholders.additionalContactInfo;
        hasOldKeys = true;
      }
      if (!_.isNil(judgingSettings.promptPlaceholders.companyCharacteristics) && judgingSettings.promptPlaceholders.companyCharacteristics !== '') {
        promptPlaceholders.companyCharacteristics = judgingSettings.promptPlaceholders.companyCharacteristics;
        hasOldKeys = true;
      }
      if (!_.isNil(judgingSettings.promptPlaceholders.companyChallenges) && judgingSettings.promptPlaceholders.companyChallenges !== '') {
        promptPlaceholders.companyChallenges = judgingSettings.promptPlaceholders.companyChallenges;
        hasOldKeys = true;
      }
      if (
        !_.isNil(judgingSettings.promptPlaceholders.organizationalWorkingCultureValues) &&
        judgingSettings.promptPlaceholders.organizationalWorkingCultureValues !== ''
      ) {
        promptPlaceholders.organizationalWorkingCultureValues = judgingSettings.promptPlaceholders.organizationalWorkingCultureValues;
        hasOldKeys = true;
      }
    }

    if (judgingSettings?.lastPrompt) {
      prompt = judgingSettings.lastPrompt;
    }

    let selectedOptions: string[] = ['A', 'B'];

    if (!_.isNil(judgingSettings?.selectedOptions) && !_.isEmpty(judgingSettings.selectedOptions)) {
      selectedOptions = judgingSettings.selectedOptions;
    }

    const isJudging = judgingSettings?.isJudging ?? false;

    setContactQualification((prev) => ({ ...prev, selectedOptions, promptPlaceholders, prompt, hasOldKeys, isJudging }));
  }, [campaign]);

  const handleAiQualifierDetailsChange = (type: keyof QualificationPromptPlaceholders) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setContactQualification((prev) => {
      const updateAiQualification = { ...prev };
      updateAiQualification.promptPlaceholders[type] = event.target.value;
      return updateAiQualification;
    });
  };

  const handleAiQualifierPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContactQualification((prev) => ({ ...prev, prompt: event.target.value }));
  };

  const handleAiQualifierSelectedOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const option = event.target.value;
    setContactQualification((prev) => ({
      ...prev,
      selectedOptions: event.target.checked ? [...prev.selectedOptions, option] : prev.selectedOptions.filter((o) => o !== option),
    }));
  };

  const handleRejudgeExistingContactsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContactQualification((prev) => ({ ...prev, rejudgeExistingContacts: event.target.checked }));
  };

  const handleIsJudgingChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const isJudgingValue = event.target.checked;

    // Update local state immediately for UI responsiveness
    setContactQualification((prev) => ({ ...prev, isJudging: isJudgingValue }));

    // Update backend immediately
    if (!_.isNil(campaign)) {
      try {
        const res = await updateJudgingSettings(campaign._id, {
          isJudging: isJudgingValue,
        });

        if (!res.error) {
          // Update campaign state to reflect the change
          setCampaign((prev) => ({
            ...prev!,
            judgingSettings: {
              ...prev!.judgingSettings!,
              isJudging: isJudgingValue,
            },
          }));
        } else {
          // Revert local state if API call failed
          setContactQualification((prev) => ({ ...prev, isJudging: !isJudgingValue }));
          handleOpenErrorDialog(res?.error, '/campaigns/:id', 'patch');
        }
      } catch (error) {
        // Revert local state if API call failed
        setContactQualification((prev) => ({ ...prev, isJudging: !isJudgingValue }));
        console.error('Error updating judging settings:', error);
      }
    }
  };

  const updateQualifyingContactsRecruiting = async () => {
    if (_.isNil(campaign)) return;

    const { promptPlaceholders, selectedOptions, rejudgeExistingContacts, hasOldKeys } = contactQualification;

    // Filter selectedOptions to only allow 'A', 'B', or 'C'
    const validOptions = ['A', 'B', 'C'];
    const filteredSelectedOptions = selectedOptions.filter((opt) => validOptions.includes(opt));

    if (!hasOldKeys) {
      promptPlaceholders.additionalContactInfo = undefined;
      promptPlaceholders.companyCharacteristics = undefined;
      promptPlaceholders.companyChallenges = undefined;
      promptPlaceholders.organizationalWorkingCultureValues = undefined;
    }

    setContactQualification((prev) => ({ ...prev, loading: true }));

    const res = await qualifyFunnelContactsRecruiting(
      campaign._id,
      promptPlaceholders,
      filteredSelectedOptions,
      rejudgeExistingContacts,
      true
    );

    if (!res.error) {
      setCampaign((prev) => ({
        ...prev!,
        judgingSettings: {
          ...prev!.judgingSettings!,
          promptPlaceholders,
          selectedOptions: filteredSelectedOptions,
        },
      }));
      handleOpenSuccessAlert(t('qualificationProcessStartedMessage'));
      setActiveCampaignPipelineStep(CampaignPipelineSteps.CONTACT_OVERVIEW);
      fetchContacts();
    } else {
      handleOpenErrorDialog(res?.error, '/campaigns/:id/judge-profiles', 'post');
    }

    setContactQualification((prev) => ({ ...prev, loading: false }));
  };

  const updateQualifyingContactsLead = async () => {
    if (_.isNil(campaign)) return;

    const { prompt, selectedOptions, rejudgeExistingContacts } = contactQualification;

    // Filter selectedOptions to only allow 'A', 'B', or 'C'
    const validOptions = ['A', 'B', 'C'];
    const filteredSelectedOptions = selectedOptions.filter((opt) => validOptions.includes(opt));

    setContactQualification((prev) => ({ ...prev, loading: true }));

    const res = await judgeFunnelProfiles(campaign._id, prompt, filteredSelectedOptions, rejudgeExistingContacts, true);

    if (!res.error) {
      setCampaign((prev) => ({
        ...prev!,
        judgingSettings: {
          ...prev!.judgingSettings!,
          lastPrompt: prompt,
          selectedOptions: filteredSelectedOptions,
        },
      }));
      handleOpenSuccessAlert(t('qualificationProcessStartedMessage'));
      setActiveCampaignPipelineStep(CampaignPipelineSteps.CONTACT_OVERVIEW);
      fetchContacts();
    } else {
      handleOpenErrorDialog(res?.error, '/campaigns/:id/judge-profiles', 'post');
    }

    setContactQualification((prev) => ({ ...prev, loading: false }));
  };

  /**
   * Contact preview drawer handlers
   */

  async function refreshContactList() {
    fetchContacts();
  }

  /**
   * Load contacts number dialog handlers
   */

  const handleOpenLoadContactsNumberDialog = (search: Search) => {
    return () => {
      setLoadContactsNumberDialog((prev) => ({ ...prev, open: true, search }));
    };
  };

  const handleCloseLoadContactsNumberDialog = () => {
    setLoadContactsNumberDialog({ open: false, loading: false, search: null, numberOfContactsToLoad: 50 });
  };

  const handleNumberOfContactsToLoadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoadContactsNumberDialog((prev) => ({ ...prev, numberOfContactsToLoad: +event.target.value }));
  };

  const handleContinueSearch = async () => {
    if (_.isNil(campaign)) return;

    const { search, numberOfContactsToLoad } = loadContactsNumberDialog;

    if (_.isNil(search)) return;

    setLoadContactsNumberDialog((prev) => ({ ...prev, loading: true }));

    // A finished search sits past its last page — continuing there would fetch
    // nothing. Reloading starts a fresh pass; contacts already in the campaign are
    // skipped server-side, so only the missing ones are added.
    const isFinishedSearch = search.status === ContactsSearchStatuses.FINISHED;
    const res = isFinishedSearch
      ? await reloadSearch(campaign._id, search._id, numberOfContactsToLoad)
      : await continueSearch(campaign._id, search._id, numberOfContactsToLoad);

    if (!res.error) {
      handleCloseLoadContactsNumberDialog();
      setActiveCampaignPipelineStep(CampaignPipelineSteps.CONTACT_OVERVIEW);
      fetchContacts();

      if (
        activeCampaignPipelineStep === CampaignPipelineSteps.MANAGE_SEARCHES ||
        activeCampaignPipelineStep === CampaignPipelineSteps.CONTACT_OVERVIEW
      ) {
        fetchSearches();
      }
      handleOpenSuccessAlert(isFinishedSearch ? t('searchReloadedSuccessMessage') : t('searchContinuedSuccessMessage'));
    } else {
      setLoadContactsNumberDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(
        res?.error,
        isFinishedSearch
          ? '/campaigns/:campaignId/searches/:searchId/reload'
          : '/campaigns/:campaignId/search/:searchId/continue',
        'post',
      );
    }
  };

  /**
   * Search objective handlers
   */

  const handleSelectContactSearchObjective = (newObjective: SearchObjective) => {
    return () => {
      if (newObjective.key !== selectedContactSearchObjective?.key) {
        setSelectedContactSearchObjective(newObjective);
        setSearchToAdd({
          key: newObjective.key,
          title: newObjective.title,
          subTitle: newObjective.subTitle,
          regex: newObjective.regex,
          search: { name: '', url: '', numberOfContactsToLoad: 50 },
        });
      } else {
        setSelectedContactSearchObjective(null);
        setSearchToAdd(null);
      }
    };
  };

  /**
   * Paste urls handlers
   */

  const handleOwnSearchFieldChange = (fieldType: keyof SingleSearch) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchToAdd((prev) => {
        if (_.isNil(prev)) return prev;

        const value = fieldType === 'numberOfContactsToLoad' ? parseInt(event.target.value, 10) : event.target.value;

        return { ...prev, search: { ...prev.search, [fieldType]: value } };
      });
    };
  };

  const handleJobTitlesChange = (titles: string[]) => {
    setSearchToAdd((prev) => {
      if (_.isNil(prev)) return prev;

      return { ...prev, search: { ...prev.search, targetJobTitles: titles } };
    });
  };

  const handlePeopleTypeChange = (type: 'includeReactions' | 'includeComments' | 'includeAuthor') => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchToAdd((prev) => {
        if (!prev) return null;

        return {
          ...prev,
          crawlerOptions: {
            ...prev.crawlerOptions,
            [type]: event.target.checked,
          },
        };
      });
    };
  };

  const handleProfileDegreeChange = (value: 1 | 2 | 3) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchToAdd((prev) => {
        if (!prev) return null;

        const currentDegrees = prev.crawlerOptions?.includedProfileDegrees || [];
        let newDegrees = [...currentDegrees];

        if (event.target.checked) {
          if (!newDegrees.includes(value)) {
            newDegrees.push(value);
          }
        } else {
          newDegrees = newDegrees.filter((deg) => deg !== value);
        }

        return {
          ...prev,
          crawlerOptions: {
            ...prev.crawlerOptions,
            includedProfileDegrees: newDegrees,
          },
        };
      });
    };
  };

  /**
   * Separate LinkedIn account handlers
   */

  const getBestLinkedinProductForAccount = (liProductType?: LinkedinTypes): LinkedinTypes => {
    if (liProductType === LinkedinTypes.RECRUITER) return LinkedinTypes.RECRUITER;
    if (liProductType === LinkedinTypes.SALES_NAV) return LinkedinTypes.SALES_NAV;
    return LinkedinTypes.LINKEDIN;
  };

  /**
   * "Kandidaten finden" opens on the KI search, for Recruiting and Sales alike. The user
   * only types the location: method, sourcing account and the account's best LinkedIn
   * product are filled in here instead of being asked one section at a time.
   */
  useEffect(() => {
    if (activeCampaignPipelineStep !== CampaignPipelineSteps.ADD_CONTACTS || _.isNil(campaign)) return;

    if (contactSearchMethod === '') {
      setContactSearchMethod(ContactSearchMethods.WE_SEARCH);
      setSeparateLinkedinAccount(campaign.linkedinAccount || '');
      return;
    }

    if (contactSearchMethod === ContactSearchMethods.WE_SEARCH && !linkedinProduct) {
      const sourcingAccount =
        separateLinkedinAccount && separateLinkedinAccount !== campaign.linkedinAccount ? fullSeparateLinkedinAccount : fullLinkedinAccount;
      if (!_.isNil(sourcingAccount)) {
        setLinkedinProduct(getBestLinkedinProductForAccount(sourcingAccount.liProductType));
      }
    }
  }, [
    activeCampaignPipelineStep,
    campaign?._id,
    contactSearchMethod,
    linkedinProduct,
    separateLinkedinAccount,
    fullSeparateLinkedinAccount,
    fullLinkedinAccount,
  ]);

  const handleSeparateLinkedinAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSeparateLinkedinAccount(event.target.value);
    handleResetLocationSettings();
    handleResetLinkedinSearch();
    setPaiSearchLanguage(['default', 'en']);

    if (event.target.value === '') {
      setFullSeparateLinkedinAccount(undefined);
      setLinkedinProduct(getBestLinkedinProductForAccount(fullLinkedinAccount?.liProductType));
    } else {
      (async () => {
        const res = await getLinkedinAccountById(event.target.value);

        if (!res.error) {
          setFullSeparateLinkedinAccount(res);
          setLinkedinProduct(getBestLinkedinProductForAccount(res.liProductType));
        }
      })();
    }
  };

  /**
   * Add searches handlers
   */

  const handleAddOwnLinkedinSearch = () => {
    if (
      _.isNil(campaign?.judgingSettings) ||
      (_.isNil(campaign?.judgingSettings.promptPlaceholders) && _.isNil(campaign?.judgingSettings.lastPrompt))
    ) {
      handleOpenQualifyContactsDialog();
    } else {
      addOwnLinkedinSearches();
    }
  };

  const handleAddOwnLinkedinSearchWithSkipQualification = () => {
    addOwnLinkedinSearches();
  };

  const addOwnLinkedinSearches = async () => {
    if (!campaign) return;

    setAddSearchesLoading(true);

    const crawlerOpts = searchToAdd?.crawlerOptions;
    const finalCrawlerOptions = !crawlerOpts || _.isNil(crawlerOpts) ? undefined : crawlerOpts;

    const searchUrl = searchToAdd?.search.url || '';
    const searchName = searchToAdd?.search.name || '';
    const searchNumber = searchToAdd?.search.numberOfContactsToLoad || 0;
    const isJobPostings = selectedContactSearchObjective?.key === 'jobPostings';
    const targetJobTitles = searchToAdd?.search.targetJobTitles || [];

    const res = await createSearches(
      campaign._id,
      LinkedinCrawlerSearchTypes.PEOPLE_SEARCH,
      [searchUrl],
      undefined,
      finalCrawlerOptions,
      separateLinkedinAccount || undefined,
      undefined,
      undefined,
      searchName,
      searchNumber,
      isJobPostings ? targetJobTitles : undefined
    );

    if (!res.error) {
      setAddSearchesLoading(false);

      setActiveCampaignPipelineStep(CampaignPipelineSteps.CONTACT_OVERVIEW);
      fetchSearches();

      setSelectedContactSearchObjective(null);
      setSearchToAdd(null);
      setContactSearchMethod('');
    } else {
      setAddSearchesLoading(false);
      handleOpenErrorDialog(res?.error, '/campaigns/:campaignId/search', 'post');
    }
  };

  /**
   * File upload handlers
   */

  const readFiles = (files: any[]) => {
    files.forEach((file: any) => {
      const reader = new FileReader();

      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');

      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        reader.onload = (e: any) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const parsedData: string[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (!_.isNil(parsedData)) {
            handleOpenFileUploadDialog(parsedData, file.name);
          }
        };
        reader.readAsArrayBuffer(file);
      } else if (file.name.endsWith('.csv')) {
        reader.onload = (e: any) => {
          const text = e.target.result;
          const parsedData = parse(text, { delimiter: ';' });

          if (!_.isNil(parsedData.data)) {
            handleOpenFileUploadDialog(parsedData.data as string[][], file.name);
          }
        };
        reader.readAsText(file);
      } else {
        console.log('Unsupported file type');
      }
    });
  };

  const onFileDrop = useCallback(
    (files: any) => {
      if (files.length > 0) {
        readFiles(files);
      }
    },
    [fileUploadDialog, contactsToAdd]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const acceptedFiles = event.target.files;
    const files = !_.isNil(acceptedFiles) ? Array.from(acceptedFiles) : [];

    if (files.length > 0) {
      readFiles(files);
    }

    event.target.value = '';
  };

  const handleOpenFileUploadDialog = (data: string[][], fileName: string) => {
    const columns = data[1];
    const newColumns = Array.from({ length: columns.length }, (_, index) => ({
      text: columns[index] || '',
      type: '',
    }));

    setFileUploadDialog((prev) => ({
      ...prev,
      open: true,
      data: [...prev.data, ...data],
      columns: newColumns,
      fileNames: [...prev.fileNames, fileName],
    }));
  };

  const handleColumnTypeChange = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateFileColumnDialog = { ...fileUploadDialog };

      if (event.target.value === FileUploadMatchColumns.EMPTY) {
        updateFileColumnDialog.columns[idx].type = '';
      } else {
        const existingSameType = updateFileColumnDialog.columns.find((col) => col?.type === event.target.value);

        if (!_.isNil(existingSameType)) {
          existingSameType.type = '';
        }

        updateFileColumnDialog.columns[idx].type = event.target.value;
      }

      setFileUploadDialog(updateFileColumnDialog);
    };
  };

  const handleHasTableHeadingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { data, columns } = fileUploadDialog;

    const columnStrings = data[event.target.checked ? 1 : 0];

    if (!columnStrings) return;

    let newColumns: { text: string; type: string }[] = [];

    columnStrings.forEach((col, index) => {
      const updateColumns = [...columns];
      updateColumns[index].text = col;
      newColumns = updateColumns;
    });

    setFileUploadDialog((prev) => ({ ...prev, columns: newColumns, hasTableHeading: event.target.checked }));
  };

  const handleCloseFileUploadDialog = () => {
    setFileUploadDialog({ open: false, data: [], columns: [], fileNames: [], hasTableHeading: true });
  };

  const handleAcceptColumns = () => {
    const { data, columns, hasTableHeading, fileNames } = fileUploadDialog;

    const transformedData: {
      _id: string;
      name: string;
      firstName?: string;
      lastName?: string;
      url?: string;
      email?: string;
      currentCompanies: string[];
      occupation?: string;
      customField?: string;
      phoneNumber?: string;
    }[] = data
      .filter((row) => row.length > 1)
      .map((row: string[]) => {
        const firstNameIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.FIRST_NAME);
        const lastNameIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.LAST_NAME);
        const urlIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.LINKEDIN_URL);
        const companyIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.COMPANY);
        const emailIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.EMAIL);
        const positionIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.POSITION);
        const customFieldIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.CUSTOM_FIELD);
        const phoneNumberIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.PHONE_NUMBER);

        let contactName: string = '';
        let contactFirstName: string | undefined = undefined;
        let contactLastName: string | undefined = undefined;
        let contactUrl: string | undefined = undefined;
        let contactCompany: string | undefined = undefined;
        let contactEmail: string | undefined = undefined;
        let contactPosition: string | undefined = undefined;
        let customField: string | undefined = undefined;
        let phoneNumber: string | undefined = undefined;

        const hasFirstName = !_.isNil(firstNameIndex) && firstNameIndex >= 0;
        const hasLastName = !_.isNil(lastNameIndex) && lastNameIndex >= 0;

        if (hasFirstName || hasLastName) {
          if (hasFirstName && hasLastName) {
            contactName = `${row[firstNameIndex]} ${row[lastNameIndex]}`;
            contactFirstName = row[firstNameIndex];
            contactLastName = row[lastNameIndex];
          } else if (hasFirstName && !hasLastName) {
            contactName = row[firstNameIndex];
            contactFirstName = row[firstNameIndex];
          } else if (hasLastName && !hasFirstName) {
            contactName = row[lastNameIndex];
            contactLastName = row[lastNameIndex];
          }
        }

        if (!_.isNil(urlIndex) && urlIndex >= 0) {
          contactUrl = row[urlIndex];
        }

        if (!_.isNil(companyIndex) && companyIndex >= 0) {
          contactCompany = row[companyIndex] || undefined;
        }

        if (!_.isNil(emailIndex) && emailIndex >= 0) {
          contactEmail = row[emailIndex] || undefined;
        }

        if (!_.isNil(positionIndex) && positionIndex >= 0) {
          contactPosition = row[positionIndex] || undefined;
        }

        if (!_.isNil(customFieldIndex) && customFieldIndex >= 0) {
          customField = row[customFieldIndex] || undefined;
        }

        if (!_.isNil(phoneNumberIndex) && phoneNumberIndex >= 0) {
          phoneNumber = row[phoneNumberIndex] || undefined;
        }

        return {
          _id: uuidv4(),
          name: contactName,
          firstName: contactFirstName,
          lastName: contactLastName,
          url: contactUrl,
          currentCompanies: contactCompany ? [contactCompany] : [],
          email: contactEmail,
          occupation: contactPosition,
          customField: customField,
          phoneNumber: phoneNumber,
        };
      });

    const contacts = hasTableHeading ? _.tail(transformedData) : transformedData;
    const hasUrlColumn = contacts.some((contact) => !!contact.url);
    let filteredContacts = contacts;

    if (hasUrlColumn) {
      // If we have LinkedIn URLs, require name and URL
      filteredContacts = contacts.filter((contact) => contact.name && contact.url && isLinkedinContactUrl(contact.url));
    } else {
      // If no LinkedIn URLs, accept contacts with either:
      // 1. Name (First Name or Last Name)
      // 2. Position/Occupation AND Company
      filteredContacts = contacts.filter(
        (contact) => contact.name || (contact.occupation && contact.currentCompanies && contact.currentCompanies.length > 0)
      );
    }

    setHasLinkedinUrls(hasUrlColumn);
    setContactsToAdd((prev) => [...prev, ...filteredContacts]);
    setFileName(fileNames.join(', '));
    handleCloseFileUploadDialog();
  };

  const handleDeleteContacts = () => {
    setContactsToAdd([]);
    setFileName('');
    setHasLinkedinUrls(true);
  };

  /**
   * Add contacts handlers
   */

  const handleAddContactsFromFile = () => {
    if (_.isNil(campaign)) return;

    (async () => {
      setAddContactsLoading(true);

      const chunkedContacts = _.chunk(contactsToAdd, 250);
      const responses = [];
      // Sequentially process each chunk
      if (hasLinkedinUrls) {
        for (const chunk of chunkedContacts) {
          chunk.forEach((contact) => {
            if (!_.isNil(contact._id)) {
              delete contact._id;
            }
            if (!_.isNil(contact.firstName)) {
              delete contact.firstName;
            }
            if (!_.isNil(contact.lastName)) {
              delete contact.lastName;
            }
            if (contact.occupation === '') {
              delete contact.occupation;
            }
            contact.fileName = fileName;
          });
          const res = await updateFunnel(campaign._id, { addContacts: chunk });
          responses.push(res);
        }
      } else {
        for (const chunk of chunkedContacts) {
          chunk.forEach((contact) => {
            if (!_.isNil(contact._id)) {
              delete contact._id;
            }
            if (!_.isNil(contact.name)) {
              delete contact.name;
            }
          });
          const res = await createSearches(
            campaign._id,
            LinkedinCrawlerSearchTypes.URL_FINDER,
            undefined,
            undefined,
            undefined,
            undefined,
            chunk,
            SearchTypes.CONTACTS
          );
          responses.push(res);
        }
      }

      if (!responses.some((res) => res.error)) {
        const resCampaign = await getFunnelById(campaign._id);

        if (!resCampaign.error) {
          resetContactUpload();

          setCampaign(resCampaign);
          setContactSearchMethod('');
          setActiveCampaignPipelineStep(CampaignPipelineSteps.CONTACT_OVERVIEW);

          fetchContacts();
          fetchSearches();

          if (_.isNil(campaign.judgingSettings) || _.isNil(campaign.judgingSettings.promptPlaceholders)) {
            handleOpenQualifyContactsDialog();
          }
        }

        setAddContactsLoading(false);
      } else {
        setAddContactsLoading(false);
        responses.forEach((res) => {
          if (res.error) {
            handleOpenErrorDialog(res?.error, '/campaigns/:id', 'patch');
          }
        });
      }
    })();
  };

  /**
   * Qualify contacts handlers
   */

  const handleSingleContactIsActiveChange = (contact: FunnelContact) => {
    return async () => {
      if (_.isNil(campaign)) return;

      const newIsActive = !contact.isActive;

      const res = await updateContactIsActive(
        campaign._id,
        contact.linkedInURL ? [contact.linkedInURL] : [],
        newIsActive,
        contact.contactId ? [contact.contactId] : [],
      );

      if (!res.error) {
        fetchContacts();

        if (newIsActive) {
          handleOpenSuccessAlert(t('contactActivatedSuccessMessage'));
        } else {
          handleOpenSuccessAlert(t('contactDeactivatedSuccessMessage'));
        }

        if (contacts.length === 1 && contactFilter === ContactFilter.IS_ACTIVE && !newIsActive) {
          setContactsPage((prev) => (prev === 1 ? 1 : prev - 1));
        }

        if (contacts.length === 1 && contactFilter === ContactFilter.IS_NOT_ACTIVE && newIsActive) {
          setContactsPage((prev) => (prev === 1 ? 1 : prev - 1));
        }
      } else {
        handleOpenErrorDialog(res?.error, '/campaigns/:id/contacts', 'put');
      }
    };
  };

  const handleMultiContactIsActiveChange = async () => {
    if (_.isNil(campaign)) return;

    // The table only holds the current page. "Activate all" means the filtered
    // list, not the 25 rows on screen.
    const listRes = await getAllFunnelContacts(campaign._id, {
      filter: contactFilter || undefined,
      q: contactsSearchParam || undefined,
      limit: 10000,
      offset: 0,
    });
    const pool: FunnelContact[] =
      !listRes?.error && Array.isArray(listRes?.funnelContacts) && listRes.funnelContacts.length > 0
        ? listRes.funnelContacts
        : contacts;

    const activeContacts = pool.filter((contact) => contact.isActive);
    const eligibleForActivation = pool.filter((contact) => !contact.isActive && isEligibleForActivation(contact));

    // If there are no eligible contacts to activate and there are active contacts, we want to deactivate all
    const allEligibleActive = eligibleForActivation.length === 0 && activeContacts.length > 0;

    let contactsToBeChanged: FunnelContact[] = [];
    let newIsActive: boolean;

    if (allEligibleActive) {
      // Deactivate all active contacts
      contactsToBeChanged = activeContacts;
      newIsActive = false;
    } else {
      // Activate only eligible contacts that are not already active
      contactsToBeChanged = eligibleForActivation;
      newIsActive = true;
    }

    const linkedInUrls = contactsToBeChanged.map((contact) => contact.linkedInURL).filter((url) => Boolean(url?.trim()));
    const contactIds = contactsToBeChanged.map((contact) => contact.contactId).filter((id): id is string => Boolean(id?.trim()));
    if (linkedInUrls.length === 0 && contactIds.length === 0) return;

    const res = await updateContactIsActive(campaign._id, linkedInUrls, newIsActive, contactIds);

    if (!res.error) {
      fetchContacts();
      handleOpenSuccessAlert(
        newIsActive
          ? t('contactsActivatedSuccessMessage', { count: Math.max(linkedInUrls.length, contactIds.length, contactsToBeChanged.length) })
          : t('contactsDeactivatedSuccessMessage', { count: Math.max(linkedInUrls.length, contactIds.length, contactsToBeChanged.length) })
      );

      if (contactFilter === ContactFilter.IS_ACTIVE && !newIsActive && contacts.length === contactsToBeChanged.length) {
        setContactsPage((prev) => (prev === 1 ? 1 : prev - 1));
      }
      if (contactFilter === ContactFilter.IS_NOT_ACTIVE && newIsActive && contacts.length === contactsToBeChanged.length) {
        setContactsPage((prev) => (prev === 1 ? 1 : prev - 1));
      }
    } else {
      handleOpenErrorDialog(res?.error, '/campaigns/:id/contacts', 'put');
    }
  };

  /**
   * Delete contacts permanently handlers
   */

  const handleOpenDeleteContactPermanentlyDialog = (contact: FunnelContact) => {
    return () => {
      setDeleteContactPermanentlyDialog((prev) => ({ ...prev, open: true, contact }));
    };
  };

  const handleCloseDeleteContactPermanentlyDialog = () => {
    setDeleteContactPermanentlyDialog({ open: false, loading: false, contact: null });
  };

  const handleDeleteContactPermanently = async () => {
    if (_.isNil(campaign)) return;

    const { contact } = deleteContactPermanentlyDialog;

    if (_.isNil(contact)) return;

    setDeleteContactPermanentlyDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateContactIsDeleted(campaign._id, [contact.linkedInURL], true);

    if (!res.error) {
      fetchContacts();
      handleCloseDeleteContactPermanentlyDialog();
      handleOpenSuccessAlert(t('contactDeletedSuccessMessage'));

      if (contacts.length === 1) {
        setContactsPage((prev) => (prev === 1 ? 1 : prev - 1));
      }
    } else {
      setDeleteContactPermanentlyDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:id/contacts', 'put');
    }
  };

  /**
   * Change ai judgement dialog handlers
   */

  const handleOpenChangeAiJudgementDialog = (contact: FunnelContact) => {
    return () => {
      setChangeAiJudgementDialog((prev) => ({ ...prev, open: true, contact, newJudgement: contact.aiJudgement ?? '' }));
    };
  };

  const handleCloseChangeAiJudgementDialog = () => {
    setChangeAiJudgementDialog({ open: false, contact: null, loading: false, newJudgement: '' });
  };

  const handleNewJudgementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChangeAiJudgementDialog((prev) => ({ ...prev, newJudgement: event.target.value }));
  };

  const handleChangeAiJudgement = async () => {
    if (_.isNil(campaign)) return;

    const { contact, newJudgement } = changeAiJudgementDialog;

    if (_.isNil(contact)) return;

    setChangeAiJudgementDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateContactAiJudgement(campaign._id, [contact.linkedInURL], newJudgement);

    if (!res.error) {
      fetchContacts();
      handleCloseChangeAiJudgementDialog();
      handleOpenSuccessAlert(t('aiJudgementUpdatedSuccessMessage'));
    } else {
      setChangeAiJudgementDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:id/contacts', 'put');
    }
  };

  /**
   * Rename contact search dialog handlers
   */

  const handleOpenRenameContactSearchDialog = (search: Search) => {
    return () => {
      setRenameContactSearchDialog((prev) => ({ ...prev, open: true, search, name: search.name || '' }));
    };
  };

  const handleCloseRenameContactSearchDialog = () => {
    setRenameContactSearchDialog({ open: false, loading: false, search: null, name: '' });
  };

  const handleSearchNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRenameContactSearchDialog((prev) => ({ ...prev, name: event.target.value }));
  };

  const handleRenameSearch = async () => {
    const { search, name } = renameContactSearchDialog;

    if (_.isNil(search)) return;
    if (_.isNil(campaign)) return;

    setRenameContactSearchDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateSearchName(campaign._id, search._id, name);

    if (!res.error) {
      fetchSearches();
      handleCloseRenameContactSearchDialog();
      handleOpenSuccessAlert(t('searchUpdatedSuccessMessage'));
    } else {
      setRenameContactSearchDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:campaignId/search/:searchId', 'put');
    }
  };

  /**
   * Edit LinkedIn account of a search
   */

  const handleOpenEditSearchLinkedinAccountDialog = (search: Search) => {
    return () => {
      setEditSearchLinkedinAccountDialog((prev) => ({ ...prev, open: true, search }));
    };
  };

  const handleCloseEditSearchLinkedinAccountDialog = () => {
    setEditSearchLinkedinAccountDialog({ open: false, loading: false, search: null, selectedLinkedinAccount: '' });
  };

  const handleSearchLinkedinAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditSearchLinkedinAccountDialog((prev) => ({ ...prev, open: true, selectedLinkedinAccount: event.target.value }));
  };

  const handleUpdateSearchLinkedinAccount = async () => {
    if (_.isNil(campaign)) return;

    const { search, selectedLinkedinAccount } = editSearchLinkedinAccountDialog;

    if (_.isNil(search)) return;

    setEditSearchLinkedinAccountDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateSearch(campaign._id, search._id, { linkedinAccount: selectedLinkedinAccount });

    if (!res.error) {
      handleCloseEditSearchLinkedinAccountDialog();
      fetchSearches();
      handleOpenSuccessAlert(t('searchUpdatedSuccessMessage'));
    } else {
      setEditSearchLinkedinAccountDialog((prev) => ({ ...prev, loading: false }));
      if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
        handleOpenCopilotLicenseExpiredDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/campaigns/:campaignId/search/:searchId', 'put');
      }
    }
  };

  /**
   * Delete contacts search dialog handlers
   */

  const handleOpenDeleteContactsSearchDialog = (search: Search) => {
    return () => {
      setDeleteContactsSearchDialog((prev) => ({ ...prev, open: true, search }));
    };
  };

  const handleCloseDeleteContactsSearchDialog = () => {
    setDeleteContactsSearchDialog({ open: false, loading: false, search: undefined, deleteContacts: false });
  };

  const handleDeleteSearchContactsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeleteContactsSearchDialog((prev) => ({ ...prev, deleteContacts: event.target.checked }));
  };

  const handleDeleteSearch = async () => {
    if (_.isNil(campaign)) return;

    const { search, deleteContacts } = deleteContactsSearchDialog;
    if (_.isNil(search)) return;

    setDeleteContactsSearchDialog((prev) => ({ ...prev, loading: true }));

    const res = await deleteSearch(campaign._id, search._id, deleteContacts);

    if (!res.error) {
      fetchSearches();
      if (deleteContacts) {
        resetContactsPage();
        resetFinalContactsPage();
      }
      handleCloseDeleteContactsSearchDialog();
      handleOpenSuccessAlert(t('deleteSearchSuccessMessage'));
    } else {
      setDeleteContactsSearchDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:campaignId/search/:searchId', 'delete');
    }
  };

  /**
   * Delete contacts file dialog handlers
   */

  const handleOpenDeleteContactsFileDialog = (file: FileUpload) => {
    return () => {
      setDeleteContactsFileDialog((prev) => ({ ...prev, open: true, file }));
    };
  };

  const handleCloseDeleteContactsFileDialog = () => {
    setDeleteContactsFileDialog({ open: false, loading: false, file: null });
  };

  const handleDeleteContactsFile = async () => {
    if (_.isNil(campaign)) return;
    const { file } = deleteContactsFileDialog;
    if (_.isNil(file)) return;

    setDeleteContactsFileDialog((prev) => ({ ...prev, loading: true }));

    const res = await deleteFileUpload(campaign._id, file.name);

    if (!res.error) {
      fetchSearches();
      handleCloseDeleteContactsFileDialog();
    } else {
      setDeleteContactsFileDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:campaignId/file', 'delete');
    }
  };

  /**
   * Add single contact dialog handlers
   */

  const handleOpenAddSingleContactDialog = () => {
    setAddSingleContactDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseAddSingleContactDialog = () => {
    setAddSingleContactDialog({
      open: false,
      loading: false,
      contact: { linkedinUrl: '', firstName: '', lastName: '', email: '', company: '', phoneNumber: '' },
    });
  };

  const handleSingleContactFieldChange = (type: keyof AddContact) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddSingleContactDialog((prev) => ({ ...prev, contact: { ...prev.contact, [type]: event.target.value } }));
    };
  };

  const handleAddSingleContact = async () => {
    if (_.isNil(campaign)) return;

    const addContact: LinkedInCrawlerContact = { url: '' };

    const { linkedinUrl, firstName, lastName, email, company, phoneNumber } = addSingleContactDialog.contact;

    if (linkedinUrl) {
      addContact.url = linkedinUrl;
    }

    if (firstName || lastName) {
      addContact.name =
        !_.isNil(firstName) && !_.isNil(lastName) ? `${firstName} ${lastName}` : !_.isNil(firstName) ? firstName : !_.isNil(lastName) ? lastName : '';
    }

    if (email) {
      addContact.email = email;
    }

    if (company) {
      addContact.currentCompanies = [company];
    }

    if (phoneNumber) {
      addContact.phoneNumber = phoneNumber;
    }

    setAddSingleContactDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateFunnel(campaign._id, { addContacts: [addContact] });

    if (!res.error) {
      setCampaign((prev) => ({ ...prev!, funnelContacts: res.funnelContacts || [] }));
      fetchContacts();
      handleCloseAddSingleContactDialog();
      handleOpenSuccessAlert(t('contactAddedSuccessMessage'));
    } else {
      setAddSingleContactDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:id', 'patch');
    }
  };

  /**
   * Add multiple contacts
   */

  const handleAddMultipleContacts = () => {
    setActiveCampaignPipelineStep(CampaignPipelineSteps.ADD_CONTACTS);
  };

  /**
   * Manage blacklists
   *
   */

  const handleBlacklistScopeChange = (newScope: BlacklistScopes) => {
    return () => {
      setActiveBlacklistScope(newScope);

      if (newScope === BlacklistScopes.GLOBAL) {
        fetchGlobalBlacklists(true);
      }
      if (newScope === BlacklistScopes.LOCAL) {
        fetchLocalBlacklists(true);
      }
    };
  };

  const handleRemoveContactFromBlacklist = (linkedInURL: string) => {
    return async () => {
      if (_.isNil(campaign)) return;

      const res = await updateContactIsBlacklisted(campaign._id, [linkedInURL], false);

      if (!res.error) {
        fetchContacts();
        handleOpenSuccessAlert(t('contactRemovedFromBlacklistSuccessMessage'));

        if (contactFilter === ContactFilter.BLACKLISTED && contacts.length === 1) {
          setContactsPage((prev) => (prev === 1 ? 1 : prev - 1));
        }
      } else {
        handleOpenErrorDialog(res?.error, '/campaigns/:id/contacts', 'put');
      }
    };
  };

  const handleRemoveContactFromSuspended = (linkedInURL: string) => {
    return async () => {
      if (_.isNil(campaign)) return;

      const res = await updateContactIsSuspended(campaign._id, [linkedInURL], false);

      if (!res.error) {
        fetchContacts();
        handleOpenSuccessAlert(t('contactRemovedFromSuspendedSuccessMessage'));

        if (contactFilter === ContactFilter.SUSPENDED && contacts.length === 1) {
          setContactsPage((prev) => (prev === 1 ? 1 : prev - 1));
        }
      } else {
        handleOpenErrorDialog(res?.error, '/campaigns/:id/contacts', 'put');
      }
    };
  };

  const handleRecoverContactFromDeleted = (linkedInURL: string) => {
    return async () => {
      if (_.isNil(campaign)) return;

      const res = await updateContactIsDeleted(campaign._id, [linkedInURL], false);

      if (!res.error) {
        fetchContacts();
        handleOpenSuccessAlert(t('contactRecoveredFromDeletedSuccessMessage'));

        if (contactFilter === ContactFilter.DELETED && contacts.length === 1) {
          setContactsPage((prev) => (prev === 1 ? 1 : prev - 1));
        }
      } else {
        handleOpenErrorDialog(res?.error, '/campaigns/:id/contacts', 'put');
      }
    };
  };

  /**
   * Add local blacklist dialog handlers
   */

  const handleOpenAddLocalBlacklistDialog = () => {
    setAddLocalBlacklistDialog((prev) => ({
      ...prev,
      open: true,
      selectedLinkedinAccount: prev.selectedLinkedinAccount || campaign?.linkedinAccount || linkedinAccounts[0]?._id || '',
    }));
  };

  const handleCloseAddLocalBlacklistDialog = () => {
    setAddLocalBlacklistDialog({
      open: false,
      loading: false,
      blacklist: null,
      blacklistName: '',
      blacklistType: BlacklistTypes.CONTACT,
      blacklistUploadVariant: BlacklistUploadVariant.FILE,
      individualItemsTextField: '',
      individualItemsTextFieldError: null,
      individualItemsList: [],
      fileColumnSelection: {
        columns: [],
        data: [],
        fileNames: [],
        hasTableHeading: true,
      },
      fileItemsList: [],
      selectedLinkedinAccount: campaign?.linkedinAccount || linkedinAccounts[0]?._id || '',
      companyQuery: '',
      companyOptions: [],
      selectedCompanies: [],
      companyLoading: false,
      companyIsEmpty: false,
    });
  };

  const handleBlacklistNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddLocalBlacklistDialog((prev) => ({ ...prev, blacklistName: event.target.value }));
  };

  const handleCreateLocalBlacklist = async () => {
    if (_.isNil(campaign)) return;

    setAddLocalBlacklistDialog((prev) => ({ ...prev, loading: true }));

    const { blacklistName, blacklistType, blacklistUploadVariant, individualItemsList, fileItemsList, selectedCompanies } = addLocalBlacklistDialog;

    const newBlacklistItems = buildBlacklistItemsFromDialog({
      blacklistType,
      blacklistUploadVariant,
      individualItemsList,
      fileItemsList,
      selectedCompanies,
    });

    const res = await createLocalBlacklist(blacklistType, blacklistName, newBlacklistItems, campaign._id);

    if (!res.error) {
      await refreshBlacklistViews(true);
      setCampaign((prev) => {
        if (!_.isNil(prev)) {
          const updateFunnel = { ...prev };
          const selectedBlacklists: string[] = _.cloneDeep((updateFunnel as any).blacklists ?? (updateFunnel as any).blacklistIds ?? []);
          selectedBlacklists.push(res._id);
          (updateFunnel as any).blacklists = selectedBlacklists;
          (updateFunnel as any).blacklistIds = selectedBlacklists;

          return updateFunnel;
        } else {
          return prev;
        }
      });
      handleCloseAddLocalBlacklistDialog();
      handleOpenSuccessAlert(t('localBlacklistCreatedSuccessMessage'));
    } else {
      setAddLocalBlacklistDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/blacklists', 'post');
    }
  };

  /**
   * Blacklist type handlers
   */

  const handleBlacklistTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddLocalBlacklistDialog((prev) => ({ ...prev, blacklistType: event.target.value as BlacklistTypes }));
    resetUploadValues();
    handleResetFileColumnSelection();
    handleResetBlacklistItems();
  };

  /**
   * Blacklist upload handlers
   */

  const handleBlacklistUploadVariantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddLocalBlacklistDialog((prev) => ({ ...prev, blacklistUploadVariant: event.target.value as BlacklistUploadVariant }));
    resetUploadValues();
    handleResetFileColumnSelection();
    handleResetBlacklistItems();
  };

  /**
   * Upload contact / companies handlers
   */

  const resetUploadValues = () => {
    setAddLocalBlacklistDialog((prev) => ({
      ...prev,
      individualItemsTextField: '',
      individualItemsTextFieldError: null,
      individualItemsList: [],
      companyQuery: '',
      companyOptions: [],
      selectedCompanies: [],
      companyIsEmpty: false,
    }));
  };

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddLocalBlacklistDialog((prev) => ({ ...prev, individualItemsTextField: event.target.value, individualItemsTextFieldError: null }));
  };

  const isLinkedinContactUrl = (url: string) => {
    return validator.isURL(url) && url.includes('linkedin');
  };

  const handleAddIndividualItem = () => {
    const { blacklistType, individualItemsTextField } = addLocalBlacklistDialog;

    if (blacklistType === BlacklistTypes.CONTACT && !isLinkedinContactUrl(individualItemsTextField)) {
      setAddLocalBlacklistDialog((prev) => ({ ...prev, individualItemsTextFieldError: true }));

      return;
    }

    setAddLocalBlacklistDialog((prev) => {
      const updateDialog = { ...prev };
      updateDialog.individualItemsList.push(individualItemsTextField);
      updateDialog.individualItemsTextField = '';
      updateDialog.individualItemsTextFieldError = null;

      return updateDialog;
    });
  };

  const handlePasteIndividualItem = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');

    setAddLocalBlacklistDialog((prev) => ({ ...prev, individualItemsTextField: pastedText }));

    const { blacklistType } = addLocalBlacklistDialog;

    if (blacklistType === BlacklistTypes.CONTACT && !isLinkedinContactUrl(pastedText)) {
      setAddLocalBlacklistDialog((prev) => ({ ...prev, individualItemsTextFieldError: true }));

      return;
    }

    setAddLocalBlacklistDialog((prev) => {
      const updateDialog = { ...prev };
      updateDialog.individualItemsList.push(pastedText);
      updateDialog.individualItemsTextField = '';
      updateDialog.individualItemsTextFieldError = null;

      return updateDialog;
    });
  };

  const handleRemoveIndividualItem = (idx: number) => {
    return () => {
      setAddLocalBlacklistDialog((prev) => {
        const updateDialog = { ...prev };
        updateDialog.individualItemsList.splice(idx, 1);

        return updateDialog;
      });
    };
  };

  const {
    handleBlacklistLinkedinAccountChange,
    handleBlacklistCompanyQueryChange,
    handleSearchBlacklistCompanies,
    handleToggleBlacklistCompany,
    handleRemoveBlacklistCompany,
  } = useBlacklistCompanyLookup({
    t,
    language: i18n.languages[0],
    setActiveTab,
    addLocalBlacklistDialog,
    setAddLocalBlacklistDialog,
    linkedinAccounts,
    setLinkedinAccounts,
    handleOpenErrorDialog,
  });

  const searchBlacklistCompanyKeyUp = () => {
    clearTimeout(typingTimer);
    if (addLocalBlacklistDialog.companyQuery.trim() !== '') {
      typingTimer = setTimeout(() => {
        handleSearchBlacklistCompanies();
      }, 1000);
    } else {
      setAddLocalBlacklistDialog((prev) => ({ ...prev, companyOptions: [] }));
    }
  };

  const searchBlacklistCompanyKeyDown = () => {
    clearTimeout(typingTimer);
  };

  /**
   * File upload handlers
   */

  const addLocalBlacklistOnFileDrop = useCallback((files: any[]) => {
    files.forEach((file: any) => {
      const reader = new FileReader();

      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');

      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        reader.onload = (e: any) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const parsedData: string[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (!_.isNil(parsedData)) {
            handleInitFileColumnSelection(parsedData, file.name);
          }
        };
        reader.readAsArrayBuffer(file);
      } else if (file.name.endsWith('.csv')) {
        reader.onload = (e: any) => {
          const text = e.target.result;
          const parsedData = parse(text, { delimiter: ';' });

          if (!_.isNil(parsedData.data)) {
            handleInitFileColumnSelection(parsedData.data as string[][], file.name);
          }
        };
        reader.readAsText(file);
      } else {
        console.log('Unsupported file type');
      }
    });
  }, []);

  const handleInitFileColumnSelection = (data: string[][], fileName: string) => {
    const columns = data[1];
    const newColumns = Array.from({ length: columns.length }, (_, index) => ({
      text: columns[index] || '',
      type: '',
    }));

    setAddLocalBlacklistDialog((prev) => ({
      ...prev,
      fileColumnSelection: {
        ...prev.fileColumnSelection,
        data: [...prev.fileColumnSelection.data, ...data],
        columns: newColumns,
        fileNames: [...prev.fileColumnSelection.fileNames, fileName],
      },
    }));
  };

  const addLocalBlacklistHandleColumnTypeChange = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addLocalBlacklistDialog };

      if (event.target.value === FileUploadMatchColumns.EMPTY) {
        updateDialog.fileColumnSelection.columns[idx].type = '';
      } else {
        const existingSameType = updateDialog.fileColumnSelection.columns.find((col) => col?.type === event.target.value);

        if (!_.isNil(existingSameType)) {
          existingSameType.type = '';
        }

        updateDialog.fileColumnSelection.columns[idx].type = event.target.value;
      }

      setAddLocalBlacklistDialog(updateDialog);
    };
  };

  const addLocalBlacklistHandleHasTableHeadingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { fileColumnSelection } = addLocalBlacklistDialog;
    const { data, columns } = fileColumnSelection;

    const columnStrings = data[event.target.checked ? 1 : 0];

    if (!columnStrings) return;

    let newColumns: { text: string; type: string }[] = [];

    columnStrings.forEach((col, index) => {
      const updateColumns = [...columns];
      updateColumns[index].text = col;
      newColumns = updateColumns;
    });

    setAddLocalBlacklistDialog((prev) => ({
      ...prev,
      fileColumnSelection: { ...prev.fileColumnSelection, columns: newColumns, hasTableHeading: event.target.checked },
    }));
  };

  const handleResetFileColumnSelection = () => {
    setAddLocalBlacklistDialog((prev) => ({
      ...prev,
      fileColumnSelection: { data: [], columns: [], fileNames: [], hasTableHeading: true },
    }));
  };

  const handleResetBlacklistItems = () => {
    setAddLocalBlacklistDialog((prev) => ({
      ...prev,
      fileItemsList: [],
    }));
  };

  const addLocalBlacklistHandleAcceptColumns = () => {
    const { fileColumnSelection } = addLocalBlacklistDialog;
    const { data, columns, hasTableHeading } = fileColumnSelection;

    const transformedData: {
      url?: string;
      company?: string;
    }[] = data.map((row: string[]) => {
      const urlIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.LINKEDIN_URL);
      const companyIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.COMPANY);

      let contactUrl: string | undefined = undefined;
      let contactCompany: string | undefined = undefined;

      if (!_.isNil(urlIndex) && urlIndex >= 0) {
        contactUrl = row[urlIndex];
      }

      if (!_.isNil(companyIndex) && companyIndex >= 0) {
        contactCompany = row[companyIndex];
      }

      return {
        url: contactUrl,
        company: contactCompany,
      };
    });

    const items = hasTableHeading ? _.tail(transformedData) : transformedData;
    const hasUrlColumn = items.some((contact) => !!contact.url);
    const hasCompanyColumn = items.some((contact) => !!contact.company);
    let filteredItems = items;

    if (hasUrlColumn) {
      filteredItems = items.filter((item) => item.url && isLinkedinContactUrl(item.url));
    }
    if (hasCompanyColumn) {
      filteredItems = items.filter((item) => item.company);
    }
    filteredItems = _.uniqBy(filteredItems, hasUrlColumn ? 'url' : 'company');

    setAddLocalBlacklistDialog((prev) => ({
      ...prev,
      fileItemsList: [...prev.fileItemsList, ...filteredItems],
    }));
    handleResetFileColumnSelection();
  };

  /**
   * Edit blacklist items dialog handlers
   */

  const handleOpenEditBlacklistItemsDialog = (blacklist: Blacklist_New) => {
    return () => {
      setAddLocalBlacklistDialog((prev) => ({
        ...prev,
        open: true,
        blacklist,
        blacklistType: blacklist.blacklistType,
        selectedLinkedinAccount: prev.selectedLinkedinAccount || campaign?.linkedinAccount || linkedinAccounts[0]?._id || '',
      }));
    };
  };

  const handleUpdateBlacklistItems = async () => {
    const { blacklist, blacklistType, blacklistUploadVariant, individualItemsList, fileItemsList, selectedCompanies } = addLocalBlacklistDialog;

    if (_.isNil(blacklist)) return;

    setAddLocalBlacklistDialog((prev) => ({ ...prev, loading: true }));

    const newBlacklistItems = buildBlacklistItemsFromDialog({
      blacklistType,
      blacklistUploadVariant,
      individualItemsList,
      fileItemsList,
      selectedCompanies,
    });

    const res = await updateBlacklistNew(blacklist._id, { items: newBlacklistItems, action: 'add' });

    if (!res.error) {
      await refreshBlacklistViews(false);
      handleCloseAddLocalBlacklistDialog();
      handleOpenSuccessAlert(t('blacklistUpdatedSuccessMessage'));
    } else {
      setAddLocalBlacklistDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/blacklists/:blacklistId', 'put');
    }
  };

  /**
   * Edit blacklist name dialog handlers
   */

  const handleOpenEditBlacklistNameDialog = (blacklist: Blacklist_New) => {
    return () => {
      setEditBlacklistNameDialog((prev) => ({ ...prev, open: true, blacklist, blacklistName: blacklist.name ?? '' }));
    };
  };

  const handleCloseEditBlacklistNameDialog = () => {
    setEditBlacklistNameDialog({
      open: false,
      loading: false,
      blacklist: null,
      blacklistName: '',
    });
  };

  const handleEditBlacklistNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditBlacklistNameDialog((prev) => ({ ...prev, blacklistName: event.target.value }));
  };

  const handleRenameBlacklist = async () => {
    const { blacklist, blacklistName } = editBlacklistNameDialog;

    if (_.isNil(blacklist)) return;

    setEditBlacklistNameDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateBlacklistNew(blacklist._id, { name: blacklistName });

    if (!res.error) {
      await refreshBlacklistViews(false);
      handleCloseEditBlacklistNameDialog();
      handleOpenSuccessAlert(t('blacklistUpdatedSuccessMessage'));
    } else {
      setEditBlacklistNameDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/blacklists/:blacklistId', 'put');
    }
  };

  /**
   * Delete blacklist dialog handlers
   */

  const handleOpenDeleteBlacklistDialog = (blacklist: Blacklist_New) => {
    return () => {
      setDeleteBlacklistDialog((prev) => ({ ...prev, open: true, blacklist }));
    };
  };

  const handleCloseDeleteBlacklistDialog = () => {
    setDeleteBlacklistDialog({ open: false, loading: false, blacklist: null });
  };

  const handleDeleteBlacklist = async () => {
    const { blacklist } = deleteBlacklistDialog;

    if (_.isNil(blacklist)) return;

    setDeleteBlacklistDialog((prev) => ({ ...prev, loading: true }));

    const res = await deleteBlacklist(blacklist._id);

    if (!res.error) {
      await refreshBlacklistViews(true);
      setCampaign((prev) => {
        if (!_.isNil(prev)) {
          const updateFunnel = { ...prev };
          const updateBlacklists: string[] = _.cloneDeep((updateFunnel as any).blacklists ?? (updateFunnel as any).blacklistIds ?? []);
          const filteredBlacklists = updateBlacklists.filter((blacklistId) => blacklistId !== blacklist._id);
          (updateFunnel as any).blacklists = filteredBlacklists;
          (updateFunnel as any).blacklistIds = filteredBlacklists;

          return updateFunnel;
        } else {
          return prev;
        }
      });
      handleCloseDeleteBlacklistDialog();
      handleOpenSuccessAlert(t('blacklistDeletedSuccessMessage'));
    } else {
      setDeleteBlacklistDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/blacklists/:blacklistId', 'delete');
    }
  };

  /**
   * Blacklist items preview drawer handlers
   */

  const handleRemoveSingleBlacklistItem = (blacklist: Blacklist_New) => {
    return (identifier: string) => {
      return async () => {
        const newBlacklistItems = _.cloneDeep(blacklist.items);
        const deletedItems = newBlacklistItems.filter((item) => item.companyName == identifier || item.contactUrl === identifier);

        setRemoveBlacklistItemsLoading(true);

        const res = await updateBlacklistNew(blacklist._id, { items: deletedItems, action: 'remove' });

        if (!res.error) {
          await refreshBlacklistViews(false);
          handleOpenSuccessAlert(t('blacklistUpdatedSuccessMessage'));
        } else {
          handleOpenErrorDialog(res?.error, '/blacklists/:blacklistId', 'put');
        }

        setRemoveBlacklistItemsLoading(false);
      };
    };
  };

  /**
   * Blacklist selection handlers
   */

  const handleSelectedBlacklistsChange = (blacklist: Blacklist_New) => {
    return () => {
      if (_.isNil(campaign)) return;

      const blacklists: string[] = _.cloneDeep((campaign as any).blacklistIds ?? (campaign as any).blacklists ?? []);

      const updateSelected: string[] = _.cloneDeep(blacklists);

      if (!_.includes(blacklists, blacklist._id)) {
        updateSelected.push(blacklist._id);

        handleUpdateCampaign({ blacklistIds: updateSelected } as any);
        handleOpenSuccessAlert(t('blacklistAddedSuccessMessage'));
      } else {
        handleUpdateCampaign({ blacklistIds: updateSelected.filter((id: string) => id !== blacklist._id) } as any);
        handleOpenSuccessAlert(t('blacklistRemovedSuccessMessage'));
      }
    };
  };

  /**
   * Contacts handlers
   */

  const handleActiveQualificationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    setActiveQualifications(newValue as Qualifications[]);
  };

  const handleContactFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContactFilter(event.target.value as ContactFilter);
    setDeleteMultipleContacts(false);
    setContactsToDelete([]);
  };

  const applySidebarFilter = (filter: ContactFilter) => {
    return () => {
      setContactFilter(filter);
    };
  };

  const handleChangeContactsPage = (event: any, value: number) => {
    setContactsPage(value);
  };

  const handleContactsSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContactsSearchParam(event.target.value);
  };

  const handleClearContactsSearchField = () => {
    setContactsSearchParam('');
  };

  function resetContactsPage() {
    setContactsPage(1);
  }

  /**
   * Final pipeline handlers
   */

  const handleChangeFinalContactsPage = (event: any, value: number) => {
    setFinalContactsPage(value);
  };

  const handleFinalContactsSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFinalContactsSearchParam(event.target.value);
  };

  const handleClearFinalContactsSearchField = () => {
    setFinalContactsSearchParam('');
  };

  function resetFinalContactsPage() {
    setFinalContactsPage(1);
  }

  const exportCampaignContacts = async () => {
    if (_.isNil(campaign)) return;

    const { name } = campaign;

    const params = {
      filter: ContactFilter.IS_ACTIVE,
    };

    setExportFinalContactsLoading(true);

    const res = await getAllFunnelContacts(campaign._id, params);

    if (!res.error) {
      const allFinalContacts: FunnelContact[] = res.funnelContacts || [];
      const data = allFinalContacts.map((contact) => ({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        personalityType: contact.assessment || '',
        currentCompanies: contact.currentCompanies?.map((company) => (typeof company === 'string' ? company : company.companyName)).join(', ') || '',
        occupation: contact.occupation || '',
        location: contact.location || '',
        email: contact.email || '',
        linkedInURL: contact.linkedInURL || '',
        customField: contact.customField || '',
        humanreadableLinkedInURL: contact.profileIds?.vanity ? `https://www.linkedin.com/in/${contact.profileIds.vanity}` : '',
        phoneNumber: contact.phoneNumber || '',
      }));

      const worksheetData = [
        [
          t('firstName'),
          t('lastName'),
          t('personalityType'),
          t('company'),
          t('position'),
          t('location'),
          t('email'),
          t('linkedinUrl'),
          t('customField'),
          'Human Readable LinkedIn',
          t('phoneNumber'),
        ],
        ...data.map((item) => [
          item.firstName,
          item.lastName,
          item.personalityType,
          item.currentCompanies,
          item.occupation,
          item.location,
          item.email,
          item.linkedInURL,
          item.customField,
          item.humanreadableLinkedInURL,
          item.phoneNumber,
        ]),
      ];

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Campaign Contacts');

      const date = new Date();
      const formattedDateDE = `${date.getDate()} ${date.getMonth() + 1} ${date.getFullYear()}`;
      const formattedDateEN = `${date.getMonth() + 1} ${date.getDate()} ${date.getFullYear()}`;
      const fileDate = i18n.languages[0] === 'de' || i18n.languages[0]?.includes('de') ? formattedDateDE : formattedDateEN;

      const fileContent = `${fileDate} ${name} ${t('contacts')}`;
      const fileName = `${_.snakeCase(fileContent)}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } else {
      handleOpenErrorDialog(res?.error, '/campaigns/:campaignId/contacts', 'get');
    }

    setExportFinalContactsLoading(false);
  };

  /**
   * Enrich contacts dialog handlers
   */
  const handleOpenEnrichContactsDialog = () => {
    console.log('[EnrichContactsDialog] Opening enrichment dialog');
    setEnrichContactsDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseEnrichContactsDialog = () => {
    console.log('[EnrichContactsDialog] Closing enrichment dialog and resetting state');
    setEnrichContactsDialog({
      open: false,
      loading: false,
      apiKey: '',
      variant: EnrichmentVariants.SHORT_LIST,
      prospeosEnrichEmail: false,
      prospeosEnrichPhone: false,
      aiResearchFullFunnel: false,
      customFieldPersonPrompt: '',
      customFieldCompanyPrompt: '',
      customFieldOverwriteExisting: false,
    });
  };

  const handleApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[EnrichContactsDialog] API key changed:', event.target.value);
    setEnrichContactsDialog((prev) => ({ ...prev, apiKey: event.target.value }));
  };

  const handleVariantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[EnrichContactsDialog] Variant changed:', event.target.value);
    setEnrichContactsDialog((prev) => ({
      ...prev,
      variant: event.target.value as EnrichmentVariants,
    }));
  };

  const handleProspeosEnrichEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[EnrichContactsDialog] Prospeos enrich email changed:', event.target.checked);
    setEnrichContactsDialog((prev) => ({ ...prev, prospeosEnrichEmail: event.target.checked }));
  };

  const handleProspeosEnrichPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[EnrichContactsDialog] Prospeos enrich phone changed:', event.target.checked);
    setEnrichContactsDialog((prev) => ({ ...prev, prospeosEnrichPhone: event.target.checked }));
  };

  const handleAiResearchFullFunnelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[EnrichContactsDialog] AI Research full funnel changed:', event.target.checked);
    setEnrichContactsDialog((prev) => ({ ...prev, aiResearchFullFunnel: event.target.checked }));
  };

  const handleCustomFieldPersonPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[EnrichContactsDialog] Custom field person prompt changed');
    setEnrichContactsDialog((prev) => ({ ...prev, customFieldPersonPrompt: event.target.value }));
  };

  const handleCustomFieldCompanyPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[EnrichContactsDialog] Custom field company prompt changed');
    setEnrichContactsDialog((prev) => ({ ...prev, customFieldCompanyPrompt: event.target.value }));
  };

  const handleCustomFieldOverwriteExistingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[EnrichContactsDialog] Custom field overwrite changed:', event.target.checked);
    setEnrichContactsDialog((prev) => ({ ...prev, customFieldOverwriteExisting: event.target.checked }));
  };

  const handleStartProspeosEnrichment = async () => {
    if (_.isNil(campaign)) return;
    const { prospeosEnrichEmail, prospeosEnrichPhone } = enrichContactsDialog;
    console.log('[EnrichContactsDialog] Starting Prospeos enrichment with values:', {
      prospeosEnrichEmail,
      prospeosEnrichPhone,
    });
    handleCloseEnrichContactsDialog();
    handleOpenEnrichmentLoadingDialog();
    const response = await updateFunnelContactsWithProspeos(campaign._id, prospeosEnrichEmail, prospeosEnrichPhone);
    console.log('[EnrichContactsDialog] Prospeos enrichment response:', response);
    if (response.success) {
      setEnrichContactsSnackbar({
        open: true,
        message: t('prospeosEnrichmentInitiatedSuccessfully'),
        severity: 'success',
      });
      fetchFinalContacts();
    } else {
      setEnrichContactsSnackbar({
        open: true,
        message: response.message || t('prospeosEnrichmentFailed'),
        severity: 'error',
      });
    }
    handleCloseEnrichmentLoadingDialog();
  };

  /**
   * Enrichment loading dialog handlers
   */
  const handleOpenEnrichmentLoadingDialog = () => {
    console.log('[EnrichContactsDialog] Opening enrichment loading dialog');
    setEnrichmentLoadingDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseEnrichmentLoadingDialog = () => {
    console.log('[EnrichContactsDialog] Closing enrichment loading dialog');
    setEnrichmentLoadingDialog({ open: false });
  };

  const handleCloseEnrichContactsSnackbar = () => {
    console.log('[EnrichContactsDialog] Closing enrichment snackbar');
    setEnrichContactsSnackbar({ ...enrichContactsSnackbar, open: false });
  };

  /**
   * Campaign settings
   */

  const handleMessageApprovalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateCampaign({
      settings: {
        ...(campaign?.settings || {}),
        approveMessages: event.target.value === 'true' ? true : false,
      },
    });
  };

  /**
   * Cooldown before someone already approached in another campaign may be contacted
   * again. `0` from the select means "never again", which is the platform default and is
   * stored as 0 rather than left undefined so an explicit choice is distinguishable from
   * a campaign created before this setting existed.
   */
  const handleRecontactCooldownChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const days = Number(event.target.value);
    handleUpdateCampaign({ recontactCooldownDays: Number.isFinite(days) && days > 0 ? days : 0 });
  };

  const isLinkedinAccountMissing = () => {
    // Don't show error while LinkedIn account is still loading
    if (linkedinAccountLoading) {
      return false;
    }

    const linkedinAccount = campaign?.linkedinAccount;
    const linkedinAccountEmpty = _.isNil(linkedinAccount) || linkedinAccount === '';
    const linkedinAccountDeleted = !linkedinAccountEmpty && !linkedinAccounts.some((account) => account._id === linkedinAccount);

    return linkedinAccountEmpty || linkedinAccountDeleted;
  };

  const handleSelectedLinkedinUserChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateCampaign({ linkedinAccount: event.target.value });
    stopCheckAccountStatusInterval();
  };

  /**
   * Flow creation dialog handlers
   */

  const handleOpenFlowCreationDialog = () => {
    setFlowCreationDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseFlowCreationDialog = () => {
    setFlowCreationDialog({ open: false, loading: false, activeTab: FlowCreationTabs.FROM_TEMPLATE });
  };

  const handleSelectActiveFlowCreationTab = (newActive: FlowCreationTabs) => {
    return () => {
      const { activeTab } = flowCreationDialog;

      if (newActive !== activeTab) {
        setFlowCreationDialog((prev) => ({ ...prev, activeTab: newActive }));
      }
    };
  };

  const handleApplyTemplate = (templateKey: string) => {
    return async () => {
      if (_.isNil(campaign)) return;

      const { type } = campaign;

      setFlowCreationDialog((prev) => ({ ...prev, loading: true }));

      const res = await applyFunnelTemplate(campaign._id, templateKey, type);

      if (!res.error) {
        setCampaign(res);

        const { viewport } = res.reactFlow;
        const { x = 0, y = 0, zoom = 1 } = viewport;
        setViewport({ x, y, zoom });

        setNodes(res.reactFlow.nodes);
        setEdges(res.reactFlow.edges);

        handleCloseFlowCreationDialog();

        setOpenTemplateSuccessDialog(true);
      } else {
        setFlowCreationDialog((prev) => ({ ...prev, loading: false }));
        handleOpenErrorDialog(res?.error, '/campaigns/:campaignId/apply-template', 'put');
      }
    };
  };

  /**
   * Flow-Builder (Schritt 3): the builder already saved steps, canvas and settings on the
   * server; here the page state and the canvas follow, exactly like after applying a template.
   */
  const handleFlowBuilderApplied = (updated: Funnel) => {
    if (_.isNil(updated)) return;

    setCampaign(updated);

    const reactFlow = updated.reactFlow;
    if (!_.isNil(reactFlow)) {
      const { x = 0, y = 0, zoom = 1 } = reactFlow.viewport ?? {};
      setViewport({ x, y, zoom });
      setNodes(reactFlow.nodes || []);
      setEdges(reactFlow.edges || []);
    }
  };

  const handleApplyEmptyTemplate = async () => {
    if (_.isNil(campaign)) return;

    setFlowCreationDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateFunnel(campaign._id, { template: 'none' });

    if (!res.error) {
      const { viewport } = res.reactFlow;
      const { x = 0, y = 0, zoom = 1 } = viewport;
      setViewport({ x, y, zoom });

      setNodes([]);
      setEdges([]);

      handleCloseFlowCreationDialog();
    } else {
      setFlowCreationDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:campaignId', 'patch');
    }
  };

  const handleSelectedFunnelFunctionsLibraryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateCampaign({ funnelFunctionsLibrary: event.target.value });

    if (_.isNil(campaign)) return;

    initCampaignFunctions(campaign.type, event.target.value);
  };

  const handleSelectNodeFromList = (node: Node) => {
    return () => {
      setSelectedNode(node);
    };
  };

  const handleUnselectNodeFromList = () => {
    setSelectedNode(null);
  };

  /**
   * Change communication style warning dialog handlers
   */

  const handleOpenChangeCommunicationStyleWarningDialog = () => {
    setChangeCommunicationStyleWarningDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseChangeCommunicationStyleWarningDialog = () => {
    setChangeCommunicationStyleWarningDialog({ open: false, loading: false });
  };

  const handleChangeCommunicationStyle = () => {
    handleUpdateCampaign({
      settings: {
        ...(campaign?.settings || {}),
        formalSpeech: !(campaign?.settings?.formalSpeech ?? false),
      },
    });
    handleCloseChangeCommunicationStyleWarningDialog();
  };

  /**
   * Message settings handlers
   */

  const handleIncludeEmojiChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateCampaign({
      settings: {
        ...(campaign?.settings || {}),
        includeEmoji: event.target.checked,
      },
    });
  };

  const handleSwissSChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateCampaign({
      settings: {
        ...(campaign?.settings || {}),
        swissS: event.target.checked,
      },
    });
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateCampaign({
      settings: {
        ...(campaign?.settings || {}),
        language: event.target.value === 'auto-detect' ? 'auto' : (event.target.value as 'de' | 'en' | 'auto'),
      },
    });
  };

  const handleSecondaryLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateCampaign({
      settings: {
        ...(campaign?.settings || {}),
        secondaryLanguage: event.target.value as 'de' | 'en' | 'none',
      },
    });
  };

  /**
   * Campaign autoReply handlers
   */

  /** Toggles isActive in local state */
  const onIsActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (_.isNil(campaign)) return;

    setCampaign((prev) => ({ ...prev!, autoReply: { ...prev!.autoReply!, isActive: event.target.checked } }));
  };

  /** Updates goalOfCommunication in local state */
  const onGoalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (_.isNil(campaign)) return;

    setCampaign((prev) => ({ ...prev!, autoReply: { ...prev!.autoReply!, goalofCommunication: event.target.value } }));
  };

  /** Updates the entire caseHandling array in local state */
  const onCaseHandlingUpdate = (newCaseHandling: AutoReplyCase[]) => {
    if (_.isNil(campaign)) return;

    setCampaign((prev) => ({ ...prev!, autoReply: { ...prev!.autoReply!, caseHandling: newCaseHandling } }));
  };

  const handleUpdateAutoReply = () => {
    if (_.isNil(campaign)) return;

    const { autoReply } = campaign;

    handleUpdateCampaign({ autoReply });
  };

  /**
   * Campaign testing handlers
   */

  const handleSelectedTestContactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (!_.includes(campaignTesting.selectedTestContacts, value)) {
      setCampaignTesting((prev) => {
        const updateCampaignTesting = _.cloneDeep(prev);
        const updateSelectedContacts = [...updateCampaignTesting.selectedTestContacts];

        updateSelectedContacts.push(value);
        updateCampaignTesting.selectedTestContacts = updateSelectedContacts;

        return updateCampaignTesting;
      });
    } else {
      setCampaignTesting((prev) => ({ ...prev, selectedTestContacts: prev.selectedTestContacts.filter((selectedUrl) => selectedUrl !== value) }));
    }
  };

  const handleNextContact = () => {
    setCampaignTesting((prev) => ({
      ...prev,
      activeTestContactIndex: prev.activeTestContactIndex === prev.testContacts.length - 1 ? 0 : prev.activeTestContactIndex + 1,
    }));
  };

  const handlePrevContact = () => {
    setCampaignTesting((prev) => ({
      ...prev,
      activeTestContactIndex: prev.activeTestContactIndex === 0 ? prev.testContacts.length - 1 : prev.activeTestContactIndex - 1,
    }));
  };

  const handleNextMessage = () => {
    const { testContacts, activeTestContactIndex } = campaignTesting;
    const contact = testContacts[activeTestContactIndex];
    const messages = contact.messages;

    const newIndex = campaignTesting.activeMessageIndex === messages.length - 1 ? 0 : campaignTesting.activeMessageIndex + 1;
    const newMessageStepIndex = messages[newIndex]?.stepIndex ?? undefined;

    setCampaignTesting((prev) => ({
      ...prev,
      activeMessageIndex: newIndex,
    }));

    const nodeId = getNodeIdByStepIndex(edges, nodes, newMessageStepIndex);

    if (!_.isNil(nodeId)) {
      const foundNode = nodes.find((node) => node.id === nodeId);

      if (!_.isNil(foundNode)) {
        setSelectedNode(foundNode);
      }
    }
  };

  const handlePrevMessage = () => {
    const { testContacts, activeTestContactIndex } = campaignTesting;
    const contact = testContacts[activeTestContactIndex];
    const messages = contact.messages;

    const newIndex = campaignTesting.activeMessageIndex === 0 ? messages.length - 1 : campaignTesting.activeMessageIndex - 1;
    const newMessageStepIndex = messages[newIndex]?.stepIndex ?? undefined;

    setCampaignTesting((prev) => ({
      ...prev,
      activeMessageIndex: newIndex,
    }));

    const nodeId = getNodeIdByStepIndex(edges, nodes, newMessageStepIndex);

    if (!_.isNil(nodeId)) {
      const foundNode = nodes.find((node) => node.id === nodeId);

      if (!_.isNil(foundNode)) {
        setSelectedNode(foundNode);
      }
    }
  };

  /**
   * Unable to generate contact request dialog handlers
   */

  const handleOpenUnableToGenerateMessageDialog = () => {
    setUnableToGenerateMessageDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseUnableToGenerateMessageDialog = () => {
    setUnableToGenerateMessageDialog({ open: false });
  };

  /**
   * Start campaign handlers
   */

  const resetAiBriefing = () => {
    setPerplexityUrl('');
    setPerplexityLoading(false);
    setPerplexityStatusMessage('');
    setOffer({
      jobTitle: '',
      jobOffer: '',
      problems: '',
      features: '',
      benefits: '',
      impact: '',
    });
  };

  const resetPaiContactSearch = () => {
    setLinkedinProduct('');
    setPaiSearchLanguage(['default', 'en']);
    setIncludeOwnContacts(false);
    setLocationSettings({
      regularLocationConstraints: { query: '', options: [], selected: [], isEmpty: false, loading: false },
      recruiterLocationConstraints: {
        geo: { query: '', options: [], selected: [], isEmpty: false, loading: false },
        zip: { query: '', options: [], selected: [], isEmpty: false, loading: false },
        area: '25',
      },
    });
    setSeparateLinkedinAccount('');
    setFullSeparateLinkedinAccount(undefined);
    handleResetLinkedinSearch();
  };

  const resetOwnContactSearch = () => {
    setSelectedContactSearchObjective(null);
    setSearchToAdd(null);

    setSeparateLinkedinAccount('');
    setFullSeparateLinkedinAccount(undefined);
    setAddSearchesLoading(false);
  };

  const resetContactUpload = () => {
    setContactsToAdd([]);
    setFileName('');
    setHasLinkedinUrls(true);
    setAddContactsLoading(false);
  };

  const resetAddContacts = () => {
    setContactSearchMethod('');
    resetPaiContactSearch();
    resetOwnContactSearch();
    resetContactUpload();
  };

  const resetManageSearches = () => {};

  const resetManageFinalContacts = () => {
    setFinalContactsPage(1);
    setFinalContactsSearchParam('');
  };

  const resetAiContactQualification = () => {
    setContactQualification({
      loading: false,
      promptPlaceholders: {
        required: '',
        disqualifying: '',
        preferred: '',
        additionalContactInfo: '',
        companyCharacteristics: '',
        companyChallenges: '',
        organizationalWorkingCultureValues: '',
      },
      selectedOptions: ['A', 'B'],
      rejudgeExistingContacts: false,
      prompt: '',
      hasOldKeys: false,
      isJudging: false,
    });
  };

  const resetContactOverview = () => {
    setContactsPage(1);
    setContactsSearchParam('');
    setContactFilter(ContactFilter.ALL);
    setActiveQualifications([Qualifications.A, Qualifications.B, Qualifications.C, Qualifications.NOT_JUDGED]);
    setDeleteMultipleContacts(false);
    setContactsToDelete([]);
  };

  const resetManageBlacklists = () => {
    setActiveBlacklistScope(BlacklistScopes.GLOBAL);
    setGlobalBlacklists([]);
    setLocalBlacklists([]);
    setRemoveBlacklistItemsLoading(false);
  };

  const resetCampaignPipeline = () => {
    setActiveCampaignPipelineStep(CampaignPipelineSteps.ADD_CONTACTS);
    resetAddContacts();
    resetAiContactQualification();
    resetManageSearches();
    resetManageBlacklists();
    resetContactOverview();
    resetManageFinalContacts();
  };

  const resetCampaignFlow = () => {
    setTemplates([]);
    setSelectedNode(null);
  };

  const resetCampaignTesting = () => {
    setCampaignTesting({
      selectedTestContacts: [],
      loading: false,
      testContacts: [],
      activeTestContactIndex: 0,
      activeMessageIndex: 0,
    });
  };

  const resetCampaignBuilder = () => {
    // reset campaign state & query param '_id'
    handleResetCampaign();
    // general resets
    setExpanded(true);
    resetPromptLibraries();
    // reset individual steps
    resetAiBriefing();
    resetCampaignPipeline();
    resetCampaignFlow();
    resetCampaignTesting();
  };

  const finishOnboarding = async () => {
    if (onboardingDone) return;

    const payload = {
      onboardingDone: true,
    };

    const res = await changeUserInfo(payload);

    if (!res.error) {
      const userDetails = localStorage.getItem('user-details');

      if (!_.isNil(userDetails)) {
        const parsedDetails = JSON.parse(userDetails);
        parsedDetails.onboardingDone = true;
        setDetails(parsedDetails);
      }
    }
  };

  const {
    handleOpenStartCampaignSuccessDialog,
    handleCloseStartCampaignSuccessDialog,
    handleOpenStartMessageApprovalCampaignSuccessDialog,
    handleCloseAndProceedStartMessageApprovalCampaignSuccessDialog,
    handleOpenStartImmediatelyDialog,
    handleCloseStartImmediatelyDialog,
    handleStartCampaignImmediately,
    handleCloseStartImmediatelySuccessDialog,
  } = useStartCampaignSuccessFlow({
    campaign,
    fullLinkedinAccount,
    setActiveTab,
    pathname: location.pathname,
    navigate,
    resetCampaignBuilder,
    setStartCampaignSuccessDialog,
    setStartMessageApprovalCampaignSuccessDialog,
    setStartImmediatelyDialog,
    setStartImmediatelySuccessDialog,
    handleOpenErrorDialog,
  });

  const handleOpenStartingCampaignNotPossibleDialog = (issues: string[]) => {
    setStartingCampaignNotPossibleDialog((prev) => ({ ...prev, open: true, issues }));
  };

  const handleCloseStartingCampaignNotPossibleDialog = () => {
    setStartingCampaignNotPossibleDialog({ open: false, issues: [] });
  };

  /**
   * Flow incomplete dialog
   */

  const handleOpenFlowIncompleteDialog = () => {
    setFlowIncompleteDialog({ open: true });
  };

  const handleCloseFlowIncompleteDialog = () => {
    setFlowIncompleteDialog({ open: false });
    selectCampaignStep(campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_FLOW))();
  };

  const handleOpenFlowEmptyDialog = () => {
    setFlowEmptyDialog({ open: true });
  };

  const handleCloseFlowEmptyDialog = () => {
    setFlowEmptyDialog({ open: false });
  };

  const { handleTestCampaign } = useCampaignTestFlow({
    campaign,
    campaignTesting,
    setCampaignTesting,
    finalContacts,
    flowHasError,
    handleOpenFlowIncompleteDialog,
    handleOpenFlowEmptyDialog,
    nodes,
    edges,
    setSelectedNode,
    startCheckAccountStatusInterval,
    handleOpenUnableToGenerateMessageDialog,
    handleOpenErrorDialog,
  });

  /**
   * Copilot license expired dialog
   */

  function handleOpenCopilotLicenseExpiredDialog() {
    setCopilotLicenseExpiredDialog({ open: true });
  }

  const handleCloseCopilotLicenseExpiredDialog = () => {
    setCopilotLicenseExpiredDialog({ open: false });
  };

  /**
   * Credits exhausted dialog
   */

  const handleOpenCreditsExhaustedDialog = () => {
    setCreditsExhaustedDialog({ open: true });
  };

  const handleCloseCreditsExhaustedDialog = () => {
    setCreditsExhaustedDialog({ open: false });
  };

  /**
   *  Linkedin account not found dialog handlers
   */

  const handleOpenLinkedinAccountNotFoundDialog = () => {
    setLinkedinAccountNotFoundDialog({ open: true });
  };

  const handleCloseLinkedinAccountNotFoundDialog = () => {
    const campaignStepsArray = _.map(CampaignBuilderSteps, (tab) => tab);
    setActiveCampaignStepIndex(campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SETTINGS));
    setCampaign((prev) => ({ ...prev!, linkedinAccount: null }));
    setLinkedinAccountNotFoundDialog({ open: false });
  };

  const { isStartCampaignPossible } = useCampaignStartValidation(campaign, t);

  const { handleStartCampaign } = useCampaignStartFlow({
    campaign,
    linkedinAccounts,
    flowHasError,
    isStartCampaignPossible,
    setCampaign,
    setStartCampaignLoading,
    handleOpenFlowIncompleteDialog,
    handleOpenFlowEmptyDialog,
    handleOpenStartingCampaignNotPossibleDialog,
    handleOpenStartCampaignSuccessDialog,
    handleOpenStartMessageApprovalCampaignSuccessDialog,
    handleOpenLinkedinAccountNotFoundDialog,
    handleOpenCreditsExhaustedDialog,
    handleOpenCopilotLicenseExpiredDialog,
    handleOpenErrorDialog,
    finishOnboarding,
  });

  /**
   * Faq dialog handlers
   */

  const handleOpenFaqDialog = () => {
    setFaqDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseFaqDialog = () => {
    setFaqDialog({ open: false, activeFaq: FaqItemKey.SYNCHRONIZING_YOUR_ACCOUNT });
  };

  const handleSetActiveFaq = (faq: FaqItemKey) => {
    return () => {
      setFaqDialog((prev) => ({ ...prev, activeFaq: faq }));
    };
  };

  // Provide noop update handler while on overview step to prevent unintended PATCH requests and the resulting "saving" UI state.
  const handleUpdateCampaignReactFlowSafe: typeof handleUpdateCampaignReactFlow = useCallback(
    async (flow, signal) => {
      if (isOverviewStepRef.current) {
        // Skip saves while on overview step to avoid unintended PATCH requests
        return;
      }

      await handleUpdateCampaignReactFlow(flow, signal);
    },
    [handleUpdateCampaignReactFlow]
  );

  /**
   * Enrichment dialog handlers
   */

  // AI Research Agent enrichment (free web-search)
  const handleStartAiResearchEnrichment = async () => {
    if (_.isNil(campaign)) return;

    const { aiResearchFullFunnel } = enrichContactsDialog;
    console.log('[EnrichContactsDialog] Starting AI Research enrichment. Full funnel:', aiResearchFullFunnel);

    handleCloseEnrichContactsDialog();
    handleOpenEnrichmentLoadingDialog();

    const response = await enrichContactsLeadList(campaign._id, aiResearchFullFunnel);
    console.log('[EnrichContactsDialog] AI Research enrichment response:', response);

    if (response.success) {
      setEnrichContactsSnackbar({
        open: true,
        message: t('aiResearchEnrichmentInitiatedSuccessfully'),
        severity: 'success',
      });
      fetchFinalContacts();
    } else {
      setEnrichContactsSnackbar({
        open: true,
        message: response.message || t('aiResearchEnrichmentFailed'),
        severity: 'error',
      });
    }

    handleCloseEnrichmentLoadingDialog();
  };

  const handleStartCustomFieldEnrichment = async () => {
    if (_.isNil(campaign)) return;

    const { customFieldPersonPrompt, customFieldCompanyPrompt, customFieldOverwriteExisting } = enrichContactsDialog;
    console.log('[EnrichContactsDialog] Starting custom field enrichment');

    handleCloseEnrichContactsDialog();
    handleOpenEnrichmentLoadingDialog();

    const response = await updateCustomFieldEnrichment(
      campaign._id,
      customFieldPersonPrompt,
      customFieldCompanyPrompt,
      customFieldOverwriteExisting
    );
    console.log('[EnrichContactsDialog] Custom field enrichment response:', response);

    if (response.success) {
      setEnrichContactsSnackbar({
        open: true,
        message: t('customFieldEnrichmentInitiatedSuccessfully'),
        severity: 'success',
      });
      fetchFinalContacts();
      fetchContacts();
    } else {
      setEnrichContactsSnackbar({
        open: true,
        message: response.message || t('customFieldEnrichmentFailed'),
        severity: 'error',
      });
    }

    handleCloseEnrichmentLoadingDialog();
  };

  // Unified start button handler – decides which enrichment to execute
  const handleStartEnrichment = async (activeTab: number) => {
    const { aiResearchFullFunnel, prospeosEnrichEmail, prospeosEnrichPhone } = enrichContactsDialog;

    if (activeTab === 1) {
      await handleStartCustomFieldEnrichment();
      return;
    }

    if (aiResearchFullFunnel) {
      await handleStartAiResearchEnrichment();
      return;
    }

    if (prospeosEnrichEmail || prospeosEnrichPhone) {
      await handleStartProspeosEnrichment();
      return;
    }
  };

  /**
   * Uncertain gender handlers
   */

  const [updateSexDialog, setUpdateSexDialog] = useState<{
    open: boolean;
    loading: boolean;
    contact: FunnelContact | null;
    selectedSex: 'male' | 'female';
  }>({
    open: false,
    loading: false,
    contact: null,
    selectedSex: 'female',
  });

  const handleOpenUpdateSexDialog = (contact: FunnelContact) => {
    return () => {
      setUpdateSexDialog((prev) => ({ ...prev, open: true, contact }));
    };
  };

  const handleCloseUpdateSexDialog = () => {
    setUpdateSexDialog({ open: false, loading: false, contact: null, selectedSex: 'female' });
  };

  const handleSexChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUpdateSexDialog((prev) => ({ ...prev, selectedSex: event.target.value as 'male' | 'female' }));
  };

  const handleUpdateSex = async () => {
    if (_.isNil(campaign)) return;

    const { contact, selectedSex } = updateSexDialog;

    if (_.isNil(contact)) return;
    const contactId = [contact.contactId, contact._id]
      .map((value) => (typeof value === 'string' ? value.trim() : value ? String(value).trim() : ''))
      .find(Boolean);
    if (!contactId) {
      handleOpenErrorDialog({ code: 'MISSING_CONTACT_ID', message: 'Missing contact id for selected contact' }, '/contacts/:contactId', 'patch');
      return;
    }

    setUpdateSexDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateContactSex(campaign._id, contactId, selectedSex);

    if (!res.error) {
      handleCloseUpdateSexDialog();
      fetchContacts();
      handleOpenSuccessAlert(t('sexUpdatedSuccessMessage'));
    } else {
      setUpdateSexDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, `/contacts/${contactId}`, 'patch');
    }
  };

  const { stopCampaignLoading, abortLoading, abortRequested, handleStopCampaign, handleAbortProcessing } = useCampaignStopFlow({
    campaign,
    profileAnalysisProcessing,
    isProcessing,
    setCampaign,
    handleOpenCopilotLicenseExpiredDialog,
    handleOpenErrorDialog,
  });

  /**
   * Delete multiple contacts
   */

  const toggleDeleteMultipleContacts = () => {
    setDeleteMultipleContacts((prev) => !prev);
    setContactsToDelete([]);
  };

  const handleMultipleContactsToDeleteChange = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();

    setContactsToDelete((prev) => {
      if (prev.length === contacts.length) {
        return [];
      }
      return [...contacts];
    });
  };

  const handleContactsToDeleteChange = (contact: FunnelContact) => {
    return (event: React.MouseEvent<HTMLInputElement>) => {
      event.stopPropagation();
      event.preventDefault();

      setContactsToDelete((prev) => {
        if (prev.includes(contact)) {
          return prev.filter((c) => c !== contact);
        }
        return [...prev, contact];
      });
    };
  };

  const handleOpenDeleteMultipleContactsPermanentlyDialog = () => {
    setDeleteMultipleContactsPermanentlyDialog((prev) => ({
      ...prev,
      open: true,
      contacts: contactsToDelete,
      contactCount: contactsToDelete.length,
    }));
  };

  const handleCloseDeleteMultipleContactsPermanentlyDialog = () => {
    setDeleteMultipleContactsPermanentlyDialog({ open: false, loading: false, contacts: [], contactCount: 0 });
  };

  const handleDeleteMultipleContactsPermanently = async () => {
    if (_.isNil(campaign)) return;

    const { contacts: contactsToDelete } = deleteMultipleContactsPermanentlyDialog;

    if (_.isEmpty(contactsToDelete)) return;

    setDeleteMultipleContactsPermanentlyDialog((prev) => ({ ...prev, loading: true }));

    const linkedInURLs = contactsToDelete.map((contact) => contact.linkedInURL);

    const res = await updateContactIsDeleted(campaign._id, linkedInURLs, true);

    if (!res.error) {
      setContactsToDelete([]);
      setDeleteMultipleContacts(false);
      fetchContacts();
      handleCloseDeleteMultipleContactsPermanentlyDialog();
      handleOpenSuccessAlert(t('contactsDeletedSuccessMessage', { count: contacts.length }));

      if (contacts.length === contactsToDelete.length) {
        setContactsPage((prev) => (prev === 1 ? 1 : prev - 1));
      }
    } else {
      setDeleteMultipleContactsPermanentlyDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/campaigns/:id/contacts', 'put');
    }
  };

  /**
   * Success alert handlers
   */

  const handleOpenSuccessAlert = (message: string) => {
    setSuccessAlert({ open: true, message: message });
  };

  const handleCloseSuccessAlert = () => {
    setSuccessAlert({ open: false, message: '' });
  };

  return (
    <>
      {activeTab === NavigationStates.TOOL ? (
        _.isNil(campaign) ? (
          <CreateCampaign
            campaignLoading={campaignLoading}
            handleOpenCreateCampaignDialog={handleOpenCreateCampaignDialog}
            disableCreate={false}
          />
        ) : (
          <>
            <CampaignBuilder
                productGroup={productGroup}
                campaign={campaign}
                hasJudgingSettings={hasJudgingSettings}
                fullLinkedinAccount={fullLinkedinAccount}
                linkedinAccountLoading={linkedinAccountLoading}
                linkedinAccountUpdating={linkedinAccountUpdating}
                fullSeparateLinkedinAccount={fullSeparateLinkedinAccount}
                updateCampaignLoading={updateCampaignLoading}
                autoSyncLoading={autoSyncLoading}
                autoSyncProcessingDialogOpen={autoSyncProcessingDialog.open}
                handleOpenAutoSyncProcessingDialog={handleOpenAutoSyncProcessingDialog}
                expanded={expanded}
                activeCampaignStepIndex={activeCampaignStepIndex}
                perplexityUrl={perplexityUrl}
                perplexityLoading={perplexityLoading}
                perplexityError={perplexityError}
                perplexityStatusMessage={perplexityStatusMessage}
                offer={offer}
                activeCampaignPipelineStep={activeCampaignPipelineStep}
                contactSearchMethod={contactSearchMethod}
                linkedinProduct={linkedinProduct}
                paiSearchLanguage={paiSearchLanguage}
                includeOwnContacts={includeOwnContacts}
                regularLocationConstraints={locationSettings.regularLocationConstraints}
                recruiterLocationConstraints={locationSettings.recruiterLocationConstraints}
                linkedinSearch={linkedinSearch}
                selectedContactSearchObjective={selectedContactSearchObjective}
                searchToAdd={searchToAdd}
                separateLinkedinAccount={separateLinkedinAccount}
                addSearchesLoading={addSearchesLoading}
                contactsToAdd={contactsToAdd}
                addContactsLoading={addContactsLoading}
                searches={searches}
                searchesLoading={searchesLoading}
                linkedinAccounts={linkedinAccounts}
                activeBlacklistScope={activeBlacklistScope}
                globalBlacklists={globalBlacklists}
                localBlacklists={localBlacklists}
                globalBlacklistsLoading={globalBlacklistsLoading}
                localBlacklistsLoading={localBlacklistsLoading}
                removeBlacklistItemsLoading={removeBlacklistItemsLoading}
                contactsPage={contactsPage}
                contactsSearchParam={contactsSearchParam}
                contactsPerPage={MAX_CONTACTS_PER_PAGE}
                totalContacts={totalContacts}
                aiJudgementProcessing={aiJudgementProcessing}
                profileAnalysisProcessing={profileAnalysisProcessing}
                contacts={contacts}
                contactsLoading={contactsLoading}
                contactFilter={contactFilter}
                activeQualifications={activeQualifications}
                abortRequested={abortRequested}
                abortLoading={abortLoading}
                deleteMultipleContacts={deleteMultipleContacts}
                contactsToDelete={contactsToDelete}
                campaignTesting={campaignTesting}
                finalContacts={finalContacts}
                finalContactsPage={finalContactsPage}
                finalContactsSearchParam={finalContactsSearchParam}
                finalContactsPerPage={MAX_FINAL_CONTACTS_PER_PAGE}
                totalFinalContacts={totalFinalContacts}
                finalContactsLoading={finalContactsLoading}
                finalContactsProcessing={finalAiJudgementProcessing}
                exportFinalContactsLoading={exportFinalContactsLoading}
                startCampaignLoading={startCampaignLoading}
                stopCampaignLoading={stopCampaignLoading}
                increaseCampaignStepIndex={increaseCampaignStepIndex}
                decreaseCampaignStepIndex={decreaseCampaignStepIndex}
                selectCampaignStep={selectCampaignStep}
                handlePerplexityUrlChange={handlePerplexityUrlChange}
                handleOfferInputChange={handleOfferInputChange}
                handleOfferToggleChange={handleOfferToggleChange}
                handlePerplexityPaste={handlePerplexityPaste}
                handlePerplexityKeyDown={handlePerplexityKeyDown}
                handlePerplexityRefresh={handlePerplexityRefresh}
                handleActiveCampaignPipelineStepChange={handleActiveCampaignPipelineStepChange}
                handleContactSearchMethodChange={handleContactSearchMethodChange}
                handleMoveToNewSearch={handleMoveToNewSearch}
                handleLinkedinProductChange={handleLinkedinProductChange}
                handlePaiSearchLanguageChange={handlePaiSearchLanguageChange}
                handleIncludeOwnContactsChange={handleIncludeOwnContactsChange}
                handleLocationFieldChange={handleLocationFieldChange}
                searchKeyUp={searchKeyUp}
                searchKeyDown={searchKeyDown}
                handleMultiSelectLocationsChange={handleMultiSelectLocationsChange}
                handleMultiSelectLocationsRemove={handleMultiSelectLocationsRemove}
                handleSelectRecruiterLocationConstraint={handleSelectRecruiterLocationConstraint}
                handleRemoveRecruiterLocationConstraint={handleRemoveRecruiterLocationConstraint}
                handleRecruiterLocationConstraintQueryChange={handleRecruiterLocationConstraintQueryChange}
                searchRecruiterLocationKeyUp={searchRecruiterLocationKeyUp}
                searchRecruiterLocationKeyDown={searchRecruiterLocationKeyDown}
                handleRecruiterLocationAreaChange={handleRecruiterLocationAreaChange}
                handleResetLinkedinSearch={handleResetLinkedinSearch}
                handleResetLocationSettings={handleResetLocationSettings}
                handleStartLinkedinSearch={handleStartLinkedinSearch}
                handleOpenRefineLinkedinSearchDialog={handleOpenRefineLinkedinSearchDialog}
                handleOpenCreateNewSearchDialog={handleOpenCreateNewSearchDialog}
                onOpenFeedbackDialog={() => setFeedbackDialogOpen(true)}
                handleSelectContactSearchObjective={handleSelectContactSearchObjective}
                handleOwnSearchFieldChange={handleOwnSearchFieldChange}
                handleJobTitlesChange={handleJobTitlesChange}
                handlePeopleTypeChange={handlePeopleTypeChange}
                handleProfileDegreeChange={handleProfileDegreeChange}
                handleSeparateLinkedinAccountChange={handleSeparateLinkedinAccountChange}
                handleAddOwnLinkedinSearch={handleAddOwnLinkedinSearch}
                handleAddOwnLinkedinSearchWithSkipQualification={handleAddOwnLinkedinSearchWithSkipQualification}
                onFileDrop={onFileDrop}
                handleFileChange={handleFileChange}
                exportCampaignContacts={exportCampaignContacts}
                handleUpdateCampaign={handleUpdateCampaign}
                handleChangeFinalContactsPage={handleChangeFinalContactsPage}
                handleFinalContactsSearchChange={handleFinalContactsSearchChange}
                handleClearFinalContactsSearchField={handleClearFinalContactsSearchField}
                handleDeleteContacts={handleDeleteContacts}
                handleAddContactsFromFile={handleAddContactsFromFile}
                handleOpenRenameContactSearchDialog={handleOpenRenameContactSearchDialog}
                handleOpenDeleteContactsSearchDialog={handleOpenDeleteContactsSearchDialog}
                handleOpenDeleteContactsFileDialog={handleOpenDeleteContactsFileDialog}
                handleOpenAddSingleContactDialog={handleOpenAddSingleContactDialog}
                handleAddMultipleContacts={handleAddMultipleContacts}
                handleOpenDeleteContactPermanentlyDialog={handleOpenDeleteContactPermanentlyDialog}
                handleOpenChangeAiJudgementDialog={handleOpenChangeAiJudgementDialog}
                handleOpenLoadContactsNumberDialog={handleOpenLoadContactsNumberDialog}
                handleOpenEditSearchLinkedinAccountDialog={handleOpenEditSearchLinkedinAccountDialog}
                handleBlacklistScopeChange={handleBlacklistScopeChange}
                handleSelectedBlacklistsChange={handleSelectedBlacklistsChange}
                handleOpenAddLocalBlacklistDialog={handleOpenAddLocalBlacklistDialog}
                handleOpenEditBlacklistItemsDialog={handleOpenEditBlacklistItemsDialog}
                handleOpenEditBlacklistNameDialog={handleOpenEditBlacklistNameDialog}
                handleOpenDeleteBlacklistDialog={handleOpenDeleteBlacklistDialog}
                handleRemoveSingleBlacklistItem={handleRemoveSingleBlacklistItem}
                handleSingleContactIsActiveChange={handleSingleContactIsActiveChange}
                handleMultiContactIsActiveChange={handleMultiContactIsActiveChange}
                handleChangeContactsPage={handleChangeContactsPage}
                handleContactsSearchChange={handleContactsSearchChange}
                handleClearContactsSearchField={handleClearContactsSearchField}
                handleOpenContactDrawer={handleOpenContactDrawer}
                handleOpenEnrichContactsDialog={handleOpenEnrichContactsDialog}
                handleSelectedFunnelFunctionsLibraryChange={handleSelectedFunnelFunctionsLibraryChange}
                handleSelectNodeFromList={handleSelectNodeFromList}
                handleUnselectNodeFromList={handleUnselectNodeFromList}
                handleUpdateCampaignReactFlow={handleUpdateCampaignReactFlowSafe}
                handleOpenFlowCreationDialog={handleOpenFlowCreationDialog}
                handleFlowBuilderApplied={handleFlowBuilderApplied}
                handleOpenChangeCommunicationStyleWarningDialog={handleOpenChangeCommunicationStyleWarningDialog}
                handleIncludeEmojiChange={handleIncludeEmojiChange}
                handleSwissSChange={handleSwissSChange}
                handleLanguageChange={handleLanguageChange}
                handleSecondaryLanguageChange={handleSecondaryLanguageChange}
                onIsActiveChange={onIsActiveChange}
                onGoalChange={onGoalChange}
                onCaseHandlingUpdate={onCaseHandlingUpdate}
                handleUpdateAutoReply={handleUpdateAutoReply}
                handleMessageApprovalChange={handleMessageApprovalChange}
                handleRecontactCooldownChange={handleRecontactCooldownChange}
                isLinkedinAccountMissing={isLinkedinAccountMissing}
                handleSelectedLinkedinUserChange={handleSelectedLinkedinUserChange}
                handleSelectedTestContactChange={handleSelectedTestContactChange}
                handleNextContact={handleNextContact}
                handlePrevContact={handlePrevContact}
                handleNextMessage={handleNextMessage}
                handlePrevMessage={handlePrevMessage}
                handleTestCampaign={handleTestCampaign}
                handleStartCampaign={handleStartCampaign}
                handleStopCampaign={handleStopCampaign}
                toggleExpanded={toggleExpanded}
                handleOpenFaqDialog={handleOpenFaqDialog}
                handleOpenWizard={builderDraftInWizard ? handleOpenWizardFromBuilder : undefined}
                handleOpenStopRunningCampaignDialog={handleOpenStopRunningCampaignDialog}
                contactQualification={contactQualification}
                handleAiQualifierDetailsChange={handleAiQualifierDetailsChange}
                handleAiQualifierPromptChange={handleAiQualifierPromptChange}
                handleAiQualifierSelectedOptionChange={handleAiQualifierSelectedOptionChange}
                handleRejudgeExistingContactsChange={handleRejudgeExistingContactsChange}
                handleIsJudgingChange={handleIsJudgingChange}
                updateQualifyingContactsRecruiting={updateQualifyingContactsRecruiting}
                updateQualifyingContactsLead={updateQualifyingContactsLead}
                handleRemoveContactFromBlacklist={handleRemoveContactFromBlacklist}
                handleRemoveContactFromSuspended={handleRemoveContactFromSuspended}
                handleOpenUpdateSexDialog={handleOpenUpdateSexDialog}
                handleRecoverContactFromDeleted={handleRecoverContactFromDeleted}
                handleContactFilterChange={handleContactFilterChange}
                applySidebarFilter={applySidebarFilter}
                handleActiveQualificationsChange={handleActiveQualificationsChange}
                handleAbortProcessing={handleAbortProcessing}
                toggleDeleteMultipleContacts={toggleDeleteMultipleContacts}
                handleMultipleContactsToDeleteChange={handleMultipleContactsToDeleteChange}
                handleContactsToDeleteChange={handleContactsToDeleteChange}
                handleOpenDeleteMultipleContactsPermanentlyDialog={handleOpenDeleteMultipleContactsPermanentlyDialog}
              />
          </>
        )
      ) : activeTab === NavigationStates.PROJECTS ? (
        <CampaignOverview
          productGroup={productGroup}
          autoSizePanel={autoSizePanel}
          fetchFinalContacts={fetchFinalContacts}
          resetCampaignBuilder={resetCampaignBuilder}
          handleResetCampaign={handleResetCampaign}
          handleOpenCopilotLicenseExpiredDialog={handleOpenCopilotLicenseExpiredDialog}
          handleOpenCreateCampaignDialog={handleOpenCreateCampaignDialog}
        />
      ) : activeTab === NavigationStates.BLACKLIST ? (
        <GlobalBlacklistOverview />
      ) : activeTab === NavigationStates.STATISTICS ? (
        <CampaignStatistics productGroup={productGroup} />
      ) : activeTab === NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT ? (
        <LinkedinAccountOverview productGroup={productGroup} />
      ) : activeTab === NavigationStates.INBOX ? (
        <FunnelInbox productGroup={productGroup} />
      ) : activeTab === NavigationStates.TASK_MANAGER ? (
        <TaskManager productGroup={productGroup} handleOpenCopilotLicenseExpiredDialog={handleOpenCopilotLicenseExpiredDialog} />
      ) : null}

      <AskForSyncDialog
        open={askForSyncDialog.open}
        loading={askForSyncDialog.loading}
        userEmail={askForSyncDialog.userEmail}
        handleClose={handleCloseAskForSyncDialog}
        handleSyncLinkedinAccount={handleSyncLinkedinAccount}
      />
      <CreateCampaignDialog
        open={createCampaignDialog.open}
        loading={createCampaignDialog.loading}
        nameTakenError={createCampaignDialog.nameTakenError}
        name={createCampaignDialog.name}
        linkedinAccounts={linkedinAccounts}
        selectedLinkedinAccount={createCampaignDialog.selectedLinkedinAccount}
        startNewOrClone={createCampaignDialog.startNewOrClone}
        campaigns={campaigns}
        selectedCampaignToClone={createCampaignDialog.selectedCampaignToClone}
        cloneContacts={createCampaignDialog.cloneContacts}
        handleCampaignNameChange={handleCampaignNameChange}
        handleSelectedLinkedinAccountChange={handleSelectedLinkedinAccountChange}
        handleStartNewOrCloneChange={handleStartNewOrCloneChange}
        handleSelectedCampaignToCloneChange={handleSelectedCampaignToCloneChange}
        handleCloneContactsChange={handleCloneContactsChange}
        handleClose={handleCloseCreateCampaignDialog}
        handleCreateCampaign={handleCreateCampaign}
        handleOpenWizard={productGroup === ProductGroups.HR_BOOSTER ? handleOpenWizardFromDialog : undefined}
      />
      <CreateCampaignSuccessDialog
        productGroup={productGroup}
        open={createCampaignSuccessDialog.open}
        handleClose={handleCloseCreateCampaignSuccessDialog}
      />
      <AutoSyncProcessingDialog
        open={autoSyncProcessingDialog.open}
        syncStage={autoSyncProcessingDialog.syncStage}
        handleClose={handleCloseAutoSyncProcessingDialog}
      />
      <AutoSyncWarningDialog
        open={autoSyncWarningDialog.open}
        status={autoSyncWarningDialog.status}
        accountId={autoSyncWarningDialog.accountId}
        linkedinAccounts={linkedinAccounts}
        handleClose={handleCloseAutoSyncWarningDialog}
      />
      <FileUploadDialog
        open={fileUploadDialog.open}
        columns={fileUploadDialog.columns}
        fileName={fileUploadDialog.fileNames.join(', ')}
        hasTableHeading={fileUploadDialog.hasTableHeading}
        productGroup={productGroup}
        handleColumnTypeChange={handleColumnTypeChange}
        handleHasTableHeadingChange={handleHasTableHeadingChange}
        handleClose={handleCloseFileUploadDialog}
        handleAcceptColumns={handleAcceptColumns}
      />
      <RenameContactSearchDialog
        open={renameContactSearchDialog.open}
        loading={renameContactSearchDialog.loading}
        name={renameContactSearchDialog.name}
        handleSearchNameChange={handleSearchNameChange}
        handleClose={handleCloseRenameContactSearchDialog}
        handleRenameSearch={handleRenameSearch}
      />
      <EditSearchLinkedinAccountDialog
        open={editSearchLinkedinAccountDialog.open}
        loading={editSearchLinkedinAccountDialog.loading}
        linkedinAccounts={linkedinAccounts}
        selectedLinkedinAccount={editSearchLinkedinAccountDialog.selectedLinkedinAccount}
        handleSearchLinkedinAccountChange={handleSearchLinkedinAccountChange}
        handleClose={handleCloseEditSearchLinkedinAccountDialog}
        handleUpdateSearchLinkedinAccount={handleUpdateSearchLinkedinAccount}
      />
      <DeleteSearchDialog
        open={deleteContactsSearchDialog.open}
        loading={deleteContactsSearchDialog.loading}
        name={deleteContactsSearchDialog.search?.name}
        deleteContacts={deleteContactsSearchDialog.deleteContacts}
        handleDeleteSearchContactsChange={handleDeleteSearchContactsChange}
        handleClose={handleCloseDeleteContactsSearchDialog}
        handleDeleteSearch={handleDeleteSearch}
      />
      <DeleteFileDialog
        open={deleteContactsFileDialog.open}
        loading={deleteContactsFileDialog.loading}
        name={deleteContactsFileDialog.file?.name}
        handleClose={handleCloseDeleteContactsFileDialog}
        handleDeleteFile={handleDeleteContactsFile}
      />
      <AddSingleContactDialog
        open={addSingleContactDialog.open}
        loading={addSingleContactDialog.loading}
        contact={addSingleContactDialog.contact}
        linkedinAccount={fullLinkedinAccount}
        handleSingleContactFieldChange={handleSingleContactFieldChange}
        handleClose={handleCloseAddSingleContactDialog}
        handleAddSingleContact={handleAddSingleContact}
      />
      <RefineLinkedinSearchDialog
        open={refineLinkedinSearchDialog.open}
        loading={refineLinkedinSearchDialog.loading}
        originalLinkedinUrl={refineLinkedinSearchDialog.originalLinkedinUrl}
        alteredLinkedinUrl={refineLinkedinSearchDialog.alteredLinkedinUrl}
        urlError={refineLinkedinSearchDialog.urlError}
        handleAlteredLinkedinUrlChange={handleAlteredLinkedinUrlChange}
        handlePasteAlteredLinkedinUrl={handlePasteAlteredLinkedinUrl}
        handleClose={handleCloseRefineLinkedinSearchDialog}
        handleAcceptRefinedLinkedinUrl={handleAcceptRefinedLinkedinUrl}
      />
      <RefineRecruiterSearchDialog
        open={refineRecruiterSearchDialog.open}
        loading={refineRecruiterSearchDialog.loading}
        preLoadingFields={refineRecruiterSearchDialog.preLoadingFields}
        contactsLoading={refineRecruiterSearchDialog.contactsLoading}
        autoSyncLoading={autoSyncLoading}
        constraints={refineRecruiterSearchDialog.constraints}
        contacts={refineRecruiterSearchDialog.contacts}
        totalContacts={refineRecruiterSearchDialog.totalContacts}
        pageNumber={refineRecruiterSearchDialog.pageNumber}
        handleChangeRecruiterSearchPage={handleChangeRecruiterSearchPage}
        handleRecruiterConstraintQueryChange={handleRecruiterConstraintQueryChange}
        searchConstraintsKeyUp={searchConstraintsKeyUp}
        searchConstraintsKeyDown={searchConstraintsKeyDown}
        handleSelectRecruiterConstraint={handleSelectRecruiterConstraint}
        handleRemoveRecruiterConstraint={handleRemoveRecruiterConstraint}
        handleExcludeChange={handleExcludeChange}
        handleKeywordsChange={handleKeywordsChange}
        keywordKeyDown={keywordKeyDown}
        handleAreaChange={handleAreaChange}
        handleTextFieldYearsOfExperienceChange={handleTextFieldYearsOfExperienceChange}
        handleYearsOfExperienceChange={handleYearsOfExperienceChange}
        handleResetYearsOfExperience={handleResetYearsOfExperience}
        handleUpdateYearsOfExperience={handleUpdateYearsOfExperience}
        handleClose={handleCloseRefineRecruiterSearchDialog}
        handleAcceptSearch={handleAcceptRefinedRecruiterSearch}
        showPostalCode={true}
      />
      <CreateNewSearchDialog
        open={createNewSearchDialog.open}
        loading={createNewSearchDialog.loading}
        searchNameName={createNewSearchDialog.searchName}
        numberOfContactsToLoad={createNewSearchDialog.numberOfContactsToLoad}
        handleCreateSearchNameChange={handleCreateSearchNameChange}
        handleCreateSearchNumberOfContactsChange={handleCreateSearchNumberOfContactsChange}
        handleClose={handleCloseCreateNewSearchDialog}
        handleCreateNewSearch={handleCreateNewSearch}
      />
      {linkedinSearch.feedback ? (
        <TalentSearchFeedbackDialog
          open={feedbackDialogOpen}
          feedback={linkedinSearch.feedback}
          onAcceptTitle={handleFeedbackAcceptTitle}
          onRetrySearch={handleFeedbackRetrySearch}
          onClose={() => setFeedbackDialogOpen(false)}
        />
      ) : null}
      <AddMoreSearchesDialog
        open={addMoreSearchesDialog.open}
        searchName={addMoreSearchesDialog.searchName}
        handleClose={handleCloseAddMoreSearchesDialog}
        handleCloseAndProceed={handleCloseAndProceedAddMoreSearchesDialog}
      />
      <QualifyContactsDialog
        open={qualifyContactsDialog.open}
        loading={qualifyContactsDialog.loading}
        step={qualifyContactsDialog.step}
        promptPlaceholders={qualifyContactsDialog.promptPlaceholders}
        prompt={qualifyContactsDialog.prompt}
        selectedOptions={qualifyContactsDialog.selectedOptions}
        productGroup={productGroup}
        handleNextQualifyContactsStep={handleNextQualifyContactsStep}
        handlePrevQualifyContactsStep={handlePrevQualifyContactsStep}
        handleAiBriefingDetailsChange={handleAiBriefingDetailsChange}
        handleAiBriefingDetailsChangeForLeadBooster={handleAiBriefingDetailsChangeForLeadBooster}
        handleSelectedOptionChange={handleSelectedOptionChange}
        handleClose={handleCloseQualifyContactsDialog}
        handleCloseAndProceed={handleCloseAndProceedQualifyContactsDialog}
        startQualifyingContacts={productGroup === ProductGroups.HR_BOOSTER ? startQualifyingContactsRecruiter : startQualifyingContactsLead}
      />
      <ContactDrawer
        open={contactDrawer.open}
        productGroup={productGroup}
        funnelId={contactDrawer.funnelId}
        contactIndex={contactDrawer.contactIndex}
        profileId={contactDrawer.profileId}
        aiJudgement={contactDrawer.aiJudgement}
        aiJudgementExplanation={contactDrawer.aiJudgementExplanation}
        assessment={contactDrawer.assessment}
        percentiles={contactDrawer.percentiles}
        isActive={contactDrawer.isActive}
        contactDetails={contactDrawer.contactDetails}
        aboutContact={contactDrawer.aboutContact}
        aboutCompany={contactDrawer.aboutCompany}
        contactList={drawerContacts}
        isActiveToggleDisabled={activeCampaignPipelineStep === CampaignPipelineSteps.FINAL_CONTACT_LIST || contactDrawer.toggleDisabled}
        handleGetNextContact={handleGetNextContact}
        handleGetPrevContact={handleGetPrevContact}
        handleUpdateDrawerContactIsActive={handleUpdateDrawerContactIsActive}
        handleCloseDrawer={handleCloseContactDrawer}
      />
      <LoadContactsNumberDialog
        open={loadContactsNumberDialog.open}
        loading={loadContactsNumberDialog.loading}
        numberOfContactsToLoad={loadContactsNumberDialog.numberOfContactsToLoad}
        handleNumberOfContactsToLoadChange={handleNumberOfContactsToLoadChange}
        handleClose={handleCloseLoadContactsNumberDialog}
        handleContinueSearch={handleContinueSearch}
      />
      <DeleteContactPermanentlyDialog
        open={deleteContactPermanentlyDialog.open}
        loading={deleteContactPermanentlyDialog.loading}
        contact={deleteContactPermanentlyDialog.contact}
        handleClose={handleCloseDeleteContactPermanentlyDialog}
        handleDeleteContactPermanently={handleDeleteContactPermanently}
      />
      <DeleteMultipleContactsPermanentlyDialog
        open={deleteMultipleContactsPermanentlyDialog.open}
        loading={deleteMultipleContactsPermanentlyDialog.loading}
        contactCount={deleteMultipleContactsPermanentlyDialog.contactCount}
        handleClose={handleCloseDeleteMultipleContactsPermanentlyDialog}
        handleDeleteMultipleContactsPermanently={handleDeleteMultipleContactsPermanently}
      />
      <ChangeAiJudgementDialog
        open={changeAiJudgementDialog.open}
        loading={changeAiJudgementDialog.loading}
        contact={changeAiJudgementDialog.contact}
        newJudgement={changeAiJudgementDialog.newJudgement}
        productGroup={productGroup}
        handleNewJudgementChange={handleNewJudgementChange}
        handleChangeAiJudgement={handleChangeAiJudgement}
        handleClose={handleCloseChangeAiJudgementDialog}
      />
      <AddBlacklistDialog
        open={addLocalBlacklistDialog.open}
        loading={addLocalBlacklistDialog.loading}
        blacklist={addLocalBlacklistDialog.blacklist}
        blacklistName={addLocalBlacklistDialog.blacklistName}
        blacklistType={addLocalBlacklistDialog.blacklistType}
        blacklistUploadVariant={addLocalBlacklistDialog.blacklistUploadVariant}
        individualItemsTextField={addLocalBlacklistDialog.individualItemsTextField}
        individualItemsTextFieldError={addLocalBlacklistDialog.individualItemsTextFieldError}
        individualItemsList={addLocalBlacklistDialog.individualItemsList}
        fileColumnSelection={addLocalBlacklistDialog.fileColumnSelection}
        blacklistItems={addLocalBlacklistDialog.fileItemsList}
        linkedinAccounts={linkedinAccounts}
        selectedLinkedinAccount={addLocalBlacklistDialog.selectedLinkedinAccount}
        companyQuery={addLocalBlacklistDialog.companyQuery}
        companyOptions={addLocalBlacklistDialog.companyOptions}
        selectedCompanies={addLocalBlacklistDialog.selectedCompanies}
        companyLoading={addLocalBlacklistDialog.companyLoading}
        companyIsEmpty={addLocalBlacklistDialog.companyIsEmpty}
        handleBlacklistNameChange={handleBlacklistNameChange}
        handleBlacklistTypeChange={handleBlacklistTypeChange}
        handleBlacklistUploadVariantChange={handleBlacklistUploadVariantChange}
        onFileDrop={addLocalBlacklistOnFileDrop}
        handleColumnTypeChange={addLocalBlacklistHandleColumnTypeChange}
        handleHasTableHeadingChange={addLocalBlacklistHandleHasTableHeadingChange}
        handleAcceptColumns={addLocalBlacklistHandleAcceptColumns}
        handleResetBlacklistItems={handleResetBlacklistItems}
        handleResetFileColumnSelection={handleResetFileColumnSelection}
        handleTextFieldChange={handleTextFieldChange}
        handleAddIndividualItem={handleAddIndividualItem}
        handlePasteIndividualItem={handlePasteIndividualItem}
        handleRemoveIndividualItem={handleRemoveIndividualItem}
        handleLinkedinAccountChange={handleBlacklistLinkedinAccountChange}
        handleCompanyQueryChange={handleBlacklistCompanyQueryChange}
        searchCompanyKeyUp={searchBlacklistCompanyKeyUp}
        searchCompanyKeyDown={searchBlacklistCompanyKeyDown}
        handleToggleCompany={handleToggleBlacklistCompany}
        handleRemoveCompany={handleRemoveBlacklistCompany}
        handleClose={handleCloseAddLocalBlacklistDialog}
        handleCreateLocalBlacklist={handleCreateLocalBlacklist}
        handleUpdateBlacklistItems={handleUpdateBlacklistItems}
      />
      <DeleteBlacklistDialog
        open={deleteBlacklistDialog.open}
        loading={deleteBlacklistDialog.loading}
        blacklist={deleteBlacklistDialog.blacklist}
        handleClose={handleCloseDeleteBlacklistDialog}
        handleDeleteBlacklist={handleDeleteBlacklist}
      />
      <EditBlacklistNameDialog
        open={editBlacklistNameDialog.open}
        loading={editBlacklistNameDialog.loading}
        blacklistName={editBlacklistNameDialog.blacklistName}
        handleEditBlacklistNameChange={handleEditBlacklistNameChange}
        handleClose={handleCloseEditBlacklistNameDialog}
        handleRenameBlacklist={handleRenameBlacklist}
      />
      <EnrichContactsDialog
        open={enrichContactsDialog.open}
        loading={enrichContactsDialog.loading}
        apiKey={enrichContactsDialog.apiKey}
        variant={enrichContactsDialog.variant}
        prospeosEnrichEmail={enrichContactsDialog.prospeosEnrichEmail}
        prospeosEnrichPhone={enrichContactsDialog.prospeosEnrichPhone}
        aiResearchFullFunnel={enrichContactsDialog.aiResearchFullFunnel}
        customFieldPersonPrompt={enrichContactsDialog.customFieldPersonPrompt}
        customFieldCompanyPrompt={enrichContactsDialog.customFieldCompanyPrompt}
        customFieldOverwriteExisting={enrichContactsDialog.customFieldOverwriteExisting}
        handleApiKeyChange={handleApiKeyChange}
        handleVariantChange={handleVariantChange}
        handleProspeosEnrichEmailChange={handleProspeosEnrichEmailChange}
        handleProspeosEnrichPhoneChange={handleProspeosEnrichPhoneChange}
        handleAiResearchFullFunnelChange={handleAiResearchFullFunnelChange}
        handleCustomFieldPersonPromptChange={handleCustomFieldPersonPromptChange}
        handleCustomFieldCompanyPromptChange={handleCustomFieldCompanyPromptChange}
        handleCustomFieldOverwriteExistingChange={handleCustomFieldOverwriteExistingChange}
        handleClose={handleCloseEnrichContactsDialog}
        handleStartEnrichment={handleStartEnrichment}
      />
      <Snackbar
        open={enrichContactsSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseEnrichContactsSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseEnrichContactsSnackbar} severity={enrichContactsSnackbar.severity} sx={{ width: '100%' }}>
          {enrichContactsSnackbar.message}
        </Alert>
      </Snackbar>
      <EnrichmentLoadingDialog open={enrichmentLoadingDialog.open} />
      <FlowCreationDialog
        open={flowCreationDialog.open}
        loading={flowCreationDialog.loading}
        productGroup={productGroup}
        activeTab={flowCreationDialog.activeTab}
        templates={templates}
        openTemplateSuccessDialog={openTemplateSuccessDialog}
        handleSelectActiveTab={handleSelectActiveFlowCreationTab}
        handleClose={handleCloseFlowCreationDialog}
        handleApplyTemplate={handleApplyTemplate}
        handleApplyEmptyTemplate={handleApplyEmptyTemplate}
        handleCloseTemplateSuccessDialog={() => setOpenTemplateSuccessDialog(false)}
      />
      <ChangeCommunicationStyleWarningDialog
        open={changeCommunicationStyleWarningDialog.open}
        handleClose={handleCloseChangeCommunicationStyleWarningDialog}
        handleChangeCommunicationStyle={handleChangeCommunicationStyle}
      />
      <LinkedinAccountNotFoundDialog open={linkedinAccountNotFoundDialog.open} handleClose={handleCloseLinkedinAccountNotFoundDialog} />
      <CoPilotLicenseExpiredDialog open={copilotLicenseExpiredDialog.open} handleClose={handleCloseCopilotLicenseExpiredDialog} />
      <PaiInfoDialog
        open={creditsExhaustedDialog.open}
        title={t('creditsExhaustedDialogTitle')}
        textContent={[t('creditsExhaustedDialogContent1'), t('creditsExhaustedDialogContent2')]}
        handleClose={handleCloseCreditsExhaustedDialog}
      />
      <SearchQuotaExceededDialog
        open={searchQuotaExceededDialog.open}
        searchName={searchQuotaExceededDialog.searchName}
        nextRetryAt={searchQuotaExceededDialog.nextRetryAt}
        handleClose={handleCloseSearchQuotaExceededDialog}
        handleOpenAccountManagement={handleOpenSearchQuotaExceededAccountManagement}
      />
      <StartCampaignSuccessDialog
        open={startCampaignSuccessDialog.open}
        userFirstName={firstName}
        campaignName={campaign?.name || ''}
        productGroup={productGroup}
        handleClose={handleCloseStartCampaignSuccessDialog}
      />
      <StartMessageApprovalCampaignSuccessDialog
        open={startMessageApprovalCampaignSuccessDialog.open}
        userFirstName={firstName}
        campaign={startMessageApprovalCampaignSuccessDialog.campaign}
        linkedinAccount={startMessageApprovalCampaignSuccessDialog.linkedinAccount}
        productGroup={productGroup}
        handleOpenStartImmediatelyDialog={handleOpenStartImmediatelyDialog}
        handleCloseAndProceed={handleCloseAndProceedStartMessageApprovalCampaignSuccessDialog}
      />
      <StartImmediatelyDialog
        open={startImmediatelyDialog.open}
        loading={startImmediatelyDialog.loading}
        handleClose={handleCloseStartImmediatelyDialog}
        handleStartCampaignImmediately={handleStartCampaignImmediately}
      />
      <StartImmediatelySuccessDialog open={startImmediatelySuccessDialog.open} handleClose={handleCloseStartImmediatelySuccessDialog} />
      <FlowIncompleteDialog
        open={flowIncompleteDialog.open}
        nodes={nodes}
        linkedinAccount={fullLinkedinAccount}
        handleClose={handleCloseFlowIncompleteDialog}
      />
      <FlowEmptyDialog open={flowEmptyDialog.open} handleClose={handleCloseFlowEmptyDialog} />
      <CoPilotOnboardingJourney productGroup={productGroup} handleOpenCreateCampaignDialog={handleOpenCreateCampaignDialog} />
      <FaqDialog
        open={faqDialog.open}
        activeFaq={faqDialog.activeFaq}
        productGroup={productGroup}
        setActiveFaq={handleSetActiveFaq}
        handleClose={handleCloseFaqDialog}
      />
      <UnableToGenerateMessageDialog open={unableToGenerateMessageDialog.open} handleClose={handleCloseUnableToGenerateMessageDialog} />
      <StartingCampaignNotPossibleDialog
        open={startingCampaignNotPossibleDialog.open}
        issues={startingCampaignNotPossibleDialog.issues}
        handleClose={handleCloseStartingCampaignNotPossibleDialog}
      />
      <StopRunningCampaignDialog
        open={stopRunningCampaignDialog.open}
        loading={stopRunningCampaignDialog.loading}
        handleClose={handleCloseStopRunningCampaignDialog}
        handleStopRunningCampaignAndProceed={handleStopRunningCampaignAndProceed}
      />
      <UpdateSexDialog
        open={updateSexDialog.open}
        loading={updateSexDialog.loading}
        contact={updateSexDialog.contact}
        selectedSex={updateSexDialog.selectedSex}
        handleSexChange={handleSexChange}
        handleClose={handleCloseUpdateSexDialog}
        handleUpdateSex={handleUpdateSex}
      />
      <PaiSuccessAlert
        open={autoSyncSuccessful}
        message={t('linkedinAccountSuccessfullySignedIn')}
        position={'bottom'}
        handleClose={handleCloseAutoSyncSuccessfulSnackbar}
      />
      <PaiSuccessAlert open={successAlert.open} message={successAlert.message} position={'bottom'} handleClose={handleCloseSuccessAlert} />
    </>
  );
};

export default CampaignCoPilot;
